import { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { format, differenceInDays, isBefore, parseISO, isWithinInterval, addDays, eachDayOfInterval, startOfDay, endOfMonth, addMonths } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { responsiveImage } from "@/lib/responsiveImage";
import { FIRM_CANCELLATION_POLICY, normalizeQuotePhone } from "@/lib/booking-policy";
import { readCheckout, loadCheckout, saveCheckout, hasCheckoutSignature, checkoutReturnUrl, type CheckoutDraft, type CheckoutPayment } from "@/lib/checkoutResume";
import AirbnbDatePicker, { DateRange as AirbnbDateRange } from "@/components/AirbnbDatePicker";
import LazyMapEmbed from "@/components/LazyMapEmbed";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useToast } from "@/hooks/use-toast";
import { useVisualViewport } from "@/hooks/use-visual-viewport";
import { trackEvent, getVisitorId, getSessionId } from "@/hooks/use-page-tracking";
import { useBookingMcpTool } from "@/hooks/use-booking-mcp";
import { useDateInquiryMcpTool } from "@/hooks/use-inquiry-mcp";
import { useStartCheckoutMcpTool } from "@/hooks/use-checkout-mcp";
import DateInquiryDialog from "@/components/DateInquiryDialog";
import { useExperiment, recordConversion, fireExposure } from "@/lib/experiments";
import {
  Star, Users, Bed, Bath, Waves, Trees, Flame, Shield, Wifi, Car, Wind,
  ChevronLeft, ChevronRight, X, Grid3X3, MapPin, Check,
  Share2, Award, ChevronDown, Phone, Mail, Calendar as CalendarIcon,
  KeyRound, Tv2, Coffee, Utensils, Snowflake, Dumbbell,
  WashingMachine, ShowerHead, Key, Bike, Anchor, Thermometer, BookOpen,
  ExternalLink, Plane, Smartphone, ZoomIn, BadgePercent, MessageSquare, Lock, Gem,
  Minus, Plus, ShieldCheck,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CabinDivider } from "@/components/CabinAccents";
import GuestHeader from "@/components/GuestHeader";
import PageSEO from "@/components/PageSEO";
import EmailSignupForm from "@/components/EmailSignupForm";
import JsonLd from "@/components/JsonLd";
import SocialLinks from "@/components/SocialLinks";
import { sameAsUrls } from "@/lib/social-links";
import { SiGooglemaps, SiApple, SiWaze, SiGoogleplay } from "react-icons/si";
import { calculatePontoonLadderCost, normalizePontoonLadder, DEFAULT_PONTOON_LADDER, PONTOON_VALUE_PROPS } from "@shared/pontoonPricing";
import { getApplicableMinNights } from "@shared/minStay";
import { CANONICAL_ORIGIN } from "@/lib/canonical";
import { PONTOON_HOUSE_RULES, PONTOON_LIABILITY_CLAUSES } from "@/lib/waiver-text";
import { HOST_PHONE_DISPLAY, HOST_PHONE_TEL, HOST_PHONE_SMS, CONCIERGE_PHONE_DISPLAY, CONCIERGE_PHONE_E164, CONCIERGE_PHONE_TEL } from "@/lib/contact-info";
import { REVIEWS, REVIEW_COUNT, REVIEW_AVG } from "@/lib/reviews";
import { ALL_PHOTOS, HOME_HERO_PHOTOS } from "@/lib/cabinPhotos";
import { trackPhotoZoom, trackAmenityEngaged, subjectFromText, useAmenitySectionViews, trackDescriptionExpanded, trackAmenitiesExpanded, useReviewReadTracking } from "@/lib/engagement-tracking";

const StripePaymentPanel = lazy(() => import("@/components/StripePaymentPanel"));
const StripePaymentReturn = lazy(() => import("@/components/StripePaymentReturn"));

type DateRange = AirbnbDateRange;

// Home hero strip mirrors the Airbnb listing page's main photo display
// (heroMedia carousel order — lake exterior first), not the room-grouped
// photo tour. See HOME_HERO_PHOTOS in cabinPhotos.ts.
const PHOTOS = HOME_HERO_PHOTOS;

const AMENITIES = [
  { icon: Waves, label: "Private sandy beach right out your back door" },
  { icon: Anchor, label: "25-foot pontoon available as a rental add-on" },
  { icon: Flame, label: "Private cedar sauna with lake views" },
  { icon: Utensils, label: "Large lakeside deck with grill and seating" },
  { icon: Flame, label: "Fire pit for evening bonfires" },
  { icon: Wifi, label: "High-speed WiFi and smart TV" },
  { icon: Utensils, label: "Fully equipped kitchen with all essentials" },
  { icon: Snowflake, label: "Air conditioning and heating" },
  { icon: WashingMachine, label: "Washer and dryer" },
  { icon: Bike, label: "Kayaks & paddleboard" },
  { icon: Anchor, label: "Private dock" },
  { icon: Utensils, label: "Pool table & game room / bar" },
  { icon: Wind, label: "EV charger" },
  { icon: Flame, label: "BBQ grill + Blackstone griddle" },
  { icon: ShowerHead, label: "Outdoor shower" },
  { icon: Coffee, label: "Coffee maker (Keurig)" },
  { icon: Utensils, label: "Microwave & dishwasher" },
  { icon: Utensils, label: "Dining table — seats 12+" },
  { icon: Car, label: "Free parking on premises" },
  { icon: Bed, label: "Extra pillows & blankets" },
  { icon: Bed, label: "Iron & ironing board" },
  { icon: Key, label: "Self check-in (keypad)" },
  { icon: Waves, label: "Ice fishing access (winter)" },
  { icon: Shield, label: "Carbon monoxide alarm" },
  { icon: Shield, label: "Smoke alarm" },
  { icon: Shield, label: "Fire extinguisher" },
  { icon: Shield, label: "First aid kit" },
];

const BEDROOMS = [
  {
    name: "Master Suite",
    img: "/photos/photo-14-4325ed18-9a76-4be0-9229-a7e82468fc82.webp",
    alt: "Master Suite at Legendary Log Cabin — queen log bed with panoramic Lake Miltona views through double windows",
    beds: "1 queen bed, 1 single bed",
    note: "Wake up to beautiful panoramic lake views",
  },
  {
    name: "Sandbar Suite",
    img: "/photos/sandbar-suite-3beds.webp",
    alt: "Sandbar Suite log cabin bedroom — three queen log-frame beds, tongue-and-groove wood ceiling, barn sconce lighting",
    beds: "3 queen beds",
    note: "Private lakeside walkout, steps from the sandy beach",
  },
  {
    name: "Bedroom 3",
    img: "/photos/photo-28-a0a6c9ad-2516-4f64-8155-dc78a73a265e.webp",
    alt: "Bedroom 3 with handcrafted log bunk bed, dresser, and teal accent walls at Lake Miltona log cabin",
    beds: "1 queen bed, 1 single bed",
    note: "Bright, comfortable, and conveniently located",
  },
  {
    name: "Loft",
    img: "/photos/photo-53-7b3eb623-b030-47f3-9018-23e587aa7aaf.webp",
    alt: "Loft lounge area with futon sofas, vaulted cedar ceiling, and sliding door to private balcony with lake and forest views",
    beds: "3 sofa beds",
    note: "Dual lake & forest views plus a private porch",
  },
  {
    name: "Living Room Pull-Out",
    img: "/photos/photo-42-eb5cf81e-20ab-4686-85b5-4c39ccfb4e9a.webp",
    alt: "White leather couch in the living room that folds out into a pull-out sleeper bed for two, with log beam ceiling and flat-screen TV",
    beds: "Sleeper sofa · sleeps 2",
    note: "Comfortably sleeps two in the main living room",
  },
];


const NIGHTLY_RATE = 1000;
// Must match the server's CARD_PROCESSING_FEE_PCT so local estimates agree
// with the served quote.
const CARD_PROCESSING_FEE_PCT = 3;
// No automatic nightly discount — matches server DIRECT_BOOKING_DISCOUNT_PCT = 0.
// Direct booking — guests deal straight with the host.
const DIRECT_DISCOUNT_PCT = 0;
// MN lodging tax: 6.875% state + 0.5% Douglas County Transit = 7.375% total.
// Applied to post-discount accommodation base (not to the processing fee).
const TAX_RATE_PCT = 7.375;
const PROPERTY_NAME = "Legendary Log Cabin";
const PROPERTY_THUMBNAIL = "/photos/photo-78-ed3ba9ee-cb47-4f67-8740-2a0554bb7f33.webp";

type CalendarData = {
  approvedBookings: Array<{ checkIn: string; checkOut: string }>;
  blockedDates: Array<{ date: string; reason?: string | null }>;
};

function cabinToday(): Date {
  const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  return startOfDay(new Date(`${ymd}T12:00:00`));
}

function useCalendarAvailability() {
  const { data } = useQuery<CalendarData>({
    queryKey: ["/api/calendar"],
    queryFn: () => fetch("/api/calendar").then((r) => r.json()),
    staleTime: 60_000,
  });

  const isDisabled = (date: Date): boolean => {
    if (isBefore(startOfDay(date), cabinToday())) return true;
    if (!data) return false;
    const dateStr = format(date, "yyyy-MM-dd");
    if (data.blockedDates.some((b) => b.date === dateStr)) return true;
    if (data.approvedBookings.some((b) => {
      try { return isWithinInterval(date, { start: parseISO(b.checkIn), end: parseISO(b.checkOut) }); }
      catch { return false; }
    })) return true;
    return false;
  };

  const isTentativeVrboDate = (date: Date): boolean => {
    if (!data) return false;
    const dateStr = format(date, "yyyy-MM-dd");
    return data.blockedDates.some(
      (b) => b.date === dateStr && /^\[vrbo\]\s*tentative\b/i.test(b.reason ?? ""),
    );
  };

  const tentativeVrboRangeFor = (date: Date): DateRange => {
    const held = new Set(
      (data?.blockedDates ?? [])
        .filter((b) => /^\[vrbo\]\s*tentative\b/i.test(b.reason ?? ""))
        .map((b) => b.date),
    );
    let from = startOfDay(date);
    let to = startOfDay(date);
    while (held.has(format(addDays(from, -1), "yyyy-MM-dd"))) from = addDays(from, -1);
    while (held.has(format(to, "yyyy-MM-dd"))) to = addDays(to, 1);
    return { from, to };
  };

  return { isDisabled, isTentativeVrboDate, tentativeVrboRangeFor };
}

// Honest scarcity from the real availability calendar: counts fully-open
// weekends (Fri + Sat nights both free) in the current month, falling back to
// next month when this one is sold out. Only returns a value when 1-3 remain —
// fake or weak urgency ("7 weekends left!") erodes trust, so we say nothing
// unless the scarcity is real.
function useWeekendScarcity(): { count: number; month: string } | null {
  const { data } = useQuery<CalendarData>({
    queryKey: ["/api/calendar"],
    queryFn: () => fetch("/api/calendar").then((r) => r.json()),
    staleTime: 60_000,
  });
  return useMemo(() => {
    if (!data) return null;
    const today = cabinToday();
    // Same "today is not bookable" rule as useDisabledDates, so the count
    // never claims a weekend the date picker won't actually allow.
    const taken = (date: Date): boolean => {
      if (isBefore(startOfDay(date), today)) return true;
      const dateStr = format(date, "yyyy-MM-dd");
      if (data.blockedDates.some((b) => b.date === dateStr)) return true;
      return data.approvedBookings.some((b) => {
        try { return isWithinInterval(date, { start: parseISO(b.checkIn), end: parseISO(b.checkOut) }); }
        catch { return false; }
      });
    };
    for (let m = 0; m < 2; m++) {
      const monthAnchor = addMonths(today, m);
      const from = m === 0 ? today : new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
      const to = endOfMonth(monthAnchor);
      if (isBefore(to, from)) continue;
      let openWeekends = 0;
      for (const day of eachDayOfInterval({ start: from, end: to })) {
        if (day.getDay() !== 5) continue; // Fridays anchor a weekend
        if (!taken(day) && !taken(addDays(day, 1))) openWeekends++;
      }
      if (openWeekends === 0) continue; // month sold out — check the next one
      if (openWeekends <= 3) return { count: openWeekends, month: format(monthAnchor, "MMMM") };
      return null; // plenty open — claiming urgency would be dishonest
    }
    return null;
  }, [data]);
}

// True when the selected stay includes any blocked/booked night. Checks the
// nights of the stay (check-in through the night before check-out) against
// the same disabled-date predicate the calendar uses, so the booking widget
// can surface the inquiry escape hatch prominently when dates are blocked
// (e.g. dates prefilled from an email link that have since been booked).
function rangeHasUnavailableDates(
  dateRange: DateRange | undefined,
  isDisabled: (date: Date) => boolean,
): boolean {
  const from = dateRange?.from;
  const to = dateRange?.to;
  if (!from || !to) return false;
  const lastNight = addDays(to, -1);
  if (isBefore(lastNight, from)) return false;
  try {
    return eachDayOfInterval({ start: from, end: lastNight }).some(isDisabled);
  } catch {
    return false;
  }
}

function calcPricing(
  dateRange: DateRange | undefined,
  weekdayRate: number = NIGHTLY_RATE,
  weekendRate: number = NIGHTLY_RATE,
) {
  const from = dateRange?.from;
  const to = dateRange?.to;
  if (!from || !to) {
    return { nights: 0, weekdayNights: 0, weekendNights: 0, subtotal: 0, processingFee: 0, discountPercent: DIRECT_DISCOUNT_PCT, discountAmount: 0, taxAmount: 0, taxBreakdown: { mnState: 0, douglasCounty: 0 }, total: 0 };
  }
  const nights = differenceInDays(to, from);
  let subtotal = 0;
  let weekdayNights = 0;
  let weekendNights = 0;
  for (let i = 0; i < nights; i++) {
    const night = new Date(from);
    night.setDate(night.getDate() + i);
    const dow = night.getDay(); // 0=Sun, 5=Fri, 6=Sat
    if (dow === 5 || dow === 6) {
      weekendNights++;
      subtotal += weekendRate;
    } else {
      weekdayNights++;
      subtotal += weekdayRate;
    }
  }
  // Automatic direct-booking discount — mirrors the server's math so the
  // local estimate never diverges from the authoritative quote.
  const discountAmount = nights > 0 ? Math.round(subtotal * DIRECT_DISCOUNT_PCT / 100) : 0;
  const amountAfterDiscount = subtotal - discountAmount;
  const taxAmount = nights > 0 ? Math.round(amountAfterDiscount * TAX_RATE_PCT / 100) : 0;
  const taxMnState = nights > 0 ? Math.round(amountAfterDiscount * 6.875 / 100) : 0;
  const taxDouglas = nights > 0 ? Math.round(amountAfterDiscount * 0.5 / 100) : 0;
  // Local estimate: cleaning fee comes from the server quote and is added by
  // useResolvedPricing. This fallback is only shown briefly while the quote loads.
  const processingFee = nights > 0 ? Math.round(amountAfterDiscount * CARD_PROCESSING_FEE_PCT / 100) : 0;
  const total = amountAfterDiscount + taxAmount + processingFee;
  return { nights, weekdayNights, weekendNights, subtotal, processingFee, discountPercent: DIRECT_DISCOUNT_PCT, discountAmount, taxAmount, taxBreakdown: { mnState: taxMnState, douglasCounty: taxDouglas }, total };
}

interface StayQuote {
  checkIn: string;
  checkOut: string;
  nights: number;
  perNight: { date: string; price: number; reason: string }[];
  subtotal: number;
  cleaningFee: number;
  extraGuestFee?: number;
  extraGuests?: number;
  processingFee: number;
  processingFeePct: number;
  discountPercent: number;
  discountAmount: number;
  taxAmount: number;
  taxPct: number;
  taxBreakdown: { mnState: number; douglasCounty: number } | null;
  total: number;
  avgNightly: number;
  dynamic: boolean;
  minNightsRequired?: number;
  plMinNights?: number;
}

// Fetches the authoritative stay quote from the server. The server uses the same
// pricing engine (host overrides > live PriceLabs > seasonal/base) that computes
// the actual charge, so the displayed total always matches what's charged.
function useStayQuote(dateRange: DateRange | undefined, countedGuests?: number) {
  const checkIn = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined;
  const checkOut = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined;
  const query = useQuery<StayQuote>({
    queryKey: ["/api/pricing/quote", checkIn, checkOut, countedGuests ?? 0],
    enabled: !!checkIn && !!checkOut,
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/pricing/quote?checkIn=${checkIn}&checkOut=${checkOut}${countedGuests ? `&guests=${countedGuests}` : ""}`,
      );
      return res.json();
    },
  });

  // Funnel: fire dates_selected once a full check-in/out range is chosen, and
  // quote_generated once the authoritative server quote arrives. Server-side
  // counting dedupes per session, so repeated fires across widgets are fine.
  useEffect(() => {
    if (checkIn && checkOut) {
      const nights = dateRange?.from && dateRange?.to
        ? differenceInDays(dateRange.to, dateRange.from)
        : undefined;
      trackEvent("dates_selected", { checkIn, checkOut, nights });
    }
  }, [checkIn, checkOut]);
  // quote_generated is intentionally NOT fired here — it fires only after the
  // email+phone gate is cleared so anonymous visitors don't count as leads.

  return query;
}

// Merges the live server quote with the local estimate. Prefers server values
// when available, falling back to the local weekday/weekend estimate while the
// quote is loading or if the request fails.
function useResolvedPricing(
  dateRange: DateRange | undefined,
  weekdayRate: number,
  weekendRate: number,
  countedGuests?: number,
) {
  const local = calcPricing(dateRange, weekdayRate, weekendRate);
  const { data: quote, isLoading } = useStayQuote(dateRange, countedGuests);
  if (quote && quote.nights === local.nights && local.nights > 0) {
    return {
      ...local,
      subtotal: quote.subtotal,
      cleaningFee: quote.cleaningFee ?? 0,
      extraGuestFee: quote.extraGuestFee ?? 0,
      extraGuests: quote.extraGuests ?? 0,
      processingFee: quote.processingFee,
      discountPercent: quote.discountPercent ?? DIRECT_DISCOUNT_PCT,
      discountAmount: quote.discountAmount ?? 0,
      taxAmount: quote.taxAmount ?? 0,
      taxBreakdown: quote.taxBreakdown ?? local.taxBreakdown,
      total: quote.total,
      avgNightly: quote.avgNightly,
      dynamic: quote.dynamic,
      quoteLoading: false as boolean,
    };
  }
  const avgNightly = local.nights > 0 ? Math.round(local.subtotal / local.nights) : 0;
  return { ...local, cleaningFee: 0, extraGuestFee: 0, extraGuests: 0, avgNightly, dynamic: false, quoteLoading: isLoading };
}

function StarRating({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <span className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          style={{ width: size, height: size }}
          className={i < Math.floor(rating) ? "fill-primary text-primary" : "fill-muted text-muted"}
        />
      ))}
    </span>
  );
}

function PhotoGallery({ onOpenGallery, onOpenGalleryAtLast }: { onOpenGallery: (idx: number) => void; onOpenGalleryAtLast: () => void }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, dragFree: false });
  // Defer embla init until after first paint so its synchronous container
  // measurement doesn't force a layout reflow on the critical rendering path.
  // The carousel's height is reserved via CSS below, so the first slide looks
  // identical before and after embla activates.
  const [carouselReady, setCarouselReady] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setCarouselReady(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActiveIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi]);

  return (
    <div className="relative -mx-4 sm:mx-0">
      {/* Mobile: swipeable carousel */}
      <div className="md:hidden relative">
        <div
          ref={carouselReady ? emblaRef : undefined}
          className="overflow-hidden h-[56vh] min-h-[360px] max-h-[540px]"
          data-testid="gallery-carousel"
        >
          <div className="flex h-full">
            {PHOTOS.map((photo, idx) => (
              <div
                key={idx}
                className="flex-[0_0_100%] h-full cursor-pointer relative group"
                onClick={() => onOpenGallery(idx)}
                data-testid={idx === 0 ? "gallery-main-photo" : `gallery-photo-${idx}`}
              >
                <img
                  {...responsiveImage(photo.src, "100vw")}
                  alt={photo.alt}
                  className="w-full h-full object-cover"
                  draggable={false}
                  loading={idx === 0 ? "eager" : "lazy"}
                  fetchPriority={idx === 0 ? "high" : "auto"}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40 pointer-events-none" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-active:opacity-100 transition-opacity duration-150 pointer-events-none">
                  <div className="bg-black/50 rounded-full p-3">
                    <ZoomIn className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            ))}

            {/* End card — final swipeable slide after the featured photos */}
            <div
              className="flex-[0_0_100%] h-full relative bg-gradient-to-br from-stone-900 to-stone-800 flex items-center justify-center"
              data-testid="gallery-end-card"
            >
              <div className="flex flex-col items-center text-center px-8 max-w-xs">
                <Waves className="w-9 h-9 text-white/80 mb-3" />
                <h3 className="text-white text-xl font-semibold leading-snug">
                  Ready for the lake?
                </h3>
                <p className="text-white/70 text-sm mt-1.5 mb-5">
                  Book your stay or take the full photo tour.
                </p>
                <div className="flex flex-col items-stretch gap-2.5 w-full">
                  <Button
                    size="sm"
                    className="w-full font-semibold"
                    onClick={() => {
                      trackEvent("date_picker_opened", { source: "gallery_end_card" });
                      window.dispatchEvent(new CustomEvent("llc:open-booking"));
                    }}
                    data-testid="button-end-card-book-now"
                  >
                    Book now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dot indicators — one extra dot for the end card */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
          {Array.from({ length: PHOTOS.length + 1 }).map((_, idx) => (
            <div
              key={idx}
              className={`rounded-full transition-all duration-200 ${
                idx === activeIndex
                  ? "w-2 h-2 bg-white shadow-sm"
                  : "w-1.5 h-1.5 bg-white/50"
              }`}
            />
          ))}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="absolute bottom-3 left-4 bg-white/95 backdrop-blur-sm font-semibold shadow-md gap-2 text-xs"
          onClick={onOpenGalleryAtLast}
          data-testid="button-show-all-photos"
        >
          <Grid3X3 className="w-3.5 h-3.5" />
          Show all {ALL_PHOTOS.length} photos
        </Button>
      </div>

      {/* Desktop: 5-photo 2×2 grid */}
      <div className="hidden md:block relative">
        <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[480px] rounded-xl overflow-hidden">
          <div
            className="col-span-2 row-span-2 cursor-pointer relative group overflow-hidden"
            onClick={() => onOpenGallery(0)}
            data-testid="gallery-main-photo-desktop"
          >
            <img
              {...responsiveImage(PHOTOS[0].src, "(min-width: 768px) 640px, 100vw")}
              alt={PHOTOS[0].alt}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              loading="eager"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              <div className="bg-black/50 backdrop-blur-sm rounded-full p-4">
                <ZoomIn className="w-7 h-7 text-white" />
              </div>
            </div>
          </div>
          {PHOTOS.slice(1, 5).map((photo, idx) => (
            <div
              key={idx}
              className="cursor-pointer relative group overflow-hidden"
              onClick={() => onOpenGallery(idx + 1)}
              data-testid={`gallery-photo-desktop-${idx + 1}`}
            >
              <img
                {...responsiveImage(photo.src, "(min-width: 768px) 320px, 50vw")}
                alt={photo.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="bg-black/50 backdrop-blur-sm rounded-full p-3">
                  <ZoomIn className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm font-semibold shadow-md gap-2"
          onClick={onOpenGalleryAtLast}
          data-testid="button-show-all-photos-desktop"
        >
          <Grid3X3 className="w-4 h-4" />
          Show all {ALL_PHOTOS.length} photos
        </Button>
      </div>
    </div>
  );
}

/** Pinch-to-zoom and double-tap zoom for a single gallery slide. */
function ZoomableSlide({
  photo,
  index,
  isActive,
  onZoomChange,
}: {
  photo: { src: string; alt: string };
  index: number;
  isActive: boolean;
  onZoomChange: (zoomed: boolean) => void;
}) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const scaleRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchStartDistRef = useRef<number | null>(null);
  const pinchStartScaleRef = useRef(1);
  const pinchMidpointRef = useRef({ x: 0, y: 0 });
  const lastTapRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  // Debounce guard: one zoom gesture (a pinch fires many pointermove events)
  // produces exactly one photo_zoomed beat. Reset when the photo returns to 1x.
  const zoomFiredRef = useRef(false);

  const fireZoom = useCallback(
    (method: "pinch" | "double_tap") => {
      if (zoomFiredRef.current) return;
      zoomFiredRef.current = true;
      trackPhotoZoom({ index, alt: photo.alt, method });
    },
    [index, photo.alt],
  );

  // Reset zoom when switching slides; also notify parent so swipe re-enables
  useEffect(() => {
    if (!isActive) {
      scaleRef.current = 1;
      offsetRef.current = { x: 0, y: 0 };
      setScale(1);
      setOffset({ x: 0, y: 0 });
      onZoomChange(false);
      zoomFiredRef.current = false;
    }
  }, [isActive, onZoomChange]);

  const clampOffset = useCallback((ox: number, oy: number, s: number) => {
    if (!containerRef.current) return { x: ox, y: oy };
    const { width, height } = containerRef.current.getBoundingClientRect();
    const maxX = (width * (s - 1)) / 2;
    const maxY = (height * (s - 1)) / 2;
    return {
      x: Math.min(maxX, Math.max(-maxX, ox)),
      y: Math.min(maxY, Math.max(-maxY, oy)),
    };
  }, []);

  const applyZoom = useCallback(
    (newScale: number, newOffset: { x: number; y: number }) => {
      const clamped = clampOffset(newOffset.x, newOffset.y, newScale);
      scaleRef.current = newScale;
      offsetRef.current = clamped;
      setScale(newScale);
      setOffset(clamped);
      onZoomChange(newScale > 1.05);
    },
    [clampOffset, onZoomChange],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);

      if (pointersRef.current.size === 2) {
        const pts = Array.from(pointersRef.current.values());
        const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
        pinchStartDistRef.current = dist;
        pinchStartScaleRef.current = scaleRef.current;
        pinchMidpointRef.current = {
          x: (pts[0].x + pts[1].x) / 2,
          y: (pts[0].y + pts[1].y) / 2,
        };
      } else if (pointersRef.current.size === 1) {
        // Double-tap detection
        const now = Date.now();
        if (now - lastTapRef.current < 300) {
          if (scaleRef.current > 1.05) {
            applyZoom(1, { x: 0, y: 0 });
            zoomFiredRef.current = false;
          } else {
            const rect = containerRef.current?.getBoundingClientRect();
            const cx = rect ? e.clientX - rect.left - rect.width / 2 : 0;
            const cy = rect ? e.clientY - rect.top - rect.height / 2 : 0;
            const targetScale = 2.5;
            applyZoom(targetScale, {
              x: offsetRef.current.x - cx * (targetScale - scaleRef.current),
              y: offsetRef.current.y - cy * (targetScale - scaleRef.current),
            });
            fireZoom("double_tap");
          }
          lastTapRef.current = 0;
        } else {
          lastTapRef.current = now;
        }
      }
    },
    [applyZoom],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!pointersRef.current.has(e.pointerId)) return;

      const prev = pointersRef.current.get(e.pointerId)!;
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointersRef.current.size === 2 && pinchStartDistRef.current !== null) {
        // Pinch zoom
        const pts = Array.from(pointersRef.current.values());
        const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
        const newScale = Math.min(
          5,
          Math.max(1, pinchStartScaleRef.current * (dist / pinchStartDistRef.current)),
        );
        applyZoom(newScale, offsetRef.current);
        if (newScale > 1.05) fireZoom("pinch");
      } else if (pointersRef.current.size === 1 && scaleRef.current > 1.05) {
        // Pan while zoomed
        applyZoom(scaleRef.current, {
          x: offsetRef.current.x + dx,
          y: offsetRef.current.y + dy,
        });
      }
    },
    [applyZoom],
  );

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) {
      pinchStartDistRef.current = null;
    }
    // Snap back to 1 if pinched below threshold
    if (pointersRef.current.size === 0 && scaleRef.current < 1.05) {
      scaleRef.current = 1;
      offsetRef.current = { x: 0, y: 0 };
      setScale(1);
      setOffset({ x: 0, y: 0 });
      onZoomChange(false);
      zoomFiredRef.current = false;
    }
  }, [onZoomChange]);

  return (
    <div
      ref={containerRef}
      className="flex-[0_0_100%] h-full flex items-center justify-center px-12 sm:px-16 pb-4 touch-none select-none"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <img
        {...responsiveImage(photo.src, "(min-width: 1280px) 1280px, 100vw")}
        alt={photo.alt}
        className="w-full h-full object-contain rounded-lg pointer-events-none select-none"
        draggable={false}
        style={{
          transform: `scale(${scale}) translate(${offset.x / scale}px, ${offset.y / scale}px)`,
          transformOrigin: "center center",
          transition: scale === 1 ? "transform 0.2s ease" : "none",
          willChange: "transform",
          cursor: scale > 1.05 ? "grab" : "default",
        }}
      />
    </div>
  );
}

function GalleryModal({
  open,
  onClose,
  initialIndex,
  onCurrentChange,
}: {
  open: boolean;
  onClose: () => void;
  initialIndex: number;
  onCurrentChange?: (idx: number) => void;
}) {
  const [current, setCurrent] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const thumbnailStripRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (open && emblaApi) {
      emblaApi.scrollTo(initialIndex, true);
      setCurrent(initialIndex);
    }
    if (!open) {
      setIsZoomed(false);
    }
  }, [open, initialIndex, emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      const idx = emblaApi.selectedScrollSnap();
      setCurrent(idx);
      onCurrentChange?.(idx);
    };
    emblaApi.on("select", onSelect);
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi, onCurrentChange]);

  useEffect(() => {
    const strip = thumbnailStripRef.current;
    const thumb = thumbnailRefs.current[current];
    if (!strip || !thumb) return;
    // Scroll only within the thumbnail strip — avoids propagating scroll
    // up to the page body which caused the page to jump when the gallery opened.
    strip.scrollTo({
      left: thumb.offsetLeft - strip.offsetWidth / 2 + thumb.offsetWidth / 2,
      behavior: "smooth",
    });
  }, [current]);

  // Disable Embla drag while a photo is zoomed in so panning doesn't swipe slides
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit({ loop: true, watchDrag: !isZoomed });
  }, [emblaApi, isZoomed]);

  const prev = useCallback(() => { if (!isZoomed) emblaApi?.scrollPrev(); }, [emblaApi, isZoomed]);
  const next = useCallback(() => { if (!isZoomed) emblaApi?.scrollNext(); }, [emblaApi, isZoomed]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, prev, next, onClose]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      {/* flex flex-col (not the shadcn default grid) is load-bearing: with the
          default grid layout the inner column's h-full resolves against an
          auto-sized grid row, collapsing the height chain — the photo then
          renders at natural size and spills past the dialog (all-black
          lightbox bug on mobile). */}
      <DialogContent
        showCloseButton={false}
        className="flex flex-col max-w-screen-xl w-full h-[100dvh] sm:h-[90vh] p-0 gap-0 bg-black border-none overflow-hidden"
      >
        <DialogTitle className="sr-only">Photo Gallery</DialogTitle>
        <DialogDescription className="sr-only">
          Browse full-size photos of the cabin. Swipe or use the arrow buttons to
          move between photos.
        </DialogDescription>
        <div className="relative flex flex-col flex-1 min-h-0">
          <div className="flex items-center justify-between p-4 text-white">
            <span className="text-sm font-medium opacity-75">
              {current + 1} / {ALL_PHOTOS.length}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="text-white"
              onClick={onClose}
              data-testid="button-close-gallery"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex-1 relative overflow-hidden min-h-0">
            <div ref={emblaRef} className="h-full overflow-hidden">
              <div className="flex h-full">
                {ALL_PHOTOS.map((photo, idx) => (
                  <ZoomableSlide
                    key={idx}
                    photo={photo}
                    index={idx}
                    isActive={idx === current}
                    onZoomChange={setIsZoomed}
                  />
                ))}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="!absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/60 flex"
              onClick={prev}
              data-testid="button-gallery-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="!absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/60 flex"
              onClick={next}
              data-testid="button-gallery-next"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </div>
          <p className="text-white/60 text-xs sm:text-sm text-center pt-2 px-6 line-clamp-1">
            {ALL_PHOTOS[current].alt}
          </p>
          <div
            ref={thumbnailStripRef}
            className="flex gap-2 overflow-x-auto px-4 py-3 scrollbar-none"
            data-testid="gallery-thumbnail-strip"
          >
            {ALL_PHOTOS.map((photo, idx) => (
              <button
                key={idx}
                ref={(el) => { thumbnailRefs.current[idx] = el; }}
                onClick={() => emblaApi?.scrollTo(idx)}
                data-testid={`gallery-thumbnail-${idx}`}
                className={[
                  "flex-shrink-0 w-14 h-10 sm:w-16 sm:h-12 rounded overflow-hidden transition-all duration-150 focus:outline-none",
                  idx === current
                    ? "ring-2 ring-white ring-offset-1 ring-offset-black opacity-100"
                    : "opacity-40 hover:opacity-70",
                ].join(" ")}
                aria-label={`Go to photo ${idx + 1}: ${photo.alt}`}
                aria-current={idx === current ? "true" : undefined}
              >
                <img
                  {...responsiveImage(photo.src, "64px")}
                  alt={photo.alt}
                  className="w-full h-full object-cover pointer-events-none select-none"
                  draggable={false}
                />
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BookingCheckout({
  dateRange,
  guests,
  adults,
  children,
  infants,
  dogs = 0,
  onSuccess,
  onStepChange,
  initialDiscountCode = "",
  initialName = "",
  initialEmail = "",
  experimentVariant,
  resumeDraft,
}: {
  dateRange: DateRange | undefined;
  guests: number;
  adults: number;
  children: number;
  infants: number;
  dogs?: number;
  onSuccess: () => void;
  onStepChange?: (step: "info" | "cabinAgreement" | "agreement" | "liability" | "payment" | "success") => void;
  initialDiscountCode?: string;
  initialName?: string;
  initialEmail?: string;
  /** direct_checkout variant key ("control" | "no_gate" | "price_calendar") for funnel tagging. */
  experimentVariant?: string;
  resumeDraft?: CheckoutDraft;
}) {
  const { toast } = useToast();
  const saved = resumeDraft?.form;
  const payment = resumeDraft?.payment;
  const draftRef = useRef(resumeDraft);
  const [step, setStep] = useState<"info" | "cabinAgreement" | "agreement" | "liability" | "payment" | "success">(
    resumeDraft?.phase === "complete" ? "success" : payment ? "payment" : resumeDraft && resumeDraft.phase !== "details" ? "cabinAgreement" : "info",
  );
  const [name, setName] = useState(saved?.name ?? initialName);
  const [email, setEmail] = useState(saved?.email ?? initialEmail);
  const [phone, setPhone] = useState(saved?.phone ?? "");
  const [addrLine1, setAddrLine1] = useState(saved?.addrLine1 ?? "");
  const [addrCity, setAddrCity] = useState(saved?.addrCity ?? "");
  const [addrState, setAddrState] = useState(saved?.addrState ?? "");
  const [addrZip, setAddrZip] = useState(saved?.addrZip ?? "");
  const [message, setMessage] = useState(saved?.message ?? "");
  const [paymentType, setPaymentType] = useState<"full" | "deposit">(saved?.paymentType ?? "full");
  const [pontoonAddon, setPontoonAddon] = useState(saved?.pontoonAddon ?? false);
  const [pontoonInfoOpen, setPontoonInfoOpen] = useState(false);
  const [pontoonCostVal, setPontoonCostVal] = useState(payment?.pontoonAddonCost ?? 0);
  // Opt-in defaults to checked (opt-out): the guest is subscribed unless they
  // untick it. Label stays honest ("unsubscribe anytime").
  const [marketingOptIn, setMarketingOptIn] = useState(saved?.marketingOptIn ?? true);
  const [submitting, setSubmitting] = useState(false);
  const [clientSecret, setClientSecret] = useState(payment?.clientSecret ?? "");
  const [bookingId, setBookingId] = useState(payment?.bookingId ?? "");
  const [totalDollars, setTotalDollars] = useState(payment?.totalDollars ?? 0);
  const [taxAmountVal, setTaxAmountVal] = useState(payment?.taxAmount ?? 0);
  const [chargeAmount, setChargeAmount] = useState(payment?.chargeAmount ?? 0);
  const [depositAmountVal, setDepositAmountVal] = useState(payment?.depositAmount ?? 0);
  const [serverCleaningFee, setServerCleaningFee] = useState(payment?.cleaningFee ?? 0);
  const [serverExtraGuestFee, setServerExtraGuestFee] = useState(payment?.extraGuestFee ?? 0);
  const [serverProcessingFee, setServerProcessingFee] = useState(payment?.processingFee ?? 0);
  const [stripeKey, setStripeKey] = useState<string | null>(resumeDraft?.stripeKey ?? null);
  const [discountCode, setDiscountCode] = useState(saved?.discountCode ?? initialDiscountCode);
  const [discountExpanded, setDiscountExpanded] = useState(!!(saved?.discountCode ?? initialDiscountCode));
  const [discountValidating, setDiscountValidating] = useState(false);
  const [discountValid, setDiscountValid] = useState<{ type: "percent" | "flat_total"; percent: number; flatTotalAmount: number; codeId: string; redundant?: boolean; message?: string } | null>(saved?.discountValid ?? null);
  const [discountError, setDiscountError] = useState("");
  const [appliedDiscountAmount, setAppliedDiscountAmount] = useState(payment?.discountAmount ?? 0);
  const [appliedDiscountSource, setAppliedDiscountSource] = useState<"direct" | "code">(payment?.discountSource === "code" ? "code" : "direct");
  const [waiverAck, setWaiverAck] = useState(false);
  const [waiverName, setWaiverName] = useState("");
  const [liabilityAck, setLiabilityAck] = useState(false);
  const [liabilityName, setLiabilityName] = useState("");
  const [sigDrawn, setSigDrawn] = useState(false);
  const [cabinAgreementMessage, setCabinAgreementMessage] = useState("");
  const cabinAgreementResumeRef = useRef(false);
  const intentInFlight = useRef(false);
  const sigCanvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);

  // Let the host container (e.g. the desktop dialog) react to step changes —
  // the payment step needs a much wider container for its two-column layout.
  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  const { data: settings = {} } = useQuery<Record<string, string>>({ queryKey: ["/api/settings"] });
  const weekdayRate = parseInt(settings.nightly_rate || String(NIGHTLY_RATE)) || NIGHTLY_RATE;
  const weekendRate = parseInt(settings.weekend_rate || String(NIGHTLY_RATE)) || NIGHTLY_RATE;
  const { nights, subtotal, processingFee, discountAmount, total } = useResolvedPricing(dateRange, weekdayRate, weekendRate);
  const pontoonEnabled = settings.pontoon_enabled === "true";
  const pontoonLadder = (() => {
    try {
      const parsed = normalizePontoonLadder(JSON.parse(settings.pontoon_price_ladder || "[]"));
      return parsed.length > 0 ? parsed : DEFAULT_PONTOON_LADDER;
    } catch { return DEFAULT_PONTOON_LADDER; }
  })();

  const pontoonCost = pontoonEnabled && nights > 0
    ? calculatePontoonLadderCost(nights, pontoonLadder).total
    : 0;
  // Best-of discount logic, mirroring the server. The automatic direct-booking
  // discount covers the pontoon add-on too when it's bundled (create-intent
  // discounts subtotal + pontoonCost). `total` already has the cabin discount
  // removed, so we add back only that cabin discount to rebuild the pre-discount
  // base. A manual code only applies when it beats the auto discount — never
  // stacked on top of it.
  const pontoonAutoDiscount = pontoonAddon ? Math.round(pontoonCost * DIRECT_DISCOUNT_PCT / 100) : 0;
  const autoDiscount = discountAmount + pontoonAutoDiscount;
  const baseTotalWithPontoon = total + discountAmount + (pontoonAddon ? pontoonCost : 0);
  const codeDiscountAmount = discountValid
    ? (discountValid.type === "flat_total"
        ? Math.max(0, baseTotalWithPontoon - discountValid.flatTotalAmount)
        : Math.round(subtotal * discountValid.percent / 100))
    : 0;
  // Best-of: a manual code never stacks with the automatic direct-booking
  // discount — the guest gets whichever saves more (matching the server).
  const bestDiscountAmount = Math.max(autoDiscount, codeDiscountAmount);
  const totalWithPontoon = baseTotalWithPontoon - bestDiscountAmount;

  const depositPct = 25;
  const depositEstimate = Math.round(totalWithPontoon * depositPct / 100);

  const referralSlug = typeof document !== "undefined"
    ? (document.cookie.match(/(?:^|;\s*)ref=([^;]*)/)?.[1] || "")
    : "";

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountValidating(true);
    setDiscountError("");
    setDiscountValid(null);
    try {
      const res = await fetch("/api/discount-codes/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Email is sent so email-bound codes (WELCOME-) get early feedback;
        // the server enforces the binding again at charge time regardless.
        body: JSON.stringify({ code: discountCode.trim(), email: email.trim() || undefined }),
      });
      const data = await res.json();
      if (!res.ok) {
        setDiscountError(data.message || "Invalid code");
        // Classify the failure reason for the abandonment view.
        const errMsg = (data.message || "").toLowerCase();
        const result = errMsg.includes("expired") ? "expired"
          : errMsg.includes("already") ? "already_used"
          : "invalid";
        trackEvent("coupon_attempted", { code: discountCode.trim().toUpperCase(), result });
      } else {
        setDiscountValid({
          type: data.discountType === "flat_total" ? "flat_total" : "percent",
          percent: data.discountPercent ?? 0,
          flatTotalAmount: data.flatTotalAmount ?? 0,
          codeId: data.codeId,
          redundant: data.redundant === true,
          message: data.message,
        });
        trackEvent("coupon_attempted", { code: discountCode.trim().toUpperCase(), result: "success" });
      }
    } catch {
      setDiscountError("Failed to validate code");
    } finally {
      setDiscountValidating(false);
    }
  };

  const handleRemoveDiscount = () => {
    setDiscountValid(null);
    setDiscountCode("");
    setDiscountError("");
  };

  // Clean up any legacy stashed promo code from the old reveal-a-code popup.
  // Every direct booking now gets 10% off automatically — no code needed.
  useEffect(() => {
    try {
      sessionStorage.removeItem("llc_promo_code");
    } catch {
      /* no-op */
    }
  }, []);

  const openCabinAgreement = async () => {
    try {
      if (!dateRange?.from || !dateRange?.to) throw new Error("Select your stay dates before signing.");
      let previous: CheckoutDraft | undefined;
      if (draftRef.current) {
        const existing = await loadCheckout(draftRef.current.id);
        previous = existing;
        if (!["details", "agreement"].includes(existing.phase)) {
          window.location.assign(checkoutReturnUrl(existing.id));
          return;
        }
      }
      // Same-tab navigation works on iOS and keeps all checkout state durable.
      // Re-signing replaces only this checkout's envelope, never another stay's.
      const draft: CheckoutDraft = {
        id: draftRef.current?.id ?? crypto.randomUUID(),
        createdAt: previous?.createdAt ?? Date.now(), revision: previous?.revision,
        checkIn: format(dateRange.from, "yyyy-MM-dd"), checkOut: format(dateRange.to, "yyyy-MM-dd"),
        adults, children, infants, dogs, experimentVariant, quotedTotal: totalWithPontoon,
        form: { name, email, phone, addrLine1, addrCity, addrState, addrZip, message, paymentType, pontoonAddon, marketingOptIn, discountCode, discountValid },
        phase: "agreement",
      };
      await saveCheckout(draft);
      draftRef.current = draft;
      window.location.assign(`/rental-agreement?prebooking=1&checkoutId=${encodeURIComponent(draft.id)}`);
    } catch (err) {
      setCabinAgreementMessage((err as Error).message);
    }
  };

  const handleCreatePaymentIntent = async (waiverSignedName?: string, signatureImage?: string | null) => {
    if (!dateRange?.from || !dateRange?.to || intentInFlight.current) return;
    // Once created, reuse the same payment even if the guest backs up.
    if (clientSecret) { setStep("payment"); return; }
    intentInFlight.current = true;
    setSubmitting(true);
    try {
      let draft = await loadCheckout(draftRef.current?.id ?? null);
      if (draft.phase === "payment" || draft.phase === "complete") {
        window.location.assign(checkoutReturnUrl(draft.id));
        return;
      }
      if (draft.phase === "creating") {
        throw new Error("The previous payment setup is still being checked. Please contact the host before trying again; do not start a second checkout.");
      }
      if (!hasCheckoutSignature(draft)) {
        setCabinAgreementMessage("Your signature is missing or expired. Review and sign the agreement again; your checkout details are still here.");
        setStep("cabinAgreement");
        return;
      }
      const keyRes = await fetch("/api/stripe/publishable-key");
      const keyData = await keyRes.json();
      if (!keyData.publishableKey) {
        throw new Error(keyData.message || "Stripe is not configured. Contact the host to complete your booking.");
      }
      setStripeKey(keyData.publishableKey);

      // Persist the in-flight marker before sending. A lost response or reload
      // must not silently create a second pending booking/PaymentIntent.
      draft = { ...draft, phase: "creating" };
      await saveCheckout(draft);

      const res = await fetch("/api/payments/create-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          guestName: name,
          guestEmail: email,
          guestPhone: phone,
          guestAddress: { line1: addrLine1, city: addrCity, state: addrState, postalCode: addrZip },
          checkIn: format(dateRange.from, "yyyy-MM-dd"),
          checkOut: format(dateRange.to, "yyyy-MM-dd"),
          guests,
          adults,
          children,
          infants,
          dogs,
          message,
          paymentType,
          pontoonAddon,
          marketingOptIn,
          discountCode: discountValid ? discountCode.trim() : undefined,
          referralSlug: referralSlug || undefined,
          waiverSignedName: waiverSignedName || undefined,
          cabinAgreement: draft.signature,
          sessionId: getSessionId(),
          visitorId: getVisitorId(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // A definitive rejection is safe to retry. Ambiguous 5xx/network
        // failures keep the creating marker and require host reconciliation.
        if (
          data.agreementRequired === true ||
          data.code === "CABIN_AGREEMENT_REQUIRED" ||
          data.code === "AGREEMENT_REQUIRED"
        ) {
          await saveCheckout({ ...draft, phase: "agreement", signature: undefined, signatureAt: undefined });
          setCabinAgreementMessage(data.message || "Review and sign the current cabin rental agreement before continuing to payment.");
          setStep("cabinAgreement");
          return;
        }
        if (res.status < 500) await saveCheckout({ ...draft, phase: "agreement" });
        throw new Error(data.message || "Failed to create payment");
      }
      if (!data.clientSecret || !data.bookingId) throw new Error("Payment setup did not return a valid booking. Contact the host before retrying.");
      const savedPayment: CheckoutPayment = {
        clientSecret: data.clientSecret, bookingId: data.bookingId, totalDollars: data.totalDollars,
        taxAmount: data.taxAmount ?? 0, chargeAmount: data.chargeAmount, depositAmount: data.depositAmount || 0,
        cleaningFee: data.cleaningFee ?? 0, extraGuestFee: data.extraGuestFee ?? 0, processingFee: data.processingFee ?? 0,
        pontoonAddonCost: data.pontoonAddonCost || 0, discountAmount: data.discountAmount || 0, discountSource: data.discountSource,
      };
      const readyDraft: CheckoutDraft = { ...draft, phase: "payment", payment: savedPayment, stripeKey: keyData.publishableKey, signature: undefined, signatureAt: undefined };
      await saveCheckout(readyDraft);
      draftRef.current = readyDraft;
      // Keep reload/back navigation on the saved payment, not the empty widget.
      window.history.replaceState(null, "", checkoutReturnUrl(draft.id));
      if (Math.abs(data.totalDollars - draft.quotedTotal) > 1) {
        toast({ title: "Current price updated", description: "Your current total is shown below. Review it before submitting payment." });
      }
      setClientSecret(data.clientSecret);
      setBookingId(data.bookingId);
      setTotalDollars(data.totalDollars);
      setTaxAmountVal(data.taxAmount ?? 0);
      setChargeAmount(data.chargeAmount);
      setDepositAmountVal(data.depositAmount || 0);
      setServerCleaningFee(data.cleaningFee ?? 0);
      setServerExtraGuestFee(data.extraGuestFee ?? 0);
      setServerProcessingFee(data.processingFee ?? 0);
      setPontoonCostVal(data.pontoonAddonCost || 0);
      setAppliedDiscountAmount(data.discountAmount || 0);
      setAppliedDiscountSource(data.discountSource === "code" ? "code" : "direct");
      // Upload the drawn signature image separately (fire-and-forget) once the
      // booking exists, mirroring the standalone /pontoon page. Sent as its own
      // request to avoid proxy WAF 403s on large base64 bodies.
      if (signatureImage && data.bookingId) {
        fetch(`/api/pontoon/${data.bookingId}/signature`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ signatureImage }),
        }).catch(() => {});
      }
      setStep("payment");
      // The stashed promo code (if any) has now been consumed by this
      // booking — clear it so later unrelated checkouts start clean.
      try {
        sessionStorage.removeItem("llc_promo_code");
      } catch {
        /* no-op */
      }
      trackEvent("checkout_started", {
        bookingId: data.bookingId,
        total: data.totalDollars,
        ...(experimentVariant ? { experiment: "direct_checkout", variant: experimentVariant } : {}),
      });
    } catch (err: any) {
      setCabinAgreementMessage(err.message || "Unable to resume payment. Please contact the host.");
      toast({ title: "Something went wrong", description: err.message || "Please try again.", variant: "destructive" });
    } finally {
      intentInFlight.current = false;
      setSubmitting(false);
    }
  };

  // Only the explicitly restored checkout may consume its own signature.
  // Never auto-submit from a storage/focus event in a second, hidden tab.
  useEffect(() => {
    if (!resumeDraft || step !== "cabinAgreement" || cabinAgreementResumeRef.current) return;
    cabinAgreementResumeRef.current = true;
    if (resumeDraft.phase === "creating") {
      setCabinAgreementMessage("Payment setup was interrupted. Contact the host before trying again so we can check the existing booking.");
    } else if (hasCheckoutSignature(resumeDraft)) {
      if (pontoonAddon) setStep("agreement");
      else void handleCreatePaymentIntent();
    } else {
      setCabinAgreementMessage("Your signature is missing or expired. Sign again to continue; your dates and guest details have been restored.");
    }
  }, [resumeDraft, step, pontoonAddon]);

  const handleContinue = async () => {
    if (!name || !email) {
      toast({ title: "Missing info", description: "Please enter your name and email.", variant: "destructive" });
      return;
    }
    if (!phone.trim()) {
      toast({ title: "Phone required", description: "Please enter your phone number to continue.", variant: "destructive" });
      return;
    }
    if (!addrLine1.trim() || !addrCity.trim() || !addrState.trim() || !addrZip.trim()) {
      toast({ title: "Billing address required", description: "Please enter your billing address so we can process your booking and calculate tax.", variant: "destructive" });
      return;
    }
    if (!dateRange?.from || !dateRange?.to) {
      toast({ title: "No dates selected", description: "Please select check-in and check-out dates.", variant: "destructive" });
      return;
    }
    const baseMinNights = Math.max(1, parseInt(settings.min_nights || "1") || 1);
    // Cabin seasonal mid-week exception: a 3-night Mon→Thu stay in Aug/Sep 2026
    // qualifies for a reduced minimum; all other dates keep the base minimum.
    const minNights = getApplicableMinNights(dateRange.from, nights, baseMinNights);
    if (nights < minNights) {
      toast({ title: `Minimum ${minNights} nights`, description: `This stay requires at least ${minNights} night${minNights !== 1 ? "s" : ""}. Please extend your dates.`, variant: "destructive" });
      return;
    }
    if (discountCode.trim() && !discountValid) {
      toast({ title: "Invalid discount code", description: discountError || "Please apply a valid code or remove it before continuing.", variant: "destructive" });
      return;
    }
    // For no-gate variants: capture the guest's lead at the checkout info step
    // (no email gate was shown, so this is the first chance to record email).
    if (experimentVariant === "no_gate" || experimentVariant === "price_calendar") {
      fetch("/api/leads/capture", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          name: name.trim() || null,
          visitorId: getVisitorId(),
          sessionId: getSessionId(),
          checkIn: dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : null,
          checkOut: dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : null,
          source: "checkout_form",
        }),
      }).catch(() => {});
    }
    // Back from payment must reuse the existing intent, not create another.
    if (clientSecret) { setStep("payment"); return; }
    let hasCurrentCabinAgreement = false;
    try {
      hasCurrentCabinAgreement = hasCheckoutSignature(readCheckout(draftRef.current?.id ?? null));
    } catch {
      // Normal first entry has no saved checkout yet; the sign button saves it.
    }
    if (!hasCurrentCabinAgreement) {
      setCabinAgreementMessage("Review and sign the cabin rental agreement before continuing to payment.");
      setStep("cabinAgreement");
      return;
    }
    if (pontoonAddon) {
      setStep("agreement");
      return;
    }
    await handleCreatePaymentIntent();
  };

  const handleAdvanceToLiability = () => {
    if (!waiverAck) {
      toast({ title: "Please acknowledge the rules", description: "Check the box to confirm you've read and agree to the rules and fees.", variant: "destructive" });
      return;
    }
    if (waiverName.trim().length < 2) {
      toast({ title: "Signature required", description: "Please type your full legal name to sign the rental agreement.", variant: "destructive" });
      return;
    }
    setStep("liability");
  };

  const drawOnCanvas = (x: number, y: number) => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.lineTo(x, y);
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    if (!sigDrawn) setSigDrawn(true);
  };
  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    isDrawingRef.current = true;
    const rect = e.currentTarget.getBoundingClientRect();
    const ctx = e.currentTarget.getContext("2d");
    ctx?.beginPath();
    ctx?.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };
  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    drawOnCanvas(e.clientX - rect.left, e.clientY - rect.top);
  };
  const handleCanvasMouseUp = () => { isDrawingRef.current = false; };
  const handleClearSig = () => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
    setSigDrawn(false);
  };

  const captureSignatureJpeg = (): string | null => {
    const src = sigCanvasRef.current;
    if (!src) return null;
    const off = document.createElement("canvas");
    off.width = 300;
    off.height = 72;
    const ctx = off.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, 300, 72);
    ctx.drawImage(src, 0, 0, 300, 72);
    return off.toDataURL("image/jpeg", 0.7);
  };

  // Attach touch handlers as non-passive so preventDefault() stops page scroll
  // while drawing the signature on touch devices.
  useEffect(() => {
    if (step !== "liability") return;
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      isDrawingRef.current = true;
      const rect = canvas.getBoundingClientRect();
      const ctx = canvas.getContext("2d");
      ctx?.beginPath();
      ctx?.moveTo(e.touches[0].clientX - rect.left, e.touches[0].clientY - rect.top);
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (!isDrawingRef.current) return;
      const rect = canvas.getBoundingClientRect();
      drawOnCanvas(e.touches[0].clientX - rect.left, e.touches[0].clientY - rect.top);
    };
    const onTouchEnd = () => { isDrawingRef.current = false; };
    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", onTouchEnd);
    return () => {
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", onTouchEnd);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleSignLiability = async () => {
    if (!liabilityAck) {
      toast({ title: "Please acknowledge the waiver", description: "Check the box to confirm you've read and agree to the liability waiver.", variant: "destructive" });
      return;
    }
    if (!sigDrawn) {
      toast({ title: "Signature required", description: "Please draw your signature in the box.", variant: "destructive" });
      return;
    }
    if (liabilityName.trim().length < 2) {
      toast({ title: "Full legal name required", description: "Please type your full legal name to sign.", variant: "destructive" });
      return;
    }
    const signatureImage = captureSignatureJpeg();
    await handleCreatePaymentIntent(liabilityName.trim(), signatureImage);
  };

  const referralLink = bookingId
    ? `${window.location.origin}/r/${bookingId}`
    : "";
  const [refCopied, setRefCopied] = useState(false);
  const handleCopyReferral = () => {
    navigator.clipboard.writeText(referralLink);
    setRefCopied(true);
    setTimeout(() => setRefCopied(false), 2000);
  };

  if (step === "success") {
    return (
      <div className="text-center space-y-4 py-6 max-w-lg mx-auto" data-testid="booking-success">
        <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
          <Check className="w-7 h-7 text-primary" />
        </div>
        <h3 className="font-serif text-xl font-bold">Booking Confirmed!</h3>
        <p className="text-muted-foreground text-sm">
          {paymentType === "deposit"
            ? `Your deposit of $${depositAmountVal.toLocaleString()} has been received. The remaining balance of $${(totalDollars - depositAmountVal).toLocaleString()} is due before check-in.`
            : "Your reservation is confirmed. You'll receive a confirmation email shortly."
          }
        </p>
        <div className="bg-muted/40 rounded-lg p-3 text-sm text-left space-y-1">
          <div className="flex justify-between"><span className="text-muted-foreground">Guest</span><span className="font-medium">{name}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Check-in</span><span className="font-medium">{dateRange?.from && format(dateRange.from, "MMM d, yyyy")}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Check-out</span><span className="font-medium">{dateRange?.to && format(dateRange.to, "MMM d, yyyy")}</span></div>
          {pontoonAddon && pontoonCostVal > 0 && (
            <div className="flex justify-between"><span className="text-muted-foreground">Pontoon boat add-on</span><span className="font-medium">${pontoonCostVal.toLocaleString()}</span></div>
          )}
          <div className="flex justify-between"><span className="text-muted-foreground">{paymentType === "deposit" ? "Deposit paid" : "Total paid"}</span><span className="font-medium">${chargeAmount.toLocaleString()}</span></div>
          {paymentType === "deposit" && (
            <div className="flex justify-between"><span className="text-muted-foreground">Balance due</span><span className="font-medium text-amber-600">${(totalDollars - depositAmountVal).toLocaleString()}</span></div>
          )}
        </div>
        {paymentType === "deposit" && (
          <p className="text-xs text-muted-foreground">
            You can pay the remaining balance from your booking confirmation page.
          </p>
        )}
        {bookingId && (
          <p className="text-xs text-muted-foreground">
            Booking ID: <span data-testid="text-booking-id" className="font-mono">{bookingId}</span>
          </p>
        )}
        {referralLink && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-left" data-testid="referral-link-section">
            <p className="text-sm font-semibold text-foreground mb-1">Share with friends</p>
            <p className="text-xs text-muted-foreground mb-2">Share your referral link with friends. Booking direct avoids platform service fees.</p>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={referralLink}
                className="flex-1 border border-input rounded-md px-2 py-1.5 text-xs bg-background truncate"
                data-testid="input-referral-link"
              />
              <Button size="sm" variant="outline" onClick={handleCopyReferral} className="shrink-0 gap-1" data-testid="button-copy-referral">
                {refCopied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
                {refCopied ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>
        )}
        <Button onClick={onSuccess} variant="outline" className="w-full" data-testid="button-done">Done</Button>
      </div>
    );
  }

  if (step === "payment" && clientSecret && stripeKey) {
    const stayNights = dateRange?.from && dateRange?.to ? differenceInDays(dateRange.to, dateRange.from) : 0;
    // Derived purely from server-quoted amounts so every line sums exactly to Total.
    const cabinStayLine = totalDollars - taxAmountVal - pontoonCostVal + appliedDiscountAmount - serverCleaningFee - serverExtraGuestFee - serverProcessingFee;
    return (
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-12 lg:items-start">
        {/* Trip details + policy — shown first on mobile, right rail on desktop */}
        <div className="space-y-4 lg:order-2 lg:sticky lg:top-2">
          <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden" data-testid="card-trip-details">
            <div className="flex items-center gap-3.5 p-4 sm:p-5">
              <img {...responsiveImage(PROPERTY_THUMBNAIL, "96px")} alt={PROPERTY_NAME} className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover shrink-0" />
              <div className="min-w-0">
                <p className="font-serif font-semibold text-[15px] leading-snug">{PROPERTY_NAME}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Lake Miltona · Alexandria, Minnesota</p>
                <p className="flex items-center gap-1 text-xs mt-1.5">
                  <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
                  <span className="font-semibold text-foreground">{REVIEW_AVG}</span>
                  <span className="text-muted-foreground">· {REVIEW_COUNT} reviews</span>
                </p>
              </div>
            </div>
            <div className="border-t border-border px-4 sm:px-5 py-4 space-y-3">
              <div className="text-sm">
                <p className="font-medium text-foreground">Dates</p>
                <p className="text-muted-foreground mt-0.5">
                  {dateRange?.from && format(dateRange.from, "MMM d")} – {dateRange?.to && format(dateRange.to, "MMM d, yyyy")}
                  {stayNights > 0 && ` · ${stayNights} night${stayNights !== 1 ? "s" : ""}`}
                </p>
              </div>
              <div className="text-sm">
                <p className="font-medium text-foreground">Guests</p>
                <p className="text-muted-foreground mt-0.5">{guestSummaryText({ adults, children, infants, dogs })}</p>
              </div>
              <div className="text-sm">
                <p className="font-medium text-foreground">Booked by</p>
                <p className="text-muted-foreground mt-0.5">{name}</p>
              </div>
            </div>
            <div className="border-t border-border px-4 sm:px-5 py-4 text-sm space-y-2" data-testid="card-price-details">
              <p className="font-semibold text-foreground mb-1">Price details</p>
              {stayNights > 0 && cabinStayLine > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">Cabin · {stayNights} night{stayNights !== 1 ? "s" : ""}</span><span>${cabinStayLine.toLocaleString()}</span></div>
              )}
              {pontoonAddon && pontoonCostVal > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">Pontoon boat add-on</span><span>${pontoonCostVal.toLocaleString()}</span></div>
              )}
              {serverCleaningFee > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">Cleaning fee</span><span>${serverCleaningFee.toLocaleString()}</span></div>
              )}
              {serverExtraGuestFee > 0 && (
                <div className="flex justify-between" data-testid="text-payment-extra-guest-fee"><span className="text-muted-foreground">Extra guest fee</span><span>${serverExtraGuestFee.toLocaleString()}</span></div>
              )}
              {appliedDiscountAmount > 0 && (
                <div className="flex justify-between text-green-700 dark:text-green-500" data-testid="text-payment-discount"><span>{appliedDiscountSource === "code" ? `Discount (${discountCode.trim().toUpperCase()})` : "Direct booking discount"}</span><span>-${appliedDiscountAmount.toLocaleString()}</span></div>
              )}
              {taxAmountVal > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">Taxes (7.375%)</span><span>${taxAmountVal.toLocaleString()}</span></div>
              )}
              {serverProcessingFee > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">Card processing fee (3%)</span><span>${serverProcessingFee.toLocaleString()}</span></div>
              )}
              {dogs > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">Pet fee ({dogs} dog{dogs !== 1 ? "s" : ""})</span><span>${(dogs * 250).toLocaleString()}</span></div>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between items-baseline">
                <span className="font-semibold text-foreground">Total <span className="font-normal text-muted-foreground">(USD)</span></span>
                <span className="font-semibold text-base" data-testid="text-payment-total">${totalDollars.toLocaleString()}</span>
              </div>
              {paymentType === "deposit" && (
                <div className="rounded-lg bg-primary/5 border border-primary/15 px-3 py-2.5 mt-2 space-y-1">
                  <div className="flex justify-between text-sm text-primary font-semibold"><span>Due today</span><span>${chargeAmount.toLocaleString()}</span></div>
                  <div className="flex justify-between text-xs text-muted-foreground"><span>Balance due before check-in</span><span>${(totalDollars - chargeAmount).toLocaleString()}</span></div>
                </div>
              )}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-sm p-4 sm:p-5" data-testid="card-cancellation-policy">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-foreground">Cancellation policy</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {FIRM_CANCELLATION_POLICY}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Payment — left column on desktop */}
        <div className="lg:order-1 min-w-0">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-lg font-semibold text-foreground">Pay with</h3>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="w-3 h-3" aria-hidden="true" /> Secure checkout
            </span>
          </div>
          <Suspense fallback={<div className="h-32 flex items-center justify-center text-sm text-muted-foreground">Loading secure payment…</div>}>
            <StripePaymentPanel
              publishableKey={stripeKey}
              clientSecret={clientSecret}
              bookingId={bookingId}
              chargeAmount={chargeAmount}
              billingDetails={{
                name: name.trim() || undefined,
                email: email.trim() || undefined,
                phone: phone.trim() || undefined,
                address: {
                  line1: addrLine1.trim(),
                  city: addrCity.trim(),
                  state: addrState.trim(),
                  postal_code: addrZip.trim(),
                  country: "US",
                },
              }}
              onSuccess={async () => {
                trackEvent("booking_paid", { bookingId, total: totalDollars, chargeAmount });
                recordConversion();
                if (draftRef.current) {
                  try {
                    const completed: CheckoutDraft = {
                      ...draftRef.current, phase: "complete", signature: undefined, stripeKey: undefined,
                      // Retain the receipt amounts on reload, never a reusable
                      // payment client secret after completion.
                      payment: draftRef.current.payment ? { ...draftRef.current.payment, clientSecret: "" } : undefined,
                    };
                    await saveCheckout(completed);
                    draftRef.current = completed;
                  } catch { /* Payment succeeded; never retry it because storage failed. */ }
                }
                setStep("success");
              }}
              onBack={() => {
                // Do not edit booking facts after proof/payment binding.
                toast({ title: "Your payment is ready", description: "To change this reservation's details, contact the host before paying. Your current booking and payment have been kept." });
              }}
            />
          </Suspense>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-4" data-testid="card-on-file-notice">
            By completing your booking, you authorize {PROPERTY_NAME} to securely save this card on file and charge it for any incidentals during your stay — such as lost keys, damage, or extra cleaning — in accordance with the rental terms. Your card won't be charged beyond the amount shown above unless such charges apply.
          </p>
        </div>


      </div>
    );
  }

  if (step === "agreement") {
    return (
      <div className="space-y-5 max-w-xl">
        <div>
          <button
            onClick={() => setStep("info")}
            className="text-xs text-muted-foreground hover:text-foreground mb-3 flex items-center gap-1"
          >
            <ChevronLeft className="w-3 h-3" /> Back
          </button>
          <h3 className="font-serif text-lg font-bold text-foreground mb-1">Pontoon rental agreement</h3>
          <p className="text-sm text-muted-foreground">
            Review the rules and fees below, then acknowledge and sign. Required by Minnesota state law for watercraft rental.
          </p>
        </div>

        <div className="bg-muted/40 border border-border rounded-xl p-4 space-y-2 max-h-[300px] overflow-y-auto">
          {PONTOON_HOUSE_RULES.map((rule, i) => (
            <div key={i} className="flex items-start gap-2.5 text-sm text-foreground" data-testid={`waiver-item-${i}`}>
              <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
              <span className="leading-snug">{rule}</span>
            </div>
          ))}
        </div>

        <label
          className={`flex items-start gap-3 border-2 rounded-xl p-3.5 cursor-pointer transition-all ${waiverAck ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
          data-testid="waiver-ack-checkbox"
        >
          <input
            type="checkbox"
            checked={waiverAck}
            onChange={e => setWaiverAck(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-primary shrink-0"
          />
          <span className="text-sm text-foreground leading-snug font-medium">I have read and agree to all of the above rules and fees.</span>
        </label>

        <div className="border-t border-border pt-4 space-y-2">
          <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Sign with your full legal name
          </label>
          <input
            value={waiverName}
            onChange={e => setWaiverName(e.target.value)}
            placeholder="Type your full name"
            className="w-full border border-input rounded-xl px-3 py-3 text-sm bg-background font-serif italic focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="input-waiver-signature"
          />
          <p className="text-xs text-muted-foreground">
            By signing, you agree to the Legendary Log Cabin Pontoon Rental Agreement and authorize us to charge your card on file for any unpaid fees or damages.
          </p>
        </div>

        <Button
          onClick={handleAdvanceToLiability}
          disabled={!waiverAck || waiverName.trim().length < 2}
          className="w-full h-12 text-base font-semibold gap-2"
          data-testid="button-sign-and-continue"
        >
          Continue to liability waiver
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  if (step === "cabinAgreement") {
    return (
      <div className="space-y-5 max-w-xl text-center" data-testid="cabin-agreement-gate">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <ShieldCheck className="h-6 w-6 text-primary" aria-hidden="true" />
        </div>
        <div>
          <h3 className="font-serif text-lg font-bold text-foreground">Agreement required before payment</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {cabinAgreementMessage || "Read and sign the cabin rental agreement. No payment can be created until the signed agreement is verified."}
          </p>
        </div>
        <button
          type="button"
          onClick={openCabinAgreement}
          disabled={submitting || resumeDraft?.phase === "creating"}
          className="inline-flex h-11 w-full items-center justify-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground"
          data-testid="button-review-cabin-agreement"
        >
          Review and sign agreement
        </button>
        <Button
          className="w-full"
          variant="outline"
          disabled={submitting}
          onClick={() => {
            if (pontoonAddon) {
              try {
                if (!hasCheckoutSignature(readCheckout(draftRef.current?.id ?? null))) throw new Error("Sign the cabin agreement before continuing.");
                setStep("agreement");
              } catch (err) { setCabinAgreementMessage((err as Error).message); }
            } else void handleCreatePaymentIntent();
          }}
          data-testid="button-check-cabin-agreement"
        >
          I’ve signed — continue
        </Button>
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setStep("info")}
          data-testid="button-back-from-cabin-agreement"
        >
          ← Back to guest details
        </button>
      </div>
    );
  }

  if (step === "liability") {
    return (
      <div className="space-y-5 max-w-xl">
        <div>
          <button
            onClick={() => setStep("agreement")}
            className="text-xs text-muted-foreground hover:text-foreground mb-3 flex items-center gap-1"
          >
            <ChevronLeft className="w-3 h-3" /> Back
          </button>
          <h3 className="font-serif text-xl font-bold text-foreground mb-1">Liability Waiver</h3>
          <p className="text-sm text-muted-foreground">
            Read each clause, then acknowledge and sign below. This is a binding legal agreement.
          </p>
        </div>

        <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1 -mr-1">
          {PONTOON_LIABILITY_CLAUSES.map((c, i) => (
            <div key={i} className="flex gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold mt-0.5">{i + 1}</div>
              <div>
                <p className="text-sm font-semibold text-foreground mb-0.5">{c.title}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </div>
            </div>
          ))}
        </div>

        <label
          className={`flex items-start gap-3 border-2 rounded-xl p-3.5 cursor-pointer transition-all ${liabilityAck ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
          data-testid="liability-ack-checkbox"
        >
          <input
            type="checkbox"
            checked={liabilityAck}
            onChange={e => setLiabilityAck(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-primary shrink-0"
          />
          <span className="text-sm text-foreground leading-snug font-medium">I have read and fully understand this waiver. I voluntarily agree to its terms on behalf of myself and all passengers.</span>
        </label>

        <div className="border border-border rounded-2xl overflow-hidden">
          <div className="bg-muted/40 border-b border-border px-4 py-3 flex items-center justify-between">
            <span className="text-xs font-semibold text-foreground">Draw your signature</span>
            {sigDrawn && (
              <button onClick={handleClearSig} className="text-xs text-muted-foreground hover:text-foreground underline" data-testid="button-clear-signature">Clear</button>
            )}
          </div>
          <div className="relative bg-white">
            <canvas
              ref={sigCanvasRef}
              width={500}
              height={120}
              className="w-full h-[120px] cursor-crosshair block"
              style={{ display: "block", touchAction: "none" }}
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={handleCanvasMouseUp}
              data-testid="signature-canvas"
            />
            {!sigDrawn && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-sm text-muted-foreground select-none">Sign here with your finger or mouse</p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Full legal name
          </label>
          <input
            value={liabilityName}
            onChange={e => setLiabilityName(e.target.value)}
            placeholder="Type your full name"
            className="w-full border border-input rounded-xl px-3 py-3 text-sm bg-background font-serif italic focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="input-liability-signature"
          />
          <p className="text-xs text-muted-foreground leading-relaxed">
            By signing, I agree that my electronic signature is the legal equivalent of my handwritten signature, enforceable under the E-SIGN Act (15 U.S.C. § 7001) and Minn. Stat. § 325L.
          </p>
        </div>

        <Button
          onClick={handleSignLiability}
          disabled={submitting || !liabilityAck || !sigDrawn || liabilityName.trim().length < 2}
          className="w-full h-12 text-base font-semibold gap-2"
          data-testid="button-liability-sign"
        >
          {submitting ? "Reserving…" : "Sign & continue to payment"}
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-w-xl">
      <div className="flex items-center gap-3 bg-muted/40 rounded-lg p-3 mb-1" data-testid="booking-property-header">
        <img {...responsiveImage(PROPERTY_THUMBNAIL, "56px")} alt={PROPERTY_NAME} className="w-14 h-14 rounded-lg object-cover shrink-0" />
        <div className="min-w-0">
          <p className="font-serif font-semibold text-sm truncate">{PROPERTY_NAME}</p>
          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
            <Star className="w-3 h-3 fill-primary text-primary" />
            <span>Top rated</span>
            <span>· Lake Miltona, MN</span>
          </div>
        </div>
      </div>
      <div className="space-y-1 bg-muted/40 rounded-lg p-3 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Check-in</span>
          <span className="font-medium">
            {dateRange?.from ? format(dateRange.from, "MMM d, yyyy") : <span className="text-muted-foreground italic">Not selected</span>}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Check-out</span>
          <span className="font-medium">
            {dateRange?.to ? format(dateRange.to, "MMM d, yyyy") : <span className="text-muted-foreground italic">Not selected</span>}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Guests</span>
          <span className="font-medium">{guestSummaryText({ adults, children, infants, dogs })}</span>
        </div>
      </div>
      <div>
        <label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground block mb-1">Full Name *</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name"
          name="name" autoComplete="name" autoCapitalize="words" enterKeyHint="next"
          className="w-full border border-input rounded-lg px-3 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-ring bg-background"
          data-testid="input-name" />
      </div>
      <div>
        <label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground block mb-1">Email *</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
          name="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" enterKeyHint="next"
          className="w-full border border-input rounded-lg px-3 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-ring bg-background"
          data-testid="input-email" />
      </div>
      <div>
        <label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground block mb-1">Phone *</label>
        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 000-0000 *"
          name="tel" autoComplete="tel" inputMode="tel" enterKeyHint="next"
          className="w-full border border-input rounded-lg px-3 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-ring bg-background"
          data-testid="input-phone" />
        <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
          If checkout gets interrupted, we may follow up by phone or text — sometimes via Samantha, our AI booking assistant.
        </p>
      </div>
      <div>
        <label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground block mb-1">Billing Address *</label>
        <input type="text" value={addrLine1} onChange={(e) => setAddrLine1(e.target.value)} placeholder="Street address"
          className="w-full border border-input rounded-lg px-3 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-ring bg-background"
          data-testid="input-address-line1" />
        <div className="grid grid-cols-2 gap-2 mt-2">
          <input type="text" value={addrCity} onChange={(e) => setAddrCity(e.target.value)} placeholder="City"
            className="w-full border border-input rounded-lg px-3 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-ring bg-background"
            data-testid="input-address-city" />
          <input type="text" value={addrState} onChange={(e) => setAddrState(e.target.value)} placeholder="State"
            className="w-full border border-input rounded-lg px-3 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-ring bg-background"
            data-testid="input-address-state" />
        </div>
        <input type="text" value={addrZip} onChange={(e) => setAddrZip(e.target.value)} placeholder="ZIP code"
          className="w-full border border-input rounded-lg px-3 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-ring bg-background mt-2"
          data-testid="input-address-zip" />
        <p className="text-xs text-muted-foreground mt-1">Required for billing and accurate tax calculation.</p>
      </div>
      <div>
        <label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground block mb-1">Message (optional)</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us about your trip..."
          rows={2} className="w-full border border-input rounded-lg px-3 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-ring bg-background resize-none"
          data-testid="input-message" />
      </div>
      <label className="flex items-start gap-2.5 cursor-pointer py-1">
        <input
          type="checkbox"
          checked={marketingOptIn}
          onChange={(e) => setMarketingOptIn(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-input accent-primary"
          data-testid="checkbox-marketing-optin"
        />
        <span className="text-xs text-muted-foreground leading-relaxed">
          Yes, send me special offers and updates — unsubscribe anytime
        </span>
      </label>
      {nights > 0 && (
        <div className="border border-border rounded-lg overflow-hidden" data-testid="discount-code-section">
          <button
            className="w-full flex items-center justify-between p-3 text-left text-sm hover:bg-muted/30 transition-colors"
            onClick={() => setDiscountExpanded(!discountExpanded)}
            data-testid="button-toggle-discount"
          >
            <span className="font-medium text-muted-foreground">Have a discount code?</span>
            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${discountExpanded ? "rotate-180" : ""}`} />
          </button>
          {discountExpanded && (
            <div className="border-t border-border p-3 space-y-2">
              {discountValid ? (
                <div className="flex items-center justify-between bg-green-50 dark:bg-green-950/30 rounded-lg p-3">
                  <div>
                    <p className="text-sm font-medium text-green-700 dark:text-green-400" data-testid="text-discount-applied">
                      {discountValid.redundant
                        ? (discountValid.message || "This code has no discount value.")
                        : discountValid.type === "flat_total"
                          ? `Flat total of $${discountValid.flatTotalAmount.toLocaleString()} applied`
                          : "Discount applied"}
                    </p>
                    {!discountValid.redundant && (
                      <p className="text-xs text-green-600 dark:text-green-500">
                        Code: {discountCode.toUpperCase()}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleRemoveDiscount}
                    className="text-xs text-red-500 hover:text-red-700 font-medium"
                    data-testid="button-remove-discount"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => { setDiscountCode(e.target.value); setDiscountError(""); }}
                    placeholder="Enter code"
                    className="flex-1 border border-input rounded-lg px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-ring bg-background uppercase"
                    data-testid="input-discount-code"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleApplyDiscount}
                    disabled={discountValidating || !discountCode.trim()}
                    className="shrink-0"
                    data-testid="button-apply-discount"
                  >
                    {discountValidating ? "..." : "Apply"}
                  </Button>
                </div>
              )}
              {discountError && (
                <p className="text-xs text-destructive" data-testid="text-discount-error">{discountError}</p>
              )}
            </div>
          )}
        </div>
      )}
      {nights > 0 && pontoonEnabled && (
        <div
          className={`rounded-xl p-4 space-y-3 cursor-pointer transition-all ${pontoonAddon ? "border-2 border-primary bg-primary/5" : "border border-border hover:border-primary/40"}`}
          onClick={() => setPontoonAddon(!pontoonAddon)}
          data-testid="pontoon-addon-card"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <img
                src="/photos/pontoon-sunset-400.webp"
                alt="25-ft pontoon boat docked at sunset"
                loading="lazy"
                className="w-14 h-14 rounded-lg object-cover shrink-0 mt-0.5"
                data-testid="img-pontoon-thumb"
              />
              <div>
                <p className="text-sm font-semibold text-foreground">Add Pontoon Boat</p>
                <p className="text-xs text-muted-foreground mt-0.5">25-ft pontoon rental add-on, docked on-site</p>
                {pontoonCost > 0 && (
                  <>
                    <p className="text-sm font-semibold text-primary mt-1" data-testid="text-pontoon-addon-price">
                      +${pontoonCost.toLocaleString()}
                      <span className="text-xs font-normal text-muted-foreground"> · {nights} night{nights !== 1 ? "s" : ""}</span>
                    </p>
                  </>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setPontoonInfoOpen((o) => { if (!o) trackAmenityEngaged("pontoon", { via: "addon_learn_more" }); return !o; }); }}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary mt-1.5 hover:underline"
                  aria-expanded={pontoonInfoOpen}
                  data-testid="toggle-pontoon-info"
                >
                  {pontoonInfoOpen ? "Show less" : "Learn more"}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${pontoonInfoOpen ? "rotate-180" : ""}`} />
                </button>
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setPontoonAddon(!pontoonAddon); }}
              className={`w-11 h-6 rounded-full transition-colors relative shrink-0 mt-1 ${pontoonAddon ? "bg-primary" : "bg-muted-foreground/30"}`}
              data-testid="toggle-pontoon-addon"
            >
              <div className={`w-5 h-5 rounded-full bg-white shadow-sm absolute top-0.5 transition-transform ${pontoonAddon ? "translate-x-[22px]" : "translate-x-0.5"}`} />
            </button>
          </div>
          <div
            onClick={(e) => e.stopPropagation()}
            className={`grid transition-all duration-300 ease-in-out ${pontoonInfoOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
            data-testid="pontoon-info-panel"
          >
            <div className="overflow-hidden">
              <div className="pt-1 cursor-default">
                <img
                  src="/photos/pontoon-sunset.webp"
                  alt="Spend the day on the lake aboard the 25-ft pontoon"
                  loading="lazy"
                  className="w-full h-40 sm:h-48 rounded-lg object-cover"
                  data-testid="img-pontoon-large"
                />
                <p className="text-xs text-muted-foreground leading-relaxed mt-3">
                  Spend your days on the water with our 25-ft pontoon — docked and ready right at the cabin, so there's no
                  trailering or setup. Seats up to 14 with life jackets included. Just add it during booking and it's waiting
                  for you when you arrive.
                </p>
                <ul className="mt-3 space-y-1.5">
                  {PONTOON_VALUE_PROPS.map((p) => (
                    <li key={p.title} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1 shrink-0" />
                      <span><span className="font-semibold text-foreground">{p.title}</span> — {p.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      <Button className="w-full font-semibold py-5" onClick={handleContinue} disabled={submitting} data-testid="button-continue-payment">
        {submitting ? "Preparing checkout..." : "Request to Book"}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        You won't be charged yet
      </p>
      <p className="text-xs text-muted-foreground text-center">
        Secure payment · SSL encrypted · {FIRM_CANCELLATION_POLICY}
      </p>
    </div>
  );
}

function useInitialBookingDates(): DateRange | undefined {
  const [initial] = useState<DateRange | undefined>(() => {
    const params = new URLSearchParams(window.location.search);
    const checkIn = params.get("checkIn");
    const checkOut = params.get("checkOut");
    if (checkIn && checkOut) {
      const from = new Date(checkIn + "T12:00:00");
      const to = new Date(checkOut + "T12:00:00");
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
        window.history.replaceState({}, "", window.location.pathname);
        return { from, to };
      }
    }
    return undefined;
  });
  return initial;
}

// Airbnb-style party breakdown. Infants (under 2) are never counted toward the
// guest total or the listing capacity, matching Airbnb.
type GuestCounts = { adults: number; children: number; infants: number; dogs: number };

const DEFAULT_GUEST_COUNTS: GuestCounts = { adults: 2, children: 0, infants: 0, dogs: 0 };

// Airbnb caps infants at 5 regardless of listing capacity.
const MAX_INFANTS = 5;

// Direct cabin bookings allow up to 16 counted guests — the nightly rate
// includes 12, then the $100/guest/night extra-guest fee applies. Mirrors the
// mobile app's stepper cap.
const BOOKABLE_MAX_GUESTS = 16;

// The billable/counted guest total is adults + children (infants excluded).
function guestTotal(g: GuestCounts): number {
  return g.adults + g.children;
}

// Airbnb-style row summary: "2 guests", "3 guests, 1 infant", "2 guests, 1 dog".
function guestSummaryText(g: GuestCounts): string {
  const total = guestTotal(g);
  let s = `${total} guest${total !== 1 ? "s" : ""}`;
  if (g.infants > 0) s += `, ${g.infants} infant${g.infants !== 1 ? "s" : ""}`;
  if (g.dogs > 0) s += `, ${g.dogs} dog${g.dogs !== 1 ? "s" : ""}`;
  return s;
}

function GuestStepperRow({
  label,
  sublabel,
  value,
  onDecrement,
  onIncrement,
  decrementDisabled,
  incrementDisabled = false,
  testid,
}: {
  label: string;
  sublabel: string;
  value: number;
  onDecrement: () => void;
  onIncrement: () => void;
  decrementDisabled: boolean;
  incrementDisabled?: boolean;
  testid: string;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-semibold text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{sublabel}</p>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onDecrement}
          disabled={decrementDisabled}
          aria-label={`Decrease ${label}`}
          className="w-8 h-8 rounded-full border border-input flex items-center justify-center text-foreground transition-colors hover:border-foreground disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-input"
          data-testid={`button-${testid}-decrement`}
        >
          <Minus className="w-4 h-4" />
        </button>
        <span className="w-5 text-center text-sm font-semibold tabular-nums" data-testid={`text-${testid}-count`}>
          {value}
        </span>
        <button
          type="button"
          onClick={onIncrement}
          disabled={incrementDisabled}
          aria-label={`Increase ${label}`}
          className="w-8 h-8 rounded-full border border-input flex items-center justify-center text-foreground transition-colors hover:border-foreground disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-input"
          data-testid={`button-${testid}-increment`}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Shared Airbnb-style guest picker body (adults / children / infants steppers).
// Enforces min 1 adult; no upper guest cap (we don't want to turn parties away).
// Used by both the desktop booking widget (in a popover) and the mobile sticky
// sheet (inline).
function GuestPickerPanel({
  counts,
  onChange,
}: {
  counts: GuestCounts;
  onChange: (next: GuestCounts) => void;
}) {
  const set = (patch: Partial<GuestCounts>) => onChange({ ...counts, ...patch });
  return (
    <div>
      <GuestStepperRow
        label="Adults"
        sublabel="Age 13+"
        value={counts.adults}
        onDecrement={() => set({ adults: Math.max(1, counts.adults - 1) })}
        onIncrement={() => set({ adults: counts.adults + 1 })}
        decrementDisabled={counts.adults <= 1}
        incrementDisabled={guestTotal(counts) >= BOOKABLE_MAX_GUESTS}
        testid="adults"
      />
      <Separator />
      <GuestStepperRow
        label="Children"
        sublabel="Ages 2–12"
        value={counts.children}
        onDecrement={() => set({ children: Math.max(0, counts.children - 1) })}
        onIncrement={() => set({ children: counts.children + 1 })}
        decrementDisabled={counts.children <= 0}
        incrementDisabled={guestTotal(counts) >= BOOKABLE_MAX_GUESTS}
        testid="children"
      />
      <Separator />
      <GuestStepperRow
        label="Infants"
        sublabel="Under 2"
        value={counts.infants}
        onDecrement={() => set({ infants: Math.max(0, counts.infants - 1) })}
        onIncrement={() => set({ infants: counts.infants + 1 })}
        decrementDisabled={counts.infants <= 0}
        incrementDisabled={counts.infants >= MAX_INFANTS}
        testid="infants"
      />
      <Separator />
      <GuestStepperRow
        label="Dogs"
        sublabel="$250 per dog · per stay"
        value={counts.dogs}
        onDecrement={() => set({ dogs: Math.max(0, counts.dogs - 1) })}
        onIncrement={() => set({ dogs: counts.dogs + 1 })}
        decrementDisabled={counts.dogs <= 0}
        testid="dogs"
      />
    </div>
  );
}

function BookingWidget({ initialDates }: { initialDates?: DateRange }) {
  const { toast } = useToast();
  const [dateRange, setDateRange] = useState<DateRange | undefined>(initialDates);
  const [guestCounts, setGuestCounts] = useState<GuestCounts>(DEFAULT_GUEST_COUNTS);
  const guests = guestTotal(guestCounts);
  const [calOpen, setCalOpen] = useState(false);
  const [showInquiry, setShowInquiry] = useState(false);
  // Mirrors BookingCheckout's internal step so the dialog can widen + retitle
  // for the two-column "Confirm and pay" layout on desktop.
  const [checkoutStep, setCheckoutStep] = useState<"info" | "cabinAgreement" | "agreement" | "liability" | "payment" | "success">("info");
  const [showDateInquiry, setShowDateInquiry] = useState(false);
  // A discount code an AI agent seeded via the WebMCP booking tool, pre-filled
  // into checkout when the guest opens it.
  const [mcpDiscountCode, setMcpDiscountCode] = useState("");
  // Inquiry-dialog fields an AI agent seeded via the WebMCP inquiry tool,
  // pre-filled into the dialog when it opens. Undefined for humans.
  const [mcpInquiryName, setMcpInquiryName] = useState<string | undefined>(undefined);
  const [mcpInquiryEmail, setMcpInquiryEmail] = useState<string | undefined>(undefined);
  const [mcpInquiryMessage, setMcpInquiryMessage] = useState<string | undefined>(undefined);
  // Why the guest opened the inquiry dialog — short-stay request vs.
  // unavailable dates — so the dialog copy speaks to the right situation.
  const [inquiryContext, setInquiryContext] = useState<"short_stay" | "unavailable" | "backup_interest">("unavailable");
  const cta = useExperiment("booking_cta");
  const ctaBold = cta.variantKey === "bold";
  // direct_checkout experiment: A=control (email gate), B=no_gate, C=price_calendar.
  // defer=true: exposure fires on dates-selected, not page-render, for a cleaner denominator.
  const directCheckout = useExperiment("direct_checkout", true);
  const isNoGate = directCheckout.variantKey === "no_gate" || directCheckout.variantKey === "price_calendar";
  const isPriceCalendar = directCheckout.variantKey === "price_calendar";
  // Phone is always required — gate_phone experiment ended, phone gate is mandatory.
  const { data: widgetSettings = {} } = useQuery<Record<string, string>>({ queryKey: ["/api/settings"] });
  const widgetWeekdayRate = parseInt(widgetSettings.nightly_rate || String(NIGHTLY_RATE)) || NIGHTLY_RATE;
  const widgetWeekendRate = parseInt(widgetSettings.weekend_rate || String(NIGHTLY_RATE)) || NIGHTLY_RATE;
  const { nights, subtotal, cleaningFee, extraGuestFee, extraGuests, processingFee, discountAmount, taxAmount, taxBreakdown, total, avgNightly, quoteLoading } = useResolvedPricing(dateRange, widgetWeekdayRate, widgetWeekendRate, guests);
  // Fire direct_checkout exposure only once the guest has selected dates — a
  // meaningful intent signal — rather than at page-render, so the denominator
  // only counts visitors who actually engaged with the booking widget.
  const hasDatesWidget = nights > 0;
  useEffect(() => {
    if (hasDatesWidget) fireExposure("direct_checkout");
  }, [hasDatesWidget]);
  // gate_phone: fire exposure when the gate form becomes visible (dates selected + gate active)
  const gateActiveForExposure = hasDatesWidget && !isNoGate;
  useEffect(() => {
    if (gateActiveForExposure) fireExposure("gate_phone");
  }, [gateActiveForExposure]);
  const [taxExpanded, setTaxExpanded] = useState(false);
  // Effective per-night after the direct-booking discount, so the headline
  // "/ night" price matches what the guest actually pays (the discount applies
  // to the room subtotal, not the fees).
  const discountedNightly = nights > 0 && discountAmount > 0 ? Math.round((subtotal - discountAmount) / nights) : avgNightly;
  const widgetMinNights = Math.max(1, parseInt(widgetSettings.min_nights || "1") || 1);
  // Variant C: per-date nightly prices for calendar cells. Same pricing
  // engine as /api/pricing/calendar so displayed prices match the final quote.
  const { data: calendarPriceData } = useQuery<{ prices: Record<string, number> }>({
    queryKey: ["/api/pricing/dates"],
    queryFn: () => fetch("/api/pricing/dates?months=6").then((r) => r.json()),
    enabled: isPriceCalendar,
    staleTime: 10 * 60_000,
  });
  const calendarPrices = useMemo(
    () =>
      isPriceCalendar && calendarPriceData?.prices
        ? new Map(Object.entries(calendarPriceData.prices).map(([k, v]) => [k, Number(v)]))
        : undefined,
    [isPriceCalendar, calendarPriceData],
  );
  // Track which check-in date the guest selects inside the picker so we can
  // probe PriceLabs for that specific date before checkout is chosen.  Without
  // this, the picker only knows the PL minimum after both dates are selected
  // (when the full quote loads), so the calendar would allow too-short checkouts.
  const [pickerCheckIn, setPickerCheckIn] = useState<Date | undefined>(dateRange?.from);
  const pickerCheckInIso = pickerCheckIn ? format(pickerCheckIn, "yyyy-MM-dd") : undefined;
  const { data: probeAvail } = useQuery({
    queryKey: ["/api/availability/check", pickerCheckInIso, "pl-probe"],
    enabled: !!pickerCheckInIso,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const co = pickerCheckIn ? format(addDays(pickerCheckIn, 1), "yyyy-MM-dd") : "";
      const res = await fetch(`/api/availability/check?checkIn=${pickerCheckInIso}&checkOut=${co}`);
      if (!res.ok) return null;
      return res.json() as Promise<{ plMinNights?: number } | null>;
    },
  });
  // plMinNights = raw PriceLabs floor (not blended with seasonal exception).
  // Falls back to quote when the probe hasn't loaded yet. If neither is ready,
  // 0 means "no PL restriction" — the picker seasonal rules still apply.
  const { data: widgetQuote } = useStayQuote(dateRange);
  const pickerPlMinNights = probeAvail?.plMinNights ?? widgetQuote?.plMinNights ?? 0;
  const [guestPopoverOpen, setGuestPopoverOpen] = useState(false);
  const { isDisabled: disabledDate, isTentativeVrboDate, tentativeVrboRangeFor } = useCalendarAvailability();
  const noDates = !dateRange?.from || !dateRange?.to;
  const datesUnavailable = rangeHasUnavailableDates(dateRange, disabledDate);
  const scarcity = useWeekendScarcity();

  // No dead ends: when the picked range is blocked, ask the server for the
  // closest workable windows and offer them as one-tap chips instead of only
  // an inquiry wall.
  const altFromIso = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined;
  const altToIso = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined;
  const { data: altCheck } = useQuery({
    queryKey: ["/api/availability/check", altFromIso, altToIso, "alternatives"],
    enabled: !!altFromIso && !!altToIso && datesUnavailable,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await fetch(`/api/availability/check?checkIn=${altFromIso}&checkOut=${altToIso}`);
      if (!res.ok) return null;
      return res.json() as Promise<{
        available?: boolean;
        alternatives?: { checkIn: string; checkOut: string; nights: number }[];
      } | null>;
    },
  });
  const altWindows = datesUnavailable ? (altCheck?.alternatives ?? []).slice(0, 3) : [];
  const applyAltWindow = (a: { checkIn: string; checkOut: string }) => {
    const from = new Date(a.checkIn + "T12:00:00");
    const to = new Date(a.checkOut + "T12:00:00");
    if (isNaN(from.getTime()) || isNaN(to.getTime())) return;
    setPickerCheckIn(from);
    setDateRange({ from, to });
  };

  // Same inline email gate as the mobile drawer: once dates are picked, ask
  // for name + email before revealing the personalized quote, so a visitor who
  // never finishes booking still lands on the leads board. In-memory only —
  // it resets on reload, matching the mobile behavior.
  const [gateCaptured, setGateCaptured] = useState(false);
  const [gateEmail, setGateEmail] = useState("");
  const [gateName, setGateName] = useState("");
  const [gatePhone, setGatePhone] = useState("");
  const [gateSubmitting, setGateSubmitting] = useState(false);
  const gateEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(gateEmail.trim());
  // Variant B/C skip the email gate — quote is visible immediately on date selection.
  const gateActive = nights > 0 && !gateCaptured && !isNoGate;
  // Control: quote shows after gate. Variants B/C: immediately on date selection.
  const showQuote = nights > 0 && (gateCaptured || isNoGate);

  // Fire once when the personalized quote is first shown (after the email gate
  // passes). Then arm a one-shot scroll listener: if they scroll after seeing
  // the price they stayed engaged — captured as quote_scrolled.
  const widgetQuoteViewedRef = useRef(false);
  useEffect(() => {
    if (!showQuote || widgetQuoteViewedRef.current) return;
    widgetQuoteViewedRef.current = true;
    trackEvent("quote_viewed", { source: "widget" });
    const onScroll = () => trackEvent("quote_scrolled", { source: "widget" });
    window.addEventListener("scroll", onScroll, { once: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [showQuote]);

  const handleCaptureEmail = async () => {
    const emailTrim = gateEmail.trim().toLowerCase();
    if (!emailTrim || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) return;
    const normalizedPhone = normalizeQuotePhone(gatePhone);
    if (!normalizedPhone) {
      toast({ title: "Real phone number required", description: "Please enter your real 10-digit phone number to see your price.", variant: "destructive" });
      return;
    }
    setGateSubmitting(true);
    try {
      try {
        const verRes = await fetch("/api/verify-email", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: emailTrim }) });
        if (verRes.ok) {
          const ver = await verRes.json();
          if (!ver.valid) {
            toast({ title: "Invalid email address", description: ver.reason ?? "Please use a real email to see your price.", variant: "destructive" });
            setGateSubmitting(false);
            return;
          }
        }
      } catch { /* network error — fail open */ }
      let phoneForCapture = normalizedPhone;
      try {
        const verPhone = await fetch("/api/verify-phone", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone: phoneForCapture }) });
        if (verPhone.ok) {
          const verP = await verPhone.json();
          if (!verP.valid) {
            toast({ title: "Invalid phone number", description: verP.reason ?? "That doesn't look like a real phone number — please double-check it to see your price.", variant: "destructive" });
            setGateSubmitting(false);
            return;
          }
          if (verP.e164) phoneForCapture = verP.e164;
        }
      } catch { /* network error — fail open (phone) */ }
      const visitorId = getVisitorId();
      const sessionId = getSessionId();
      await fetch("/api/leads/capture", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: emailTrim,
          name: gateName.trim() || null,
          phone: phoneForCapture || null,
          // Typing a number under the visible consent note = okay to call.
          aiCallOptIn: !!phoneForCapture,
          visitorId,
          sessionId,
          checkIn: dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : null,
          checkOut: dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : null,
          source: "widget_gate",
        }),
      });
      if (window.geq && window.geq.identify) {
        try { window.geq.identify(emailTrim); } catch (e) {}
      }
      trackEvent("email_captured", { source: "widget_gate", email: emailTrim, visitorId });
      // Fire quote_generated now that the guest has identified — this is what
      // the leads board counts as a "price quote".
      const ci = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined;
      const co = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined;
      if (ci && co) trackEvent("quote_generated", { source: "widget_gate", checkIn: ci, checkOut: co });
    } catch {
      /* silently continue — never block the guest on a lead-capture hiccup */
    } finally {
      setGateSubmitting(false);
    }
    // An early validation return still runs finally; only unlock after validation.
    setGateCaptured(true);
  };

  // 30-second attention nudge (same as the mobile sticky CTA): pulse the
  // widget's book button after the visitor has idled without engaging the
  // booking flow. Once dismissed (calendar/inquiry opened or dates picked)
  // it never comes back.
  const [ctaAttention, setCtaAttention] = useState(false);
  const attentionDismissed = useRef(false);
  useEffect(() => {
    const t = setTimeout(() => {
      if (!attentionDismissed.current) setCtaAttention(true);
    }, 30_000);
    // Tasteful, not desperate: let the pulse run ~3 cycles then stop on its own.
    const stop = setTimeout(() => setCtaAttention(false), 35_400);
    return () => { clearTimeout(t); clearTimeout(stop); };
  }, []);
  useEffect(() => {
    if (calOpen || showInquiry || showDateInquiry || (dateRange?.from && dateRange?.to)) {
      attentionDismissed.current = true;
      setCtaAttention(false);
    }
  }, [calOpen, showInquiry, showDateInquiry, dateRange]);

  // Open the calendar and record the intent. Routing the CTA here when no dates
  // are picked yet is what turns "interested" visitors into date-pickers
  // (the dates_selected funnel event), instead of dead-ending on a form.
  const openCalendar = (source: string) => {
    trackEvent("date_picker_opened", { source });
    setCalOpen(true);
  };

  // Expose the booking flow to MCP-capable AI agents (ChatGPT, Claude, etc.).
  // The tool drives THIS widget's state and returns a live quote — no behavior
  // change for human visitors (it no-ops unless the browser exposes WebMCP).
  useBookingMcpTool({
    applyDates: (range) => setDateRange(range),
    // The agent tool passes a single guest number — map it onto the adult count
    // (children/infants stay as-is) so the breakdown-based widget still opens
    // with the requested party size.
    applyGuests: (n) => setGuestCounts((c) => ({ ...c, adults: Math.max(1, Math.floor(n)) })),
    applyDiscountCode: (code) => setMcpDiscountCode(code),
  });

  // Expose the date-inquiry dialog to AI agents. The tool pre-fills the dialog
  // (dates, guests, name, email, message) and opens it — it never submits, so
  // the human still reviews and presses "Send inquiry". No-op for humans.
  useDateInquiryMcpTool({
    openWithPrefill: (p) => {
      if (p.checkIn && p.checkOut) setDateRange({ from: p.checkIn, to: p.checkOut });
      if (p.guests !== undefined) {
        setGuestCounts((c) => ({ ...c, adults: Math.max(1, Math.floor(p.guests!)) }));
      }
      setMcpInquiryName(p.name ?? "");
      setMcpInquiryEmail(p.email);
      setMcpInquiryMessage(p.message ?? "");
      setInquiryContext("unavailable");
      setShowDateInquiry(true);
    },
  });

  // Expose the BookingCheckout ("Reserve your stay") dialog to AI agents. The
  // tool pre-fills dates, guests, and any discount code and opens the dialog on
  // its info step — it never advances the waiver/liability/payment steps and
  // never charges. The human still enters details, signs, and pays. No-op for
  // humans (no navigator.modelContext).
  useStartCheckoutMcpTool({
    openWithPrefill: (p) => {
      setDateRange({ from: p.checkIn, to: p.checkOut });
      setGuestCounts((c) => ({ ...c, adults: Math.max(1, Math.floor(p.guests)) }));
      if (p.discountCode) setMcpDiscountCode(p.discountCode);
      if (!window.matchMedia("(max-width: 1023px)").matches) {
        setShowInquiry(true);
      }
    },
  });

  // Two-state inquiry entry point (Airbnb-style contextual prominence):
  // "prominent" when the selection includes blocked dates, "quiet" otherwise.
  // Both open the dialog in its "unavailable dates" framing; the calendar's
  // min-stay notice opens it in "short_stay" framing instead (see onInquire).
  const openDateInquiry = (state: "prominent" | "quiet") => {
    trackEvent("date_inquiry_opened", { source: "widget", state, trigger: "unavailable" });
    setInquiryContext("unavailable");
    setShowDateInquiry(true);
  };

  useEffect(() => {
    const handler = (e: Event) => {
      const { checkIn, checkOut } = ((e as CustomEvent).detail ?? {}) as {
        checkIn?: string;
        checkOut?: string;
      };
      if (checkIn && checkOut) {
        const from = new Date(checkIn + "T12:00:00");
        const to = new Date(checkOut + "T12:00:00");
        if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
          setDateRange({ from, to });
          if (!window.matchMedia("(max-width: 1023px)").matches) {
            setShowInquiry(true);
          }
        }
      }
    };
    window.addEventListener("llc:open-booking", handler);
    return () => window.removeEventListener("llc:open-booking", handler);
  }, []);

  // Savings-popup handoff: open the availability calendar directly. Fired as
  // an event when the popup closes on the home page (desktop), and as a
  // one-shot sessionStorage flag when the handoff navigates here from another
  // page (the event would fire before this widget mounts).
  useEffect(() => {
    const handler = () => {
      trackEvent("date_picker_opened", { source: "savings_popup_handoff" });
      setCalOpen(true);
    };
    window.addEventListener("llc:open-calendar", handler);
    try {
      if (sessionStorage.getItem("llc_open_calendar")) {
        sessionStorage.removeItem("llc_open_calendar");
        if (!window.matchMedia("(max-width: 1023px)").matches) {
          setTimeout(handler, 600);
        }
      }
    } catch {
      /* no-op */
    }
    return () => window.removeEventListener("llc:open-calendar", handler);
  }, []);

  return (
    <>
      <div className="bg-card border border-card-border rounded-2xl p-6 shadow-xl sticky top-24">
        <div className="flex items-baseline gap-2 mb-5">
          {showQuote ? (
            <>
              {discountAmount > 0 && (
                <span className="font-serif text-lg font-normal text-muted-foreground line-through">
                  ${avgNightly.toLocaleString()}
                </span>
              )}
              <span className="font-serif text-[1.6rem] font-bold text-foreground">
                ${discountedNightly.toLocaleString()}
              </span>
              <span className="text-muted-foreground text-[15px]">/ night</span>
            </>
          ) : noDates ? (
            <button
              type="button"
              className="font-serif text-lg font-bold text-foreground text-left leading-snug hover:underline underline-offset-4 decoration-primary/50"
              onClick={() => openCalendar("widget_price_prompt")}
              data-testid="button-price-prompt"
            >
              Enter dates to see your price
            </button>
          ) : (
            <span className="font-serif text-lg font-bold text-foreground leading-snug" data-testid="text-price-prompt">
              Your price is one step away
            </span>
          )}
          <div className="ml-auto flex items-center gap-1 text-sm font-semibold">
            <Star className="w-4 h-4 fill-primary text-primary" />
            <span>{REVIEW_AVG}</span>
            <span className="text-muted-foreground font-normal">· {REVIEW_COUNT} reviews</span>
          </div>
        </div>

        <div className={`border rounded-xl overflow-hidden mb-4 transition-shadow duration-200 ${noDates ? "border-primary/60 ring-2 ring-primary/15" : "border-input"}`}>
          <div className="grid grid-cols-2 divide-x divide-input">
            <button
              className="p-3.5 text-left hover:bg-muted/40 transition-colors duration-150 group"
              onClick={() => openCalendar("widget_checkin_field")}
              data-testid="button-checkin"
            >
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
                Check-in
              </p>
              <p className={`text-sm font-semibold ${dateRange?.from ? "text-foreground" : "text-muted-foreground"}`}>
                {dateRange?.from ? format(dateRange.from, "MMM d, yyyy") : "Add date"}
              </p>
            </button>
            <button
              className="p-3.5 text-left hover:bg-muted/40 transition-colors duration-150"
              onClick={() => openCalendar("widget_checkout_field")}
              data-testid="button-checkout"
            >
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
                Check-out
              </p>
              <p className={`text-sm font-semibold ${dateRange?.to ? "text-foreground" : "text-muted-foreground"}`}>
                {dateRange?.to ? format(dateRange.to, "MMM d, yyyy") : "Add date"}
              </p>
            </button>
          </div>
          <Dialog open={calOpen} onOpenChange={setCalOpen}>
            <DialogContent showCloseButton={false} className="max-w-md sm:max-w-2xl p-0 overflow-hidden flex flex-col" style={{ height: "min(640px, 90vh)" }}>
              <DialogTitle className="sr-only">Select dates</DialogTitle>
              <AirbnbDatePicker
                value={dateRange}
                onSave={(range) => { setDateRange(range); setCalOpen(false); }}
                onClose={() => setCalOpen(false)}
                isDisabled={disabledDate}
                monthsToShow={18}
                minNights={widgetMinNights}
                plMinNights={pickerPlMinNights}
                midweekException
                onFromChange={(d) => setPickerCheckIn(d)}
                datePrices={calendarPrices}
                onInquire={(range) => {
                  trackEvent("date_inquiry_opened", { source: "widget", state: "prominent", trigger: "short_stay" });
                  setDateRange(range);
                  setCalOpen(false);
                  setInquiryContext("short_stay");
                  setShowDateInquiry(true);
                }}
                isBackupInterestDate={isTentativeVrboDate}
                onBackupInterest={(date) => {
                  const range = tentativeVrboRangeFor(date);
                  trackEvent("date_inquiry_opened", { source: "widget", state: "prominent", trigger: "backup_interest" });
                  setDateRange(range);
                  setCalOpen(false);
                  setInquiryContext("backup_interest");
                  setShowDateInquiry(true);
                }}
              />
            </DialogContent>
          </Dialog>

          <div className="border-t border-input">
            <Popover open={guestPopoverOpen} onOpenChange={setGuestPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-full p-3.5 text-left"
                  aria-label="Select guests"
                  data-testid="button-guests"
                >
                  <span className="block text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
                    Guests
                  </span>
                  <span className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground" data-testid="text-guests-summary">
                      {guestSummaryText(guestCounts)}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${guestPopoverOpen ? "rotate-180" : ""}`} />
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-4" data-testid="popover-guests">
                <GuestPickerPanel counts={guestCounts} onChange={setGuestCounts} />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {gateActive && (
          <div className="space-y-3 mb-4" data-testid="panel-widget-email-gate">
            <p className="text-sm text-muted-foreground">
              Enter your name and email to reveal live pricing for your dates.
            </p>
            <input
              className="w-full rounded-lg border border-input px-3 py-2.5 text-sm bg-background"
              placeholder="Your name"
              value={gateName}
              onChange={(e) => setGateName(e.target.value)}
              type="text"
              name="name"
              autoComplete="name"
              autoCapitalize="words"
              enterKeyHint="next"
              data-testid="input-widget-gate-name"
            />
            <input
              className="w-full rounded-lg border border-input px-3 py-2.5 text-sm bg-background"
              placeholder="Email address"
              type="email"
              value={gateEmail}
              onChange={(e) => setGateEmail(e.target.value)}
              name="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              enterKeyHint="next"
              data-testid="input-widget-gate-email"
            />
            <input
              className="w-full rounded-lg border border-input px-3 py-2.5 text-sm bg-background"
              placeholder="Phone number *"
              type="tel"
              value={gatePhone}
              onChange={(e) => setGatePhone(e.target.value)}
              name="tel"
              autoComplete="tel"
              inputMode="tel"
              enterKeyHint="done"
              data-testid="input-widget-gate-phone"
            />
            <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
              By adding your number, you're okay with a quick follow-up call from the cabin team about your quote.
            </p>
          </div>
        )}

        <Button
          className={`w-full font-bold rounded-xl transition-shadow duration-150 ${ctaBold ? "text-lg py-7 shadow-lg hover:shadow-xl ring-2 ring-primary/40" : "text-base py-6 shadow-sm hover:shadow-md"}${ctaAttention ? " motion-safe:animate-attention-pulse" : ""}`}
          disabled={gateActive && (!gateEmailValid || gateSubmitting || !gatePhone.trim())}
          onClick={() => {
            if (noDates) { openCalendar("widget_cta"); }
            else if (!gateCaptured && !isNoGate) { handleCaptureEmail(); }
            else { setShowInquiry(true); }
          }}
          data-testid="button-reserve"
        >
          {nights > 0
            ? ((gateCaptured || isNoGate)
                ? (quoteLoading ? "Request to Book" : `Request to Book · $${total.toLocaleString()}`)
                : (gateSubmitting ? "Saving…" : "See your price"))
            : ((cta.config.label as string) || "Book Your Lake Escape")}
        </Button>

        {scarcity && noDates && (
          <div
            className="mt-3 flex items-center gap-3 rounded-xl border border-amber-200/70 dark:border-amber-400/20 bg-amber-50/70 dark:bg-amber-400/10 px-3.5 py-2.5"
            data-testid="text-weekend-scarcity"
          >
            <Gem className="h-6 w-6 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-foreground leading-snug">
                Rare find! This place is usually booked
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 leading-snug mt-0.5">
                Only {scarcity.count} {scarcity.month} weekend{scarcity.count !== 1 ? "s" : ""} still open
              </p>
            </div>
          </div>
        )}

        {datesUnavailable ? (
          <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 p-3.5" data-testid="notice-dates-unavailable">
            <p className="text-sm font-semibold text-foreground">Those dates aren't available</p>
            {altWindows.length > 0 ? (
              <>
                <p className="text-xs text-muted-foreground mt-0.5 mb-2.5">
                  Good news — these nearby dates are open:
                </p>
                <div className="space-y-2 mb-3">
                  {altWindows.map((a) => (
                    <button
                      key={`${a.checkIn}-${a.checkOut}`}
                      type="button"
                      className="w-full flex items-center justify-between rounded-lg border border-primary/30 bg-background px-3.5 py-2.5 text-sm font-semibold text-foreground hover:border-primary hover:bg-primary/5 transition-colors"
                      onClick={() => applyAltWindow(a)}
                      data-testid={`button-alt-dates-${a.checkIn}`}
                    >
                      <span>
                        {format(new Date(a.checkIn + "T12:00:00"), "MMM d")} – {format(new Date(a.checkOut + "T12:00:00"), "MMM d")}
                        <span className="ml-2 font-normal text-muted-foreground">
                          {a.nights} night{a.nights !== 1 ? "s" : ""}
                        </span>
                      </span>
                      <span className="text-primary" aria-hidden="true">→</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground mt-0.5 mb-3">
                Send us a note — we can sometimes open dates or suggest close alternatives.
              </p>
            )}
            <Button
              variant="outline"
              className="w-full font-semibold py-5 rounded-xl border-primary/40 text-primary hover:text-primary"
              onClick={() => openDateInquiry("prominent")}
              data-testid="button-date-inquiry"
            >
              Inquire about these dates
            </Button>
          </div>
        ) : (
          <button
            type="button"
            className="mt-3 w-full text-center text-sm text-primary font-medium py-2.5 rounded-lg hover:bg-muted/40 transition-colors"
            onClick={() => openDateInquiry("quiet")}
            data-testid="button-date-inquiry"
          >
            Dates unavailable? <span className="underline underline-offset-4">Inquire about these dates</span>
          </button>
        )}

        {showQuote && (
          <div className="mt-4 space-y-2 text-sm">
            {quoteLoading ? (
              <div className="py-3 text-center text-muted-foreground">Calculating live pricing…</div>
            ) : (
              <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground underline underline-offset-2">
                    ${avgNightly.toLocaleString()} × {nights} night{nights !== 1 ? "s" : ""}
                  </span>
                  <span>${subtotal.toLocaleString()}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-green-600" data-testid="text-widget-discount">
                    <span>Discount</span>
                    <span>-${discountAmount.toLocaleString()}</span>
                  </div>
                )}
                {cleaningFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground underline underline-offset-2">Cleaning fee</span>
                    <span>${cleaningFee.toLocaleString()}</span>
                  </div>
                )}
                {extraGuestFee > 0 && (
                  <div className="flex justify-between" data-testid="text-widget-extra-guest-fee">
                    <span className="text-muted-foreground underline underline-offset-2">Extra guest fee ({extraGuests} guest{extraGuests !== 1 ? "s" : ""} over 12)</span>
                    <span>${extraGuestFee.toLocaleString()}</span>
                  </div>
                )}
                <div>
                  <button
                    className="flex justify-between w-full text-left group"
                    onClick={() => setTaxExpanded(v => !v)}
                    type="button"
                  >
                    <span className="text-muted-foreground underline underline-offset-2 flex items-center gap-1">
                      Taxes (7.375%)
                      <ChevronDown className={`w-3 h-3 transition-transform duration-150 ${taxExpanded ? "rotate-180" : ""}`} />
                    </span>
                    <span>${taxAmount.toLocaleString()}</span>
                  </button>
                  {taxExpanded && taxBreakdown && (
                    <div className="mt-1.5 ml-2 space-y-1 text-xs text-muted-foreground border-l-2 border-muted pl-2">
                      <div className="flex justify-between">
                        <span>MN General Sales & Use Tax (6.875%)</span>
                        <span>${taxBreakdown.mnState.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Douglas County Transit Tax (0.5%)</span>
                        <span>${taxBreakdown.douglasCounty.toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground underline underline-offset-2">Card processing fee (3%)</span>
                  <span>${processingFee.toLocaleString()}</span>
                </div>
                <Separator className="my-3" />
                <div className="flex justify-between font-semibold text-base">
                  <span>Total</span>
                  <span>${total.toLocaleString()}</span>
                </div>
              </>
            )}
          </div>
        )}

        <p className="text-xs text-muted-foreground text-center mt-4 flex items-center justify-center gap-1">
          <Lock className="w-3 h-3" /> You won't be charged yet · {FIRM_CANCELLATION_POLICY}
        </p>
      </div>

      <Dialog open={showInquiry} onOpenChange={(open) => { setShowInquiry(open); if (!open) setCheckoutStep("info"); }}>
        <DialogContent className={`${checkoutStep === "payment" ? "max-w-5xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{checkoutStep === "payment" ? "Confirm and pay" : "Reserve your stay"}</DialogTitle>
            <p className="text-sm text-muted-foreground">{PROPERTY_NAME}</p>
          </DialogHeader>
          <BookingCheckout
            onStepChange={setCheckoutStep}
            dateRange={dateRange}
            guests={guests}
            adults={guestCounts.adults}
            children={guestCounts.children}
            infants={guestCounts.infants}
            dogs={guestCounts.dogs}
            onSuccess={() => setShowInquiry(false)}
            initialDiscountCode={mcpDiscountCode}
            initialName={gateName.trim()}
            initialEmail={gateEmail.trim().toLowerCase()}
            experimentVariant={directCheckout.variantKey ?? undefined}
          />
        </DialogContent>
      </Dialog>

      <DateInquiryDialog
        open={showDateInquiry}
        onOpenChange={setShowDateInquiry}
        dateRange={dateRange}
        guests={guests}
        context={inquiryContext}
        minNights={Math.max(widgetMinNights, pickerPlMinNights)}
        prefillName={mcpInquiryName}
        prefillEmail={mcpInquiryEmail}
        prefillMessage={mcpInquiryMessage}
      />
    </>
  );
}

function MobileBookingDrawer({ initialDates }: { initialDates?: DateRange }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const cta = useExperiment("booking_cta");
  const ctaBold = cta.variantKey === "bold";
  // direct_checkout experiment mirrors the widget vars. defer=true: expose at dates-selected.
  const directCheckout = useExperiment("direct_checkout", true);
  const isNoGate = directCheckout.variantKey === "no_gate" || directCheckout.variantKey === "price_calendar";
  const isPriceCalendar = directCheckout.variantKey === "price_calendar";
  // Phone is always required — gate_phone experiment ended.
  const [showForm, setShowForm] = useState(false);

  const [gateCaptured, setGateCaptured] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showDateInquiry, setShowDateInquiry] = useState(false);
  const [gateEmail, setGateEmail] = useState("");
  const [gateName, setGateName] = useState("");
  const [gatePhone, setGatePhone] = useState("");
  const [gateSubmitting, setGateSubmitting] = useState(false);
  // Why the guest opened the inquiry dialog — short-stay request vs.
  // unavailable dates — so the dialog copy speaks to the right situation.
  const [inquiryContext, setInquiryContext] = useState<"short_stay" | "unavailable" | "backup_interest">("unavailable");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(initialDates);
  const [guestCounts, setGuestCounts] = useState<GuestCounts>(DEFAULT_GUEST_COUNTS);
  const guests = guestTotal(guestCounts);
  const [guestPickerOpen, setGuestPickerOpen] = useState(false);
  const { data: drawerSettings = {} } = useQuery<Record<string, string>>({ queryKey: ["/api/settings"] });
  const drawerWeekdayRate = parseInt(drawerSettings.nightly_rate || String(NIGHTLY_RATE)) || NIGHTLY_RATE;
  const drawerWeekendRate = parseInt(drawerSettings.weekend_rate || String(NIGHTLY_RATE)) || NIGHTLY_RATE;
  const { nights, subtotal, cleaningFee: drawerCleaningFee, extraGuestFee: drawerExtraGuestFee, extraGuests: drawerExtraGuests, processingFee, discountAmount, taxAmount, taxBreakdown: drawerTaxBreakdown, total, avgNightly, quoteLoading } = useResolvedPricing(dateRange, drawerWeekdayRate, drawerWeekendRate, guests);
  // Deferred exposure: fire when dates are selected, not at drawer-open time.
  const hasDatesDrawer = nights > 0;
  useEffect(() => {
    if (hasDatesDrawer) fireExposure("direct_checkout");
  }, [hasDatesDrawer]);
  const drawerGateActiveForExposure = hasDatesDrawer && !isNoGate;
  useEffect(() => {
    if (drawerGateActiveForExposure) fireExposure("gate_phone");
  }, [drawerGateActiveForExposure]);
  const [drawerTaxExpanded, setDrawerTaxExpanded] = useState(false);
  // Effective per-night after the direct-booking discount (see BookingWidget).
  const discountedNightly = nights > 0 && discountAmount > 0 ? Math.round((subtotal - discountAmount) / nights) : avgNightly;
  const drawerMinNights = Math.max(1, parseInt(drawerSettings.min_nights || "1") || 1);
  // Same probe pattern as BookingWidget: track the picker's check-in so we
  // can surface the PL minimum before the guest has picked a checkout date.
  const [drawerPickerCheckIn, setDrawerPickerCheckIn] = useState<Date | undefined>(dateRange?.from);
  const drawerPickerCheckInIso = drawerPickerCheckIn ? format(drawerPickerCheckIn, "yyyy-MM-dd") : undefined;
  const { data: drawerProbeAvail } = useQuery({
    queryKey: ["/api/availability/check", drawerPickerCheckInIso, "pl-probe-drawer"],
    enabled: !!drawerPickerCheckInIso,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const co = drawerPickerCheckIn ? format(addDays(drawerPickerCheckIn, 1), "yyyy-MM-dd") : "";
      const res = await fetch(`/api/availability/check?checkIn=${drawerPickerCheckInIso}&checkOut=${co}`);
      if (!res.ok) return null;
      return res.json() as Promise<{ plMinNights?: number } | null>;
    },
  });
  const { data: drawerQuote } = useStayQuote(dateRange);
  const drawerPickerPlMinNights = drawerProbeAvail?.plMinNights ?? drawerQuote?.plMinNights ?? 0;
  // Variant C: per-date nightly prices for calendar cells.
  const { data: drawerCalendarPriceData } = useQuery<{ prices: Record<string, number> }>({
    queryKey: ["/api/pricing/dates"],
    queryFn: () => fetch("/api/pricing/dates?months=6").then((r) => r.json()),
    enabled: isPriceCalendar,
    staleTime: 10 * 60_000,
  });
  const drawerCalendarPrices = useMemo(
    () =>
      isPriceCalendar && drawerCalendarPriceData?.prices
        ? new Map(Object.entries(drawerCalendarPriceData.prices).map(([k, v]) => [k, Number(v)]))
        : undefined,
    [isPriceCalendar, drawerCalendarPriceData],
  );
  const { isDisabled: disabledDate, isTentativeVrboDate, tentativeVrboRangeFor } = useCalendarAvailability();
  const drawerContentRef = useRef<HTMLDivElement>(null);
  const { keyboardHeight, viewportHeight, isKeyboardOpen } = useVisualViewport();

  // Same quote_viewed / quote_scrolled tracking as the desktop widget, but
  // scoped to the mobile drawer. source="drawer" so you can split by surface.
  const drawerQuoteViewedRef = useRef(false);
  const drawerShowQuote = nights > 0 && (gateCaptured || isNoGate);
  useEffect(() => {
    if (!drawerShowQuote || drawerQuoteViewedRef.current) return;
    drawerQuoteViewedRef.current = true;
    trackEvent("quote_viewed", { source: "drawer" });
    const onScroll = () => trackEvent("quote_scrolled", { source: "drawer" });
    window.addEventListener("scroll", onScroll, { once: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [drawerShowQuote]);

  // When the keyboard opens, scroll the focused input into view inside the
  // drawer's scrollable area. Two rAFs: the first lets the DrawerContent
  // resize to `height: viewportHeight`, the second lets the layout reflow,
  // then scrollIntoView places the input comfortably above the keyboard.
  const prevKeyboardOpen = useRef(false);
  useEffect(() => {
    if (isKeyboardOpen && !prevKeyboardOpen.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const active = document.activeElement as HTMLElement | null;
          if (active && drawerContentRef.current?.contains(active)) {
            active.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
        });
      });
    }
    prevKeyboardOpen.current = isKeyboardOpen;
  }, [isKeyboardOpen]);

  const noDates = !dateRange?.from || !dateRange?.to;
  const datesUnavailable = rangeHasUnavailableDates(dateRange, disabledDate);
  const scarcity = useWeekendScarcity();

  // No dead ends (mobile drawer): same nearby-open-dates chips as the desktop
  // widget. Most guests book from a phone, so the drawer must not be an
  // inquiry-only dead end when the picked range is blocked.
  const drawerAltFromIso = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined;
  const drawerAltToIso = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined;
  const { data: drawerAltCheck } = useQuery({
    queryKey: ["/api/availability/check", drawerAltFromIso, drawerAltToIso, "alternatives"],
    enabled: !!drawerAltFromIso && !!drawerAltToIso && datesUnavailable,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await fetch(`/api/availability/check?checkIn=${drawerAltFromIso}&checkOut=${drawerAltToIso}`);
      if (!res.ok) return null;
      return res.json() as Promise<{
        available?: boolean;
        alternatives?: { checkIn: string; checkOut: string; nights: number }[];
      } | null>;
    },
  });
  const drawerAltWindows = datesUnavailable ? (drawerAltCheck?.alternatives ?? []).slice(0, 3) : [];
  const applyDrawerAltWindow = (a: { checkIn: string; checkOut: string }) => {
    const from = new Date(a.checkIn + "T12:00:00");
    const to = new Date(a.checkOut + "T12:00:00");
    if (isNaN(from.getTime()) || isNaN(to.getTime())) return;
    setDateRange({ from, to });
  };

  // 30-second attention nudge: pulse the sticky book button after the visitor
  // has idled on the page without engaging the booking flow. Once dismissed
  // (drawer opened, form shown, or dates picked) it never comes back.
  const [ctaAttention, setCtaAttention] = useState(false);
  const attentionDismissed = useRef(false);
  useEffect(() => {
    const t = setTimeout(() => {
      if (!attentionDismissed.current) setCtaAttention(true);
    }, 30_000);
    // Tasteful, not desperate: let the pulse run ~3 cycles then stop on its own.
    const stop = setTimeout(() => setCtaAttention(false), 35_400);
    return () => { clearTimeout(t); clearTimeout(stop); };
  }, []);
  useEffect(() => {
    if (open || showForm || showPicker || (dateRange?.from && dateRange?.to)) {
      attentionDismissed.current = true;
      setCtaAttention(false);
    }
  }, [open, showForm, showPicker, dateRange]);

  // Same two-state contextual inquiry entry point as the desktop widget,
  // preserving the close-drawer-then-open-dialog handoff. The calendar's
  // min-stay notice opens the dialog in "short_stay" framing instead.
  const openDateInquiry = (state: "prominent" | "quiet") => {
    trackEvent("date_inquiry_opened", { source: "mobile_drawer", state, trigger: "unavailable" });
    setInquiryContext("unavailable");
    setOpen(false);
    setTimeout(() => setShowDateInquiry(true), 350);
  };

  // Most social traffic lands on mobile. Open the drawer straight into the
  // calendar when no dates are picked yet — one tap to the date picker instead
  // of two — to lift the land → dates_selected rate.
  const openSticky = () => {
    setOpen(true);
    if (noDates) {
      trackEvent("date_picker_opened", { source: "sticky_cta" });
      setShowPicker(true);
    }
  };

  useEffect(() => {
    // The saved-checkout dialog owns this return. Opening the date drawer
    // over it would hide payment on mobile after the #booking timeout.
    const resumingCheckout = new URLSearchParams(window.location.search).has("checkoutResume");
    if (initialDates && !resumingCheckout) {
      const t = setTimeout(() => setOpen(true), 500);
      return () => clearTimeout(t);
    }
    // Email/popup funnel lands on /#booking. On small viewports the desktop
    // anchor target is hidden, so open the drawer straight into the date
    // picker — same one-tap handoff the sticky CTA gives. On desktop, scroll
    // to the booking widget once it has rendered (the browser's native
    // anchor jump fires before React paints the section).
    const openFromBookingHash = () => {
      if (new URLSearchParams(window.location.search).has("checkoutResume")) return;
      if (window.location.hash !== "#booking") return;
      if (window.matchMedia("(max-width: 1023px)").matches) {
        setOpen(true);
        trackEvent("date_picker_opened", { source: "booking_hash" });
        setShowPicker(true);
      } else {
        document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
      }
    };
    let t: ReturnType<typeof setTimeout> | undefined;
    if (window.location.hash === "#booking" && !resumingCheckout) {
      t = setTimeout(openFromBookingHash, 500);
    }
    // Also handle same-document navigations to /#booking (e.g. the footer
    // "Book Now" link clicked while already on the home page) — the mount
    // check above only covers fresh page loads.
    window.addEventListener("hashchange", openFromBookingHash);
    return () => {
      if (t) clearTimeout(t);
      window.removeEventListener("hashchange", openFromBookingHash);
    };
  }, []);

  useEffect(() => {
    // In-page section anchors (#amenities, #reviews, #location) can be reached
    // directly: the api-server 301s /amenities -> /#amenities (etc.) for
    // bookmarks/shared links/crawlers. On a fresh SPA load the browser's native
    // anchor jump fires before React paints these sections, so scroll manually
    // once they exist. #booking has its own handler above; skip it here.
    const scrollToSectionHash = () => {
      const id = window.location.hash.slice(1);
      if (!id || id === "booking") return;
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    };
    let t: ReturnType<typeof setTimeout> | undefined;
    const id = window.location.hash.slice(1);
    if (id && id !== "booking") t = setTimeout(scrollToSectionHash, 500);
    window.addEventListener("hashchange", scrollToSectionHash);
    return () => {
      if (t) clearTimeout(t);
      window.removeEventListener("hashchange", scrollToSectionHash);
    };
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const { checkIn, checkOut } = ((e as CustomEvent).detail ?? {}) as {
        checkIn?: string;
        checkOut?: string;
      };
      if (checkIn && checkOut) {
        const from = new Date(checkIn + "T12:00:00");
        const to = new Date(checkOut + "T12:00:00");
        if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
          setDateRange({ from, to });
          setOpen(true);
        }
        return;
      }
      // No dates supplied (e.g. savings popup "Book now" while already on
      // home, mobile viewport): open the drawer straight into the date
      // picker — same one-tap handoff as the sticky CTA and #booking hash.
      setOpen(true);
      trackEvent("date_picker_opened", { source: "open_booking_event" });
      setShowPicker(true);
    };
    window.addEventListener("llc:open-booking", handler);
    return () => window.removeEventListener("llc:open-booking", handler);
  }, []);

  const handleCaptureEmail = async () => {
    const emailTrim = gateEmail.trim().toLowerCase();
    if (!emailTrim || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) return;
    const normalizedPhone = normalizeQuotePhone(gatePhone);
    if (!normalizedPhone) {
      toast({ title: "Real phone number required", description: "Please enter your real 10-digit phone number to see your price.", variant: "destructive" });
      return;
    }
    setGateSubmitting(true);
    try {
      try {
        const verRes = await fetch("/api/verify-email", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: emailTrim }) });
        if (verRes.ok) {
          const ver = await verRes.json();
          if (!ver.valid) {
            toast({ title: "Invalid email address", description: ver.reason ?? "Please use a real email to see your price.", variant: "destructive" });
            setGateSubmitting(false);
            return;
          }
        }
      } catch { /* network error — fail open */ }
      let phoneForCapture = normalizedPhone;
      try {
        const verPhone = await fetch("/api/verify-phone", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone: phoneForCapture }) });
        if (verPhone.ok) {
          const verP = await verPhone.json();
          if (!verP.valid) {
            toast({ title: "Invalid phone number", description: verP.reason ?? "That doesn't look like a real phone number — please double-check it to see your price.", variant: "destructive" });
            setGateSubmitting(false);
            return;
          }
          if (verP.e164) phoneForCapture = verP.e164;
        }
      } catch { /* network error — fail open (phone) */ }
      const visitorId = getVisitorId();
      const sessionId = getSessionId();
      await fetch("/api/leads/capture", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: emailTrim,
          name: gateName.trim() || null,
          phone: phoneForCapture || null,
          // Typing a number under the visible consent note = okay to call.
          aiCallOptIn: !!phoneForCapture,
          visitorId,
          sessionId,
          source: "mobile_drawer_gate",
        }),
      });
      if (window.geq && window.geq.identify) {
        try { window.geq.identify(emailTrim); } catch (e) {}
      }
      trackEvent("email_captured", { source: "mobile_drawer_gate", email: emailTrim, visitorId });
      // Fire quote_generated after gate so only identified visitors count as leads.
      const ci = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined;
      const co = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined;
      if (ci && co) trackEvent("quote_generated", { source: "mobile_drawer_gate", checkIn: ci, checkOut: co });
    } catch {
      /* silently continue */
    } finally {
      setGateSubmitting(false);
    }
    // Keep both desktop and mobile price gates closed on a validation return.
    setGateCaptured(true);
  };

  const handleContinueToForm = () => {
    setOpen(false);
    setTimeout(() => setShowForm(true), 350);
  };

  const handleBackToDates = () => {
    setShowForm(false);
    setTimeout(() => setOpen(true), 50);
  };

  const handleClose = () => {
    setOpen(false);
    setShowForm(false);
  };

  const gateEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(gateEmail.trim());

  return (
    <>
      {!showForm && (
        <div
          className="fixed left-0 right-0 lg:hidden bg-background border-t border-border z-40 transition-[bottom] duration-200"
          style={{ bottom: isKeyboardOpen ? `${keyboardHeight}px` : 0 }}
        >
          <div className="flex items-center justify-between px-4 py-3 gap-4">
            <button
              className="min-w-0 text-left"
              onClick={openSticky}
              data-testid="button-sticky-info"
              aria-label="Select your dates"
            >
              <div className="flex items-baseline gap-1.5">
                {nights === 0 ? (
                  <span className="font-serif font-bold text-base leading-snug">Enter dates to see your price</span>
                ) : (
                  <span className="font-serif font-bold text-xl">Complete your reservation</span>
                )}
              </div>
              {nights > 0 ? (
                <p className="text-xs text-primary font-medium">
                  {dateRange?.from && format(dateRange.from, "MMM d")} – {dateRange?.to && format(dateRange.to, "MMM d")} · {guests} guest{guests !== 1 ? "s" : ""}
                </p>
              ) : scarcity ? (
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-400" data-testid="text-weekend-scarcity-mobile">
                  Only {scarcity.count} {scarcity.month} weekend{scarcity.count !== 1 ? "s" : ""} left →
                </p>
              ) : (
                <p className="text-xs text-primary font-medium">
                  Tap to pick your dates →
                </p>
              )}
            </button>
            <Button
              className={`font-semibold shrink-0 ${ctaBold ? "px-7 py-6 text-base ring-2 ring-primary/40 shadow-lg" : "px-6 py-5"}${ctaAttention ? " motion-safe:animate-attention-pulse" : ""}`}
              onClick={openSticky}
              data-testid="button-reserve-mobile"
            >
              {nights > 0 ? "Request to Book" : ((cta.config.labelMobile as string) || "Book Your Lake Escape")}
            </Button>
          </div>
        </div>
      )}

      <Drawer open={open} onOpenChange={(v) => { if (!v) { handleClose(); setShowPicker(false); } }} dismissible={!showPicker} shouldScaleBackground={false} repositionInputs={false}>
        <DrawerContent
          className="flex flex-col overflow-hidden"
          style={
            showPicker
              ? { height: '100dvh' }
              : isKeyboardOpen
                ? { height: `${viewportHeight}px`, bottom: `${keyboardHeight}px` }
                : { maxHeight: '92dvh' }
          }
        >
          {showPicker ? (
            <AirbnbDatePicker
              value={dateRange}
              onSave={(range) => { setDateRange(range); setShowPicker(false); }}
              onClose={() => setShowPicker(false)}
              isDisabled={disabledDate}
              monthsToShow={18}
              minNights={drawerMinNights}
              plMinNights={drawerPickerPlMinNights}
              midweekException
              onFromChange={(d) => setDrawerPickerCheckIn(d)}
              datePrices={drawerCalendarPrices}
              onInquire={(range) => {
                trackEvent("date_inquiry_opened", { source: "mobile_drawer", state: "prominent", trigger: "short_stay" });
                setDateRange(range);
                setShowPicker(false);
                setOpen(false);
                setInquiryContext("short_stay");
                setShowDateInquiry(true);
              }}
              isBackupInterestDate={isTentativeVrboDate}
              onBackupInterest={(date) => {
                const range = tentativeVrboRangeFor(date);
                trackEvent("date_inquiry_opened", { source: "mobile_drawer", state: "prominent", trigger: "backup_interest" });
                setDateRange(range);
                setShowPicker(false);
                setOpen(false);
                setInquiryContext("backup_interest");
                setShowDateInquiry(true);
              }}
            />
          ) : (
          <>
          <DrawerHeader className="pb-2 border-b border-border">
            <div className="flex items-center justify-between">
              <DrawerTitle className="font-serif text-lg">
                Book your stay
              </DrawerTitle>
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs font-medium text-muted-foreground" data-testid="text-step-indicator">
              Step 1 of 2
            </p>
            <p className="text-sm font-medium text-foreground mt-0.5">{PROPERTY_NAME}</p>
            <div className="flex items-center gap-2 mt-0.5">
              {nights === 0 ? (
                <span className="font-serif font-bold text-[15px] leading-snug">Enter dates to see your price</span>
              ) : (
                <span className="font-serif font-bold text-lg">Complete your reservation</span>
              )}
              <div className="ml-auto flex items-center gap-1 text-sm">
                <Star className="w-3.5 h-3.5 fill-primary text-primary" />
                <span className="font-medium">{REVIEW_AVG} · {REVIEW_COUNT} reviews</span>
              </div>
            </div>
          </DrawerHeader>

          <div ref={drawerContentRef} className="overflow-y-auto flex-1 min-h-0 px-4 pb-6 pt-4" style={{ overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" } as React.CSSProperties}>
            <div className="space-y-4">
              <button
                className="w-full border border-border rounded-lg p-4 text-left hover:bg-muted/50 transition-colors"
                onClick={() => setShowPicker(true)}
                data-testid="button-select-dates-mobile"
              >
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">
                      Check-in
                    </p>
                    <p className={`text-sm font-medium ${dateRange?.from ? "text-foreground" : "text-muted-foreground"}`}>
                      {dateRange?.from ? format(dateRange.from, "MMM d, yyyy") : "Add date"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">
                      Check-out
                    </p>
                    <p className={`text-sm font-medium ${dateRange?.to ? "text-foreground" : "text-muted-foreground"}`}>
                      {dateRange?.to ? format(dateRange.to, "MMM d, yyyy") : "Add date"}
                    </p>
                  </div>
                </div>
                {!dateRange?.from && (
                  <p className="text-xs text-primary font-medium mt-2">Tap to select your dates →</p>
                )}
              </button>

              {scarcity && noDates && (
                <div
                  className="flex items-center gap-3 rounded-xl border border-amber-200/70 dark:border-amber-400/20 bg-amber-50/70 dark:bg-amber-400/10 px-3.5 py-2.5"
                  data-testid="text-weekend-scarcity-drawer"
                >
                  <Gem className="h-6 w-6 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-foreground leading-snug">
                      Rare find! This place is usually booked
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-400 leading-snug mt-0.5">
                      Only {scarcity.count} {scarcity.month} weekend{scarcity.count !== 1 ? "s" : ""} still open
                    </p>
                  </div>
                </div>
              )}

              <div className="border border-border rounded-lg">
                <button
                  type="button"
                  onClick={() => setGuestPickerOpen((o) => !o)}
                  className="w-full flex items-center justify-between p-4 text-left"
                  aria-expanded={guestPickerOpen}
                  aria-label="Select guests"
                  data-testid="button-guests-mobile"
                >
                  <span className="font-semibold text-sm text-foreground">Guests</span>
                  <span className="flex items-center gap-1.5">
                    <span className="font-bold text-base text-foreground" data-testid="text-guests-summary-mobile">
                      {guestSummaryText(guestCounts)}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${guestPickerOpen ? "rotate-180" : ""}`} />
                  </span>
                </button>
                {guestPickerOpen && (
                  <div className="px-4 pb-4 border-t border-border pt-1" data-testid="panel-guests-mobile">
                    <GuestPickerPanel counts={guestCounts} onChange={setGuestCounts} />
                  </div>
                )}
              </div>

              {nights > 0 && !gateCaptured && !isNoGate && (
                <div className="space-y-3" data-testid="panel-inline-email-gate">
                  <p className="text-sm text-muted-foreground">
                    Enter your name and email to reveal live pricing for your dates.
                  </p>
                  <input
                    className="w-full rounded-lg border border-border px-3 py-3 text-base bg-background"
                    placeholder="Your name"
                    value={gateName}
                    onChange={(e) => setGateName(e.target.value)}
                    type="text"
                    name="name"
                    autoComplete="name"
                    autoCapitalize="words"
                    enterKeyHint="next"
                    data-testid="input-gate-name"
                  />
                  <input
                    className="w-full rounded-lg border border-border px-3 py-3 text-base bg-background"
                    placeholder="Email address"
                    type="email"
                    value={gateEmail}
                    onChange={(e) => setGateEmail(e.target.value)}
                    name="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    inputMode="email"
                    enterKeyHint="next"
                    data-testid="input-gate-email"
                  />
                  <input
                    className="w-full rounded-lg border border-border px-3 py-3 text-base bg-background"
                    placeholder="Phone number *"
                    type="tel"
                    value={gatePhone}
                    onChange={(e) => setGatePhone(e.target.value)}
                    name="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    enterKeyHint="done"
                    data-testid="input-gate-phone"
                  />
                  <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                    By adding your number, you're okay with a quick follow-up call from the cabin team about your quote.
                  </p>
                </div>
              )}

              {nights > 0 && gateCaptured && (
                <div className="bg-accent/30 rounded-lg p-4 space-y-2 text-sm border border-accent" data-testid="panel-inline-quote">
                  {quoteLoading ? (
                    <div className="py-3 text-center text-muted-foreground">Calculating live pricing…</div>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">${avgNightly.toLocaleString()} × {nights} night{nights !== 1 ? "s" : ""}</span>
                        <span>${subtotal.toLocaleString()}</span>
                      </div>
                      {discountAmount > 0 && (
                        <div className="flex justify-between text-green-600" data-testid="text-inline-discount">
                          <span>Discount</span>
                          <span>-${discountAmount.toLocaleString()}</span>
                        </div>
                      )}
                      {drawerCleaningFee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Cleaning fee</span>
                          <span>${drawerCleaningFee.toLocaleString()}</span>
                        </div>
                      )}
                      {drawerExtraGuestFee > 0 && (
                        <div className="flex justify-between" data-testid="text-inline-extra-guest-fee">
                          <span className="text-muted-foreground">Extra guest fee ({drawerExtraGuests} guest{drawerExtraGuests !== 1 ? "s" : ""} over 12)</span>
                          <span>${drawerExtraGuestFee.toLocaleString()}</span>
                        </div>
                      )}
                      <div>
                        <button
                          className="flex justify-between w-full text-left"
                          onClick={() => setDrawerTaxExpanded(v => !v)}
                          type="button"
                        >
                          <span className="text-muted-foreground underline underline-offset-2 flex items-center gap-1">
                            Taxes (7.375%)
                            <ChevronDown className={`w-3 h-3 transition-transform duration-150 ${drawerTaxExpanded ? "rotate-180" : ""}`} />
                          </span>
                          <span>${taxAmount.toLocaleString()}</span>
                        </button>
                        {drawerTaxExpanded && drawerTaxBreakdown && (
                          <div className="mt-1.5 ml-2 space-y-1 text-xs text-muted-foreground border-l-2 border-muted pl-2">
                            <div className="flex justify-between">
                              <span>MN General Sales & Use Tax (6.875%)</span>
                              <span>${drawerTaxBreakdown.mnState.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Douglas County Transit Tax (0.5%)</span>
                              <span>${drawerTaxBreakdown.douglasCounty.toLocaleString()}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Card processing fee (3%)</span>
                        <span>${processingFee.toLocaleString()}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between font-semibold">
                        <span>Total</span>
                        <span>${total.toLocaleString()}</span>
                      </div>
                    </>
                  )}
                </div>
              )}

              <Button
                className="w-full font-semibold py-6 text-base"
                disabled={nights > 0 && !gateCaptured && !isNoGate && (!gateEmailValid || gateSubmitting || !gatePhone.trim())}
                onClick={nights === 0 ? () => setShowPicker(true) : (!gateCaptured && !isNoGate) ? handleCaptureEmail : handleContinueToForm}
                data-testid="button-continue-to-booking"
              >
                {nights === 0
                  ? "Continue"
                  : (gateCaptured || isNoGate)
                    ? quoteLoading
                      ? "Request to Book"
                      : `Request to Book · $${total.toLocaleString()}`
                    : gateSubmitting
                      ? "Saving…"
                      : "See your price"}
              </Button>
              {datesUnavailable ? (
                <div className="rounded-xl border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 p-3.5" data-testid="notice-dates-unavailable-mobile">
                  <p className="text-sm font-semibold text-foreground">Those dates aren't available</p>
                  {drawerAltWindows.length > 0 ? (
                    <>
                      <p className="text-xs text-muted-foreground mt-0.5 mb-2.5">
                        Good news — these nearby dates are open:
                      </p>
                      <div className="space-y-2 mb-3">
                        {drawerAltWindows.map((a) => (
                          <button
                            key={`${a.checkIn}-${a.checkOut}`}
                            type="button"
                            className="w-full flex items-center justify-between rounded-lg border border-primary/30 bg-background px-3.5 py-2.5 text-sm font-semibold text-foreground hover:border-primary hover:bg-primary/5 transition-colors"
                            onClick={() => applyDrawerAltWindow(a)}
                            data-testid={`button-alt-dates-mobile-${a.checkIn}`}
                          >
                            <span>
                              {format(new Date(a.checkIn + "T12:00:00"), "MMM d")} – {format(new Date(a.checkOut + "T12:00:00"), "MMM d")}
                              <span className="ml-2 font-normal text-muted-foreground">
                                {a.nights} night{a.nights !== 1 ? "s" : ""}
                              </span>
                            </span>
                            <span className="text-primary" aria-hidden="true">→</span>
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-0.5 mb-3">
                      Send us a note — we can sometimes open dates or suggest close alternatives.
                    </p>
                  )}
                  <Button
                    variant="outline"
                    className="w-full font-semibold py-5 border-primary/40 text-primary hover:text-primary"
                    onClick={() => openDateInquiry("prominent")}
                    data-testid="button-date-inquiry-mobile"
                  >
                    Inquire about these dates
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="w-full text-center text-sm text-primary font-medium py-2.5 rounded-lg hover:bg-muted/40 transition-colors"
                  onClick={() => openDateInquiry("quiet")}
                  data-testid="button-date-inquiry-mobile"
                >
                  Dates unavailable? <span className="underline underline-offset-4">Inquire about these dates</span>
                </button>
              )}
              <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1">
                <Lock className="w-3 h-3" /> You won't be charged yet · {FIRM_CANCELLATION_POLICY}
              </p>
            </div>
          </div>
          </>
          )}
        </DrawerContent>
      </Drawer>

      <DateInquiryDialog
        open={showDateInquiry}
        onOpenChange={setShowDateInquiry}
        dateRange={dateRange}
        guests={guests}
        context={inquiryContext}
        minNights={Math.max(drawerMinNights, drawerPickerPlMinNights)}
      />

      {showForm && (
        <div
          className="fixed inset-0 z-[60] bg-background flex flex-col"
          style={{ WebkitOverflowScrolling: "touch" } as React.CSSProperties}
        >
          <div className="shrink-0 bg-background border-b border-border px-4 sm:px-6 py-3">
            <div className="max-w-5xl mx-auto flex items-center justify-between">
              <button
                onClick={handleBackToDates}
                className="flex items-center gap-1.5 text-sm text-primary font-medium"
                data-testid="button-back-to-dates"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to dates
              </button>
              <button
                onClick={handleClose}
                className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-muted"
                data-testid="button-close-form"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-10 pt-4 sm:pt-6" style={{ overscrollBehavior: "contain" }}>
            <div className="max-w-5xl mx-auto w-full">
              <h2 className="font-serif text-xl sm:text-2xl font-semibold mb-4 sm:mb-6" data-testid="text-step-indicator-form">Confirm and pay</h2>
              <BookingCheckout
                dateRange={dateRange}
                guests={guests}
                adults={guestCounts.adults}
                children={guestCounts.children}
                infants={guestCounts.infants}
                dogs={guestCounts.dogs}
                onSuccess={handleClose}
                initialName={gateName}
                initialEmail={gateEmail}
                experimentVariant={directCheckout.variantKey ?? undefined}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Blog preview strip ────────────────────────────────────────────────────
function BlogPreview() {
  const { data: posts = [] } = useQuery<
    Array<{ slug: string; title: string; excerpt: string | null }>
  >({
    queryKey: ["/api/blog/posts"],
    queryFn: () => fetch("/api/blog/posts?limit=4").then((r) => r.json()),
    staleTime: 10 * 60 * 1000,
  });

  if (posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
      {posts.map((post) => (
        <a
          key={post.slug}
          href={`/blog/${post.slug}`}
          className="group block border border-border rounded-2xl p-5 bg-background hover:border-primary/40 hover:shadow-sm transition-all"
        >
          <h3 className="font-serif font-bold text-foreground text-base leading-snug group-hover:text-primary transition-colors mb-2 line-clamp-2">
            {post.title}
          </h3>
          {post.excerpt && (
            <p className="text-sm text-muted-foreground line-clamp-3">{post.excerpt}</p>
          )}
          <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
            Read guide <ChevronRight className="w-3 h-3" />
          </span>
        </a>
      ))}
    </div>
  );
}

function ResumedCheckout() {
  const [state, setState] = useState<{ id: string; draft: CheckoutDraft | null; error: string; loading: boolean } | null>(() => {
    const id = new URLSearchParams(window.location.search).get("checkoutResume");
    if (!id) return null;
    return { id, draft: null, error: "", loading: true };
  });
  useEffect(() => {
    if (!state) return;
    let active = true;
    void loadCheckout(state.id).then(
      draft => { if (active) setState({ id: state.id, draft, error: "", loading: false }); },
      err => { if (active) setState({ id: state.id, draft: null, error: err.message, loading: false }); },
    );
    return () => { active = false; };
  }, []);
  const [open, setOpen] = useState(Boolean(state));
  const [step, setStep] = useState("info");
  const close = () => {
    setOpen(false);
    const url = new URL(window.location.href);
    url.searchParams.delete("checkoutResume");
    window.history.replaceState(null, "", url.pathname + url.search + "#booking");
  };
  if (!state) return null;
  const draft = state.draft;
  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) close(); }}>
      <DialogContent className={`${step === "payment" ? "max-w-5xl" : "max-w-xl"} max-h-[90dvh] overflow-y-auto`} data-testid="resumed-checkout">
        <DialogHeader>
          <DialogTitle>{state.error ? "Checkout needs attention" : "Continue your reservation"}</DialogTitle>
          <DialogDescription>{state.error || "Your dates and guest details have been restored. Signing alone does not charge you."}</DialogDescription>
        </DialogHeader>
        {state.loading ? <p role="status">Restoring your checkout…</p> : draft ? (
          <BookingCheckout
            resumeDraft={draft}
            dateRange={{ from: parseISO(draft.checkIn), to: parseISO(draft.checkOut) }}
            guests={draft.adults + draft.children}
            adults={draft.adults} children={draft.children} infants={draft.infants} dogs={draft.dogs}
            experimentVariant={draft.experimentVariant}
            onStepChange={setStep}
            onSuccess={close}
          />
        ) : <Button onClick={() => window.location.assign("/#booking")}>Return to date selection</Button>}
      </DialogContent>
    </Dialog>
  );
}

export default function Home() {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const lastGalleryIndex = useRef(0);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const registerSection = useAmenitySectionViews();
  const registerReview = useReviewReadTracking();
  const initialBookingDates = useInitialBookingDates();
  const { data: leadPricing } = useQuery<{ fromPrice: number; avgPrice: number; maxPrice?: number }>({ queryKey: ["/api/pricing/lead"] });
  const priceRangeStr = leadPricing
    ? `$${leadPricing.fromPrice.toLocaleString()}–$${(leadPricing.maxPrice ?? Math.max(leadPricing.avgPrice, leadPricing.fromPrice)).toLocaleString()}/night`
    : "$664–$1,500/night";

  const openGallery = (idx: number) => {
    // Hero strip is a curated subset — map the clicked photo to its position
    // in the full 60-photo listing-order gallery.
    const fullIdx = Math.max(
      0,
      ALL_PHOTOS.findIndex((p) => p.src === PHOTOS[idx]?.src),
    );
    // A guest clicking straight into a pontoon/sauna/pool-table photo is an
    // amenity engagement, not just a browse — surface it as its own beat.
    const subject = subjectFromText(ALL_PHOTOS[fullIdx]?.alt);
    if (subject) trackAmenityEngaged(subject, { via: "gallery", index: fullIdx });
    lastGalleryIndex.current = fullIdx;
    setGalleryIndex(fullIdx);
    setGalleryOpen(true);
  };

  const openGalleryAtLastViewed = () => {
    setGalleryIndex(lastGalleryIndex.current);
    setGalleryOpen(true);
  };

  const SHORT_DESC = `Escape to the #1 sandy beach on Lake Miltona! Kid-friendly, gradual shoreline with hard-bottom, weed-free swimming and 5,731 acres of clear water to explore. Sleeps 12 across 4 bedrooms — including a large suite with lake views, plus a pull-out sleeper in the main-floor living room. Stocked kitchen, large living/dining area, sauna, pool table, bar & EV charger. Private dock, decks, patio and campfire. Blackstone Grill, Gas Grill. Onsite 25-foot pontoon rental!`;

  const FULL_DESC = `Enjoy full, private access to this spacious lakefront log home designed for comfort, connection and entertaining. The home features 4 bedrooms, with recent upgrades including a lakeside walkout Sandbar Suite, EV charger, and outdoor shower.

Inside, you'll find an open-concept living and dining area with panoramic lake views, a fully equipped kitchen, and multiple gathering spaces for your group to spread out and relax. The lower level offers a pool table, bar, sauna, mini-fridge and coffee station — perfect evenings in after a full day on the water.

Bedroom 4 includes flexible futon-style sleeping — great as a kids' hangout or extra sleeping space — and the main-floor living room adds a pull-out sleeper — ideal for larger families and multi-generational groups.

Outdoor living is just as impressive. Enjoy meals on the lower lakeside patio with a grill and outdoor dining area, or head to the deck on the main floor featuring additional dining seating and Blackstone griddle for cooking breakfast, lunches, and dinners with a view.

The property also includes a private sandy beach, dock and campfire area, with easy shoreline visibility to the swimming area for added peace of mind with children.

The home is ideal for families, multi-generational groups and responsible adult gatherings seeking a high-end lakefront experience.`;


  const photoOrigin = CANONICAL_ORIGIN;
  const propertyImages = [
    `${photoOrigin}/photos/photo-78-ed3ba9ee-cb47-4f67-8740-2a0554bb7f33.webp`,
    `${photoOrigin}/photos/photo-76-e66f6bc5-f5c8-48e6-93fa-dab1513e6a69.webp`,
    `${photoOrigin}/photos/photo-59-dc75e7b2-e307-4798-84a6-982e67301239.webp`,
    `${photoOrigin}/photos/photo-69-bd4e4283-7b76-4356-83f0-e4f508690d4a.webp`,
    `${photoOrigin}/photos/photo-72-d0c4fbfa-1933-43b9-bbf6-85c17a7931cb.webp`,
    `${photoOrigin}/photos/photo-64-51b9aeed-e39d-4d4e-ae96-e1cffcdba966.webp`,
    `${photoOrigin}/photos/photo-17-4d6aa0b9-f190-4c61-9a73-4f46df72260f.webp`,
    `${photoOrigin}/photos/photo-22-6888d0e1-02dc-4de8-9e23-2a9ebdd230f3.webp`,
    `${photoOrigin}/photos/photo-14-4325ed18-9a76-4be0-9229-a7e82468fc82.webp`,
    `${photoOrigin}/photos/photo-34-c45cbb39-c609-4b29-9ac0-b8420ce3b7e8.webp`,
    `${photoOrigin}/photos/photo-24-86e42cdb-ac00-4a8a-92d5-869ef9649c49.webp`,
    `${photoOrigin}/photos/photo-55-a1074a77-3433-46a1-bbf0-b7bc8156981c.webp`,
    `${photoOrigin}/photos/photo-60-e67b9ba8-9ca5-48f8-895e-a01549b2433a.webp`,
    `${photoOrigin}/photos/photo-61-1d4b97cf-dfae-43fc-bac0-210d29ac745c.webp`,
  ];

  const sharedAddress = {
    "@type": "PostalAddress",
    "streetAddress": "240 W Sandy Beach Drive NW",
    "addressLocality": "Miltona",
    "addressRegion": "MN",
    "postalCode": "56354",
    "addressCountry": "US"
  };
  const sharedGeo = { "@type": "GeoCoordinates", "latitude": 46.05571, "longitude": -95.37823 };

  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Legendary Log Cabin on Lake Miltona",
    "url": `${photoOrigin}/`,
    "description": "Direct booking website for the Legendary Log Cabin — luxury lakefront cabin rental on Lake Miltona near Alexandria, Minnesota.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${photoOrigin}/?dates={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  const vacationRentalJsonLd = {
    "@context": "https://schema.org",
    "@type": "VacationRental",
    "@id": `${photoOrigin}/#vacation-rental`,
    "name": "Legendary Log Cabin on Lake Miltona",
    "description": "Luxury lakefront log cabin rental on Lake Miltona near Alexandria, Minnesota. Private sandy beach, onsite pontoon boat rental, sauna, game room. Perfect for families, reunions, and group getaways.",
    "url": `${photoOrigin}/`,
    "image": propertyImages,
    "identifier": "937046115859272290",
    // Google Vacation Rentals requires latitude/longitude at the top level of
    // the VacationRental entity (nested geo alone fails the GVR check).
    "latitude": sharedGeo.latitude,
    "longitude": sharedGeo.longitude,
    "address": sharedAddress,
    "geo": sharedGeo,
    "amenityFeature": [
      { "@type": "LocationFeatureSpecification", "name": "Private Sandy Beach", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Onsite Pontoon Boat Rental", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Private Dock", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Private Sauna", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Pool Table & Game Room", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "High-Speed WiFi", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Fully Equipped Kitchen", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "EV Charger", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Free Parking", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Campfire Area", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Kayaks & Paddleboard", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Air Conditioning", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Washer & Dryer", "value": true }
    ],
    "checkinTime": "16:00",
    "checkoutTime": "11:00",
    "numberOfRooms": 4,
    "numberOfBedrooms": 4,
    "occupancy": { "@type": "QuantitativeValue", "maxValue": 12 },
    "floorSize": { "@type": "QuantitativeValue", "value": 3200, "unitCode": "FTK" },
    "containsPlace": {
      "@type": "Accommodation",
      "additionalType": "EntirePlace",
      "name": "Entire log cabin",
      "occupancy": { "@type": "QuantitativeValue", "value": 12 },
      "numberOfBedrooms": 4,
      "numberOfBathroomsTotal": 2,
      "bed": [
        { "@type": "BedDetails", "numberOfBeds": 5, "typeOfBed": "Queen" },
        { "@type": "BedDetails", "numberOfBeds": 2, "typeOfBed": "Single" },
        { "@type": "BedDetails", "numberOfBeds": 4, "typeOfBed": "Sofa bed" }
      ],
      "description": "Master bedroom: queen plus single. Sandbar Suite: three queens. Bedroom 3: bunk queen plus single. Loft: three sofa beds. Living room: one pull-out. 11 beds total; sleeps 12.",
      "floorSize": { "@type": "QuantitativeValue", "value": 3200, "unitCode": "FTK" },
      "amenityFeature": [
        { "@type": "LocationFeatureSpecification", "name": "Private Sandy Beach", "value": true },
        { "@type": "LocationFeatureSpecification", "name": "Private Sauna", "value": true },
        { "@type": "LocationFeatureSpecification", "name": "Fully Equipped Kitchen", "value": true },
        { "@type": "LocationFeatureSpecification", "name": "Air Conditioning", "value": true },
        { "@type": "LocationFeatureSpecification", "name": "Washer & Dryer", "value": true }
      ]
    },
    "containedInPlace": {
      "@type": "Lake",
      "name": "Lake Miltona",
      "geo": sharedGeo
    }
  };

  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "name": "Legendary Log Cabin on Lake Miltona",
    "url": `${photoOrigin}/`,
    "image": propertyImages[0],
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Miltona",
      "addressRegion": "MN",
      "addressCountry": "US"
    },
    "geo": sharedGeo,
    "telephone": CONCIERGE_PHONE_E164,
    "priceRange": priceRangeStr,
    "currenciesAccepted": "USD",
    "paymentAccepted": "Credit Card"
  };

  const touristAttractionJsonLd = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Legendary Log Cabin on Lake Miltona",
    "description": "Luxury lakefront log cabin on Lake Miltona, Minnesota — private sandy beach, 25-ft pontoon boat, cedar sauna, stone fireplace, and game room. Sleeps 12. 2–2.5 hours from Minneapolis Twin Cities via I-94 West. Serving guests from Wayzata, Edina, Minnetonka, Eden Prairie, Maple Grove, Plymouth, and Woodbury.",
    "url": `${photoOrigin}/`,
    "image": propertyImages[0],
    "geo": sharedGeo,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Miltona",
      "addressRegion": "MN",
      "postalCode": "56354",
      "addressCountry": "US"
    },
    "touristType": ["Family", "Couples", "Groups", "Anglers", "Luxury travelers"],
    "isAccessibleForFree": false,
    "amenityFeature": [
      { "@type": "LocationFeatureSpecification", "name": "Private Sandy Beach", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Pontoon Boat", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Cedar Sauna", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Stone Fireplace", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Game Room", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Sleeps 12", "value": true }
    ],
    "containedInPlace": {
      "@type": "LakeBodyOfWater",
      "name": "Lake Miltona",
      "description": "5,731-acre lake in Douglas County, Minnesota — clear water, excellent walleye fishing, and uncrowded summer atmosphere"
    }
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How many people does the cabin sleep?",
        "acceptedAnswer": { "@type": "Answer", "text": "Legendary Log Cabin sleeps 12 guests across 4 bedrooms with 11 beds total, including the living-room pull-out." }
      },
      {
        "@type": "Question",
        "name": "What are the check-in and check-out times?",
        "acceptedAnswer": { "@type": "Answer", "text": "Check-in is from 4:00 PM to 8:00 PM and check-out is at 11:00 AM." }
      },
      {
        "@type": "Question",
        "name": "Where is the cabin located?",
        "acceptedAnswer": { "@type": "Answer", "text": "On the western shore of Lake Miltona in Douglas County, Minnesota, about 15 minutes north of Alexandria, MN." }
      },
      {
        "@type": "Question",
        "name": "What amenities are included?",
        "acceptedAnswer": { "@type": "Answer", "text": "A private sandy beach and dock, kayaks and paddleboard, private sauna, game room with pool table and bar, stone fireplace, fully stocked kitchen, Smart TV, high-speed WiFi, EV charger, and grills. A 25-ft pontoon is a seasonal add-on." }
      },
      {
        "@type": "Question",
        "name": "How do I book?",
        "acceptedAnswer": { "@type": "Answer", "text": "Book directly through our website at legendarylogcabin.com. Pick your dates on the booking calendar to see live pricing, then complete checkout securely via Stripe." }
      },
      {
        "@type": "Question",
        "name": "Is a pontoon boat available?",
        "acceptedAnswer": { "@type": "Answer", "text": "The 25-foot Premier Sunsation pontoon seats 14 and is a paid add-on to a cabin booking only. There is a 2-night minimum starting at $1,250, up to $2,400 for 7 nights; first tank of fuel included. It is docked at the cabin. Add it at checkout when you reserve your dates." }
      },
      {
        "@type": "Question",
        "name": "Is the beach safe for young children?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes. The private sandy beach has a very gradual, shallow entry with a hard bottom and no weeds, making it one of the safest swimming beaches on Lake Miltona." }
      },
      {
        "@type": "Question",
        "name": "Are pets allowed at the cabin?",
        "acceptedAnswer": { "@type": "Answer", "text": "Pets are allowed by written approval only. Contact the host before bringing a pet." }
      },
      {
        "@type": "Question",
        "name": "What time is check-in and checkout?",
        "acceptedAnswer": { "@type": "Answer", "text": "Check-in is between 4:00 PM and 8:00 PM, and checkout is by 11:00 AM. Self check-in is available via keypad entry." }
      },
      {
        "@type": "Question",
        "name": "What is the cancellation policy?",
        "acceptedAnswer": { "@type": "Answer", "text": FIRM_CANCELLATION_POLICY }
      },
      {
        "@type": "Question",
        "name": "Does the cabin have a sauna?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes, the cabin features a private sauna on the lower level — perfect for relaxing after a day on the lake or warming up during winter visits." }
      },
      {
        "@type": "Question",
        "name": "How far is the cabin from Alexandria, MN?",
        "acceptedAnswer": { "@type": "Answer", "text": "Legendary Log Cabin is located about 15–20 minutes north of downtown Alexandria, Minnesota, directly on the shores of Lake Miltona in Douglas County." }
      },
      {
        "@type": "Question",
        "name": "What activities are available nearby?",
        "acceptedAnswer": { "@type": "Answer", "text": "Guests enjoy swimming, fishing (walleye, bass, northern pike), kayaking, paddleboarding, and pontoon cruises on Lake Miltona. Nearby attractions include Carlos Creek Winery, downtown Alexandria shopping and dining, Central Lakes Trail biking, and Lake Carlos State Park. In winter, ice fishing and snowmobiling are popular." }
      },
      {
        "@type": "Question",
        "name": "Can the cabin be booked in winter?",
        "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. The cabin is available year-round. Winter guests enjoy ice fishing on Lake Miltona, snowmobiling on nearby trails, and cozy evenings by the stone fireplace and sauna." }
      }
    ]
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `${photoOrigin}/` },
      { "@type": "ListItem", "position": 2, "name": "Cabin Rentals", "item": `${photoOrigin}/` },
      { "@type": "ListItem", "position": 3, "name": "Legendary Log Cabin on Lake Miltona", "item": `${photoOrigin}/` }
    ]
  };

  const ownerPersonJsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Jake",
    "description": "Owner and host of the Legendary Log Cabin on Lake Miltona, Minnesota. Jake has managed the property since 2019 and is a top-rated host on Airbnb and VRBO. A Minnesota native with deep knowledge of Lake Miltona, the Alexandria Lakes Area, walleye fishing, and the best local spots for dining and recreation.",
    "knowsAbout": [
      "Lake Miltona vacation rentals",
      "Minnesota lake cabin hosting",
      "Alexandria MN area",
      "Walleye fishing on Lake Miltona",
      "Twin Cities lake cabin getaways"
    ],
    "owns": { "@id": `${photoOrigin}/#vacation-rental` },
    "sameAs": sameAsUrls
  };

  return (
    <div className="min-h-screen bg-background">
      <Suspense fallback={null}>
        <StripePaymentReturn confirmPath={(id) => `/api/payments/${id}/confirm`} />
      </Suspense>
      <PageSEO
        title="Legendary Log Cabin on Lake Miltona | Private Beach, Pontoon & Sauna | Sleeps 12"
        description="Stay at Legendary Log Cabin on Lake Miltona, Minnesota. Enjoy your own private sandy beach, 25-ft pontoon boat, cedar sauna, and stunning lake views. This beautiful log cabin sleeps 12 and is just 2.5 hours from Minneapolis. Book direct and save."
        canonicalPath="/"
        ogImage={`${CANONICAL_ORIGIN}/photos/photo-78-ed3ba9ee-cb47-4f67-8740-2a0554bb7f33.webp`}
      />
      <JsonLd id="jsonld-home-website" schema={websiteJsonLd} />
      <JsonLd id="jsonld-home-vacation-rental" schema={vacationRentalJsonLd} />
      <JsonLd id="jsonld-home-lodging" schema={localBusinessJsonLd} />
      <JsonLd id="jsonld-home-faq" schema={faqJsonLd} />
      <JsonLd id="jsonld-home-breadcrumb" schema={breadcrumbJsonLd} />
      <JsonLd id="jsonld-home-tourist" schema={touristAttractionJsonLd} />
      <JsonLd id="jsonld-home-owner" schema={ownerPersonJsonLd} />
      <GuestHeader
        actions={
          <>
            <a
              href="/admin"
              className="hidden md:inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors px-2.5 py-1.5 rounded-md hover:bg-muted text-sm font-medium"
              data-testid="button-host-login"
            >
              <KeyRound className="w-4 h-4" />
              <span>Host</span>
            </a>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground px-2.5"
              data-testid="button-share"
              aria-label="Share this listing"
              onClick={async () => {
                const shareData = {
                  title: "Legendary Log Cabin — Lake Miltona, MN",
                  text: "Check out this amazing lakefront cabin rental — private beach, pontoon & sauna!",
                  url: "https://www.legendarylogcabin.com",
                };
                if (navigator.share) {
                  try { await navigator.share(shareData); } catch {}
                } else {
                  await navigator.clipboard.writeText("https://www.legendarylogcabin.com");
                  alert("Link copied to clipboard!");
                }
              }}
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline text-sm">Share</span>
            </Button>
          </>
        }
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8 pb-28 lg:pb-8">
        <div className="mb-4 sm:mb-6">
          <h1 className="font-serif text-2xl sm:text-3xl lg:text-[2.6rem] font-bold text-foreground mb-2 leading-tight tracking-tight">
            Large Luxury Log Home, Sandy Beach, Pontoon, Sauna
          </h1>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 fill-primary text-primary" />
              <span className="font-semibold text-foreground">Top rated on Airbnb &amp; VRBO</span>
              <span>·</span>
            </div>
            <span className="inline-flex items-center gap-1.5 bg-accent text-accent-foreground text-xs font-semibold px-2.5 py-1 rounded-full border border-accent-foreground/10">
              <Award className="w-3 h-3" /> Guest Favorite
            </span>
            <div className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              <span>Lake Miltona, Miltona, Minnesota</span>
            </div>
          </div>
        </div>

        <PhotoGallery onOpenGallery={openGallery} onOpenGalleryAtLast={openGalleryAtLastViewed} />

        <div className="mt-6 sm:mt-10 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10 lg:gap-16">
          <div id="highlights">
            {/* Property header */}
            <div className="mb-5 pb-5 border-b border-border">
              <div className="min-w-0">
                <h2 className="font-serif text-lg sm:text-xl font-bold text-foreground mb-1">
                  Entire log cabin on Lake Miltona, Minnesota
                </h2>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                  <span>4 bedrooms</span>
                  <span>·</span>
                  <span>11 beds</span>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <Bath className="w-3.5 h-3.5" /> 2 baths
                  </span>
                </div>
              </div>
              <div
                className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-accent bg-accent/40 p-4"
                data-testid="host-badge-top"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 overflow-hidden shrink-0">
                    <img
                      src={`${import.meta.env.BASE_URL}img/samantha.jpg`}
                      alt="Host Samantha"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground leading-tight">Samantha</p>
                    <p className="text-xs text-muted-foreground leading-tight">Your host · replies within the hour · here for any questions</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:ml-auto">
                  <a
                    href={HOST_PHONE_SMS}
                    onClick={() => trackEvent("phone_sms_click", { source: "host_badge_top" })}
                    aria-label="Text your host Samantha"
                    data-testid="link-text-host-badge"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <MessageSquare className="w-4 h-4" aria-hidden="true" /> Text
                  </a>
                  <a
                    href={HOST_PHONE_TEL}
                    onClick={() => trackEvent("phone_call_click", { source: "host_badge_top" })}
                    aria-label="Call your host Samantha"
                    data-testid="link-call-host-badge"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Phone className="w-4 h-4" aria-hidden="true" /> Call
                  </a>
                  <a
                    href="/contact"
                    onClick={() => trackEvent("host_message_click", { source: "host_badge_top" })}
                    aria-label="Message your host Samantha to inquire"
                    data-testid="link-message-host-badge"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Mail className="w-4 h-4" aria-hidden="true" /> Message
                  </a>
                </div>
              </div>
            </div>

            {/* Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-6 mb-6 border-b border-border">
              {[
                { icon: Waves, title: "Private sandy beach", desc: "Hard-bottom, weed-free — rare on Minnesota lakes" },
                { icon: Trees, title: "5,731 acres of lake", desc: "Clear water, no algae, room to explore" },
                { icon: Flame, title: "Finnish sauna", desc: "Steps from the water on the lower level" },
                { icon: Users, title: "25-ft pontoon on-site", desc: "Add it to your stay — departs from your dock" },
              ].map((h) => {
                const subject = subjectFromText(h.title);
                return (
                  <div
                    key={h.title}
                    ref={subject ? registerSection(subject) : undefined}
                    className="flex gap-3 items-start"
                  >
                    <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center shrink-0">
                      <h.icon className="w-4 h-4 text-accent-foreground" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-foreground">{h.title}</p>
                      <p className="text-sm text-muted-foreground">{h.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Description */}
            <div className="pb-6 mb-6 border-b border-border">
              <div className={`text-[15px] leading-relaxed text-foreground space-y-3 ${!showFullDesc ? "line-clamp-4" : ""}`}>
                <p>{SHORT_DESC}</p>
                {showFullDesc &&
                  FULL_DESC.split("\n\n").map((para, i) => <p key={i}>{para}</p>)}
              </div>
              <button
                onClick={() => {
                  if (!showFullDesc) trackDescriptionExpanded();
                  setShowFullDesc(!showFullDesc);
                }}
                className="mt-3 text-sm font-semibold underline underline-offset-2 text-foreground flex items-center gap-1"
                data-testid="button-show-more-desc"
              >
                {showFullDesc ? "Show less" : "Show more"}
                <ChevronDown className={`w-4 h-4 transition-transform ${showFullDesc ? "rotate-180" : ""}`} />
              </button>
            </div>

            {/* Bedrooms */}
            <div className="pb-6 mb-6 border-b border-border">
              <h2 className="font-serif text-lg sm:text-xl font-bold text-foreground mb-4">
                Four bedrooms
              </h2>
              {/* Single responsive render: horizontal scroll on mobile, 2×2 grid on sm+ */}
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:overflow-visible sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 sm:gap-4">
                {BEDROOMS.map((room, idx) => (
                  <div
                    key={room.name}
                    className="border border-border rounded-xl overflow-hidden flex-shrink-0 w-64 sm:w-auto sm:flex-shrink"
                    data-testid={`card-bedroom-${idx}`}
                  >
                    <div className="h-36 sm:h-44 overflow-hidden">
                      <img
                        {...responsiveImage(room.img, "(min-width: 640px) 50vw, 256px")}
                        alt={room.alt}
                        className="w-full h-full object-cover sm:hover:scale-105 sm:transition-transform sm:duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3 sm:p-4">
                      <p className="font-semibold text-foreground text-sm sm:text-base">{room.name}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">{room.beds}</p>
                      <p className="text-xs text-primary mt-1 font-medium">{room.note}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Amenities */}
            <div id="amenities" className="pb-6 mb-6 border-b border-border">
              <h2 className="font-serif text-lg sm:text-xl font-bold text-foreground mb-4">
                Amenities
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(showAllAmenities ? AMENITIES : AMENITIES.slice(0, 10)).map((a) => {
                  const subject = subjectFromText(a.label);
                  return (
                    <div
                      key={a.label}
                      ref={subject ? registerSection(subject) : undefined}
                      className="flex items-center gap-3 text-sm py-0.5"
                    >
                      <a.icon className="w-5 h-5 text-foreground shrink-0" />
                      <span className="text-foreground">{a.label}</span>
                    </div>
                  );
                })}
              </div>
              <Button
                variant="outline"
                className="mt-4 font-semibold"
                onClick={() => {
                  if (!showAllAmenities) trackAmenitiesExpanded();
                  setShowAllAmenities(!showAllAmenities);
                }}
                data-testid="button-show-amenities"
              >
                {showAllAmenities
                  ? "Show fewer amenities"
                  : `Show all ${AMENITIES.length} amenities`}
              </Button>
            </div>

            {/* Reviews */}
            <div id="reviews" className="pb-6 mb-6 border-b border-border">
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 fill-primary text-primary" />
                  <span className="font-serif text-xl sm:text-2xl font-bold">Top rated on Airbnb &amp; VRBO</span>
                </div>
                <Badge variant="secondary" className="text-xs">Guest Favorite</Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                {REVIEWS.slice(0, 8).map((r, i) => (
                  <div key={i} ref={registerReview(i, r.name)} className="bg-card border border-card-border rounded-xl p-4 shadow-sm card-hover" data-testid={`card-review-${i}`}>
                    <div className="flex items-center gap-3 mb-2.5">
                      <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center font-semibold text-primary-foreground shrink-0 text-sm">
                        {r.name[0]}
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-foreground">{r.name}</p>
                        <p className="text-xs text-muted-foreground">{r.location}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mb-2.5">
                      <StarRating rating={r.rating} />
                      <span className="text-xs text-muted-foreground">· {r.date} · {r.trip}</span>
                    </div>
                    <p className="text-sm text-foreground/80 leading-relaxed">{r.text}</p>
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                className="mt-5 font-semibold"
                data-testid="button-show-all-reviews"
                onClick={() => setShowAllReviews(true)}
              >
                Show all guest reviews
              </Button>

              {/* All Reviews Drawer */}
              <Drawer open={showAllReviews} onOpenChange={setShowAllReviews}>
                <DrawerContent className="max-h-[92vh]">
                  <DrawerHeader className="pb-2 border-b border-border">
                    <DrawerTitle className="font-serif text-lg">Guest reviews</DrawerTitle>
                  </DrawerHeader>
                  <div className="overflow-y-auto px-4 pb-8 pt-4">
                    <div className="flex items-center gap-2 mb-5">
                      <Star className="w-5 h-5 fill-primary text-primary" />
                      <span className="font-bold text-base">{REVIEW_AVG} · {REVIEW_COUNT} guest reviews</span>
                    </div>
                    <div className="space-y-6">
                      {REVIEWS.map((r, i) => (
                        <div key={i} className="pb-6 border-b border-border last:border-0">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center font-semibold text-primary-foreground shrink-0 text-sm">
                              {r.name[0]}
                            </div>
                            <div>
                              <p className="font-semibold text-sm text-foreground">{r.name}</p>
                              <p className="text-xs text-muted-foreground">{r.location} · {r.date}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 mb-2">
                            <StarRating rating={r.rating} />
                            <span className="text-xs text-muted-foreground">· {r.trip}</span>
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed">{r.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </DrawerContent>
              </Drawer>
            </div>

            {/* Location */}
            <div id="location" className="pb-6 mb-6 border-b border-border">
              <h2 className="font-serif text-lg sm:text-xl font-bold text-foreground mb-1">
                Where you'll be
              </h2>
              <p className="text-muted-foreground text-sm mb-4">Lake Miltona, Miltona, Minnesota</p>
              <div className="rounded-xl overflow-hidden h-80 sm:h-96 bg-muted relative shadow-md border border-border">
                <LazyMapEmbed
                  title="Location map"
                  src="https://maps.google.com/maps?q=46.0557,-95.3782&z=13&output=embed"
                  className="absolute inset-0 w-full h-full"
                />
              </div>

              <div className="mt-3">
                <p className="text-xs text-muted-foreground mb-2 font-medium uppercase tracking-wide">Open in your maps app</p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=46.0557,-95.3782"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white border border-border rounded-full px-4 py-2 text-sm font-medium text-gray-800 shadow-sm hover:shadow-md hover:border-gray-300 transition-all"
                    data-testid="button-maps-google"
                  >
                    <SiGooglemaps role="img" aria-label="Google Maps" className="w-4 h-4 text-[#4285F4]" />
                    <span aria-hidden="true">Google Maps</span>
                  </a>
                  <a
                    href="https://maps.apple.com/?ll=46.0557,-95.3782&z=13&t=h"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white border border-border rounded-full px-4 py-2 text-sm font-medium text-gray-800 shadow-sm hover:shadow-md hover:border-gray-300 transition-all"
                    data-testid="button-maps-apple"
                  >
                    <SiApple role="img" aria-label="Apple Maps" className="w-4 h-4 text-gray-800" />
                    <span aria-hidden="true">Apple Maps</span>
                  </a>
                  <a
                    href="https://waze.com/ul?ll=46.0557,-95.3782&navigate=yes"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white border border-border rounded-full px-4 py-2 text-sm font-medium text-gray-800 shadow-sm hover:shadow-md hover:border-gray-300 transition-all"
                    data-testid="button-maps-waze"
                  >
                    <SiWaze role="img" aria-label="Waze" className="w-4 h-4 text-[#33CCFF]" />
                    <span aria-hidden="true">Waze</span>
                  </a>
                  <a
                    href="https://www.bing.com/maps?cp=46.0557~-95.3782&lvl=13&sp=point.46.0557_-95.3782_Lake+Miltona"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white border border-border rounded-full px-4 py-2 text-sm font-medium text-gray-800 shadow-sm hover:shadow-md hover:border-gray-300 transition-all"
                    data-testid="button-maps-bing"
                  >
                    <ExternalLink className="w-4 h-4 text-[#008272]" />
                    Bing Maps
                  </a>
                </div>
              </div>

              <div className="mt-4 mb-4">
                <h3 className="font-semibold text-sm text-foreground mb-2">Nearby distances</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { icon: MapPin, name: "Alexandria", time: "20 min" },
                    { icon: MapPin, name: "Fergus Falls", time: "45 min" },
                    { icon: MapPin, name: "Brainerd", time: "1 hr" },
                    { icon: MapPin, name: "Fargo, ND", time: "1.5 hrs" },
                    { icon: MapPin, name: "Minneapolis / St. Paul", time: "2.5 hrs" },
                    { icon: Plane, name: "Alexandria Airport (AXN)", time: "25 min" },
                  ].map((item) => (
                    <div
                      key={item.name}
                      className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2 text-sm"
                      data-testid={`distance-chip-${item.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
                    >
                      <item.icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <span className="font-medium text-foreground truncate block">{item.name}</span>
                        <span className="text-xs text-muted-foreground">{item.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <h3 className="font-semibold text-sm text-foreground mb-1">Neighborhood highlights</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Extremely peaceful neighborhood. Wonderful area that's safe — everyone here is at the lake and happy to be there. The road is a dead-end so perfect for kids, and private enough where there is no through traffic. The neighbors are very friendly.
                </p>
              </div>
            </div>

            {/* House rules */}
            <div className="pb-6 mb-6 border-b border-border">
              <h2 className="font-serif text-lg sm:text-xl font-bold text-foreground mb-4">Things to know</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div>
                  <h3 className="font-semibold text-sm text-foreground mb-3">House rules</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {[
                      "Check-in: 4:00 PM – 8:00 PM",
                      "Checkout: 11:00 AM",
                      "No parties or events",
                      "Quiet hours 10 PM–8 AM",
                      "No smoking indoors",
                      "Pets by written approval only",
                    ].map((rule) => (
                      <li key={rule} className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                        {rule}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-foreground mb-3">Safety & property</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {[
                      { icon: Shield, text: "Carbon monoxide alarm" },
                      { icon: Shield, text: "Smoke alarm" },
                      { icon: Waves, text: "Nearby lake — supervise children" },
                      { icon: Flame, text: "Campfires in designated area only" },
                    ].map((s) => (
                      <li key={s.text} className="flex items-start gap-2">
                        <s.icon className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                        {s.text}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-foreground mb-3">Cancellation policy</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {FIRM_CANCELLATION_POLICY}
                  </p>
                </div>
              </div>
            </div>

            {/* Explore the Area */}
            <div id="explore-area" className="pb-6 mb-6 border-b border-border">
              <h2 className="font-serif text-lg sm:text-xl font-bold text-foreground mb-2">
                Explore the Area
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                Legendary Log Cabin sits on the western shore of <strong className="text-foreground">Lake Miltona</strong> — a 5,731-acre lake in <strong className="text-foreground">Douglas County, Minnesota</strong>, about 15 minutes north of <strong className="text-foreground">Alexandria, MN</strong>. The Alexandria Lakes Area is home to 450+ lakes and offers year-round activities for families, anglers, and outdoor enthusiasts.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                {[
                  { label: "Carlos Creek Winery", desc: "Award-winning wines, live music — 10 min away" },
                  { label: "Downtown Alexandria", desc: "Shopping, dining, Runestone Museum, Big Ole Viking" },
                  { label: "Central Lakes Trail", desc: "55 miles of paved biking & walking trails" },
                  { label: "Lake Carlos State Park", desc: "Hiking, swimming beach, nature programs" },
                  { label: "Fishing on Lake Miltona", desc: "Walleye, bass, northern pike, muskellunge — 5,731 acres to explore" },
                  { label: "Ice Fishing & Snowmobiling", desc: "Winter adventures steps from the cabin" },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3 text-sm" data-testid={`area-item-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                    <MapPin className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium text-foreground">{item.label}</span>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <a href="/explore" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="link-explore-area">
                <MapPin className="w-3.5 h-3.5" /> Parks, restaurants &amp; things to do →
              </a>
            </div>

            {/* Host */}
            <div className="bg-accent/40 rounded-xl p-5 sm:p-6 border border-accent">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 rounded-full overflow-hidden bg-primary/10 shrink-0">
                  <img
                    src={`${import.meta.env.BASE_URL}img/samantha.jpg`}
                    alt="Samantha"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
                <div className="min-w-0">
                  <h2 className="font-serif text-base sm:text-lg font-bold text-foreground">Hosted by Samantha</h2>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-0.5">
                    <span className="flex items-center gap-1">
                      <Star className="w-3 h-3 fill-primary text-primary" /> 5.0 rating
                    </span>
                    <span>· 3 years hosting</span>
                    <span>· 100% response rate</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Lives in Plymouth, MN · Responds within an hour
                  </p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                Co-hosts Jake & Julie are on-call for anything you need during your stay. We love sharing this special place and want your experience to be absolutely memorable.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button asChild variant="outline" size="sm" className="gap-2 font-medium" data-testid="button-message-host">
                  <a href="/contact">
                    <Mail className="w-4 h-4" />
                    Message Host
                  </a>
                </Button>
                <Button asChild variant="outline" size="sm" className="gap-2 font-medium" data-testid="button-call-host">
                  <a href={HOST_PHONE_TEL} onClick={() => trackEvent("phone_call_click", { source: "host_card" })}>
                    <Phone className="w-4 h-4" />
                    Call
                  </a>
                </Button>
                <Button asChild variant="outline" size="sm" className="gap-2 font-medium" data-testid="button-text-host">
                  <a href={HOST_PHONE_SMS} onClick={() => trackEvent("phone_sms_click", { source: "host_card" })}>
                    <MessageSquare className="w-4 h-4" />
                    Text
                  </a>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3" data-testid="text-host-phone">
                Call or text anytime:{" "}
                <a href={HOST_PHONE_TEL} className="font-medium text-foreground hover:underline" onClick={() => trackEvent("phone_call_click", { source: "host_card_number" })}>
                  {HOST_PHONE_DISPLAY}
                </a>
              </p>
            </div>
          </div>

          <aside id="booking" className="hidden lg:block scroll-mt-24">
            <BookingWidget initialDates={initialBookingDates} />
            <a
              href="/book-direct"
              className="mt-3 flex items-center justify-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 font-medium transition-colors"
              data-testid="link-why-book-direct"
            >
              <BadgePercent className="w-3.5 h-3.5" />
              Why Book Direct?
            </a>
          </aside>
        </div>
      </main>

      {/* ── Featured On / Authority Strip ── */}
      <section className="border-y border-border bg-muted/20 py-8 sm:py-10" aria-label="Featured on and listed with">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-6">
            Listed, verified &amp; recognized on
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {/* Airbnb Guest Favorite */}
            <div className="flex flex-col items-center gap-1.5 text-center">
              <div className="flex items-center gap-2 bg-[#FF385C]/10 rounded-lg px-4 py-2.5">
                <Star className="w-4 h-4 fill-[#FF385C] text-[#FF385C]" />
                <span className="font-bold text-rose-700 text-sm tracking-tight">Airbnb</span>
              </div>
              <span className="text-xs text-muted-foreground font-medium">Guest Favorite</span>
            </div>
            {/* VRBO */}
            <div className="flex flex-col items-center gap-1.5 text-center">
              <div className="flex items-center gap-2 bg-[#1C4E80]/10 rounded-lg px-4 py-2.5">
                <Waves className="w-4 h-4 text-[#1C4E80]" />
                <span className="font-bold text-[#1C4E80] text-sm tracking-tight">VRBO</span>
              </div>
              <span className="text-xs text-muted-foreground font-medium">Verified Property</span>
            </div>
            {/* Direct Booking */}
            <div className="flex flex-col items-center gap-1.5 text-center">
              <div className="flex items-center gap-2 bg-primary/10 rounded-lg px-4 py-2.5">
                <Check className="w-4 h-4 text-primary" />
                <span className="font-bold text-primary text-sm tracking-tight">www.legendarylogcabin.com</span>
              </div>
              <span className="text-xs text-muted-foreground font-medium">Book Direct with the Host</span>
            </div>
            {/* Minnesota DNR Licensed */}
            <div className="flex flex-col items-center gap-1.5 text-center">
              <div className="flex items-center gap-2 bg-emerald-600/10 rounded-lg px-4 py-2.5">
                <Shield className="w-4 h-4 text-emerald-700" />
                <span className="font-bold text-emerald-800 text-sm tracking-tight">MN Licensed</span>
              </div>
              <span className="text-xs text-muted-foreground font-medium">Short-Term Rental #2181</span>
            </div>
            {/* Alexandria Lakes Area */}
            <div className="flex flex-col items-center gap-1.5 text-center">
              <div className="flex items-center gap-2 bg-blue-600/10 rounded-lg px-4 py-2.5">
                <MapPin className="w-4 h-4 text-blue-700" />
                <span className="font-bold text-blue-700 text-sm tracking-tight">Alexandria Lakes Area</span>
              </div>
              <span className="text-xs text-muted-foreground font-medium">Douglas County, Minnesota</span>
            </div>
          </div>
        </div>
      </section>

      <CabinDivider className="text-foreground mt-8 sm:mt-12" />

      {/* ── Drive Times from Twin Cities ── */}
      <section className="bg-muted/30 py-14 sm:py-20" id="drive-times">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
              <Car className="w-4 h-4" />
              Straight west on I-94 — no stoplights, no Brainerd traffic
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-3">
              Drive Times from Twin Cities Suburbs
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Lake Miltona is 2–2.5 hours from most of the metro — the same drive as Brainerd, but without the resort crowds or OTA markups.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-10">
            {[
              { city: "Maple Grove", time: "2 hr 0 min", miles: "120 mi" },
              { city: "Plymouth", time: "2 hr 10 min", miles: "130 mi" },
              { city: "Wayzata", time: "2 hr 15 min", miles: "140 mi" },
              { city: "Shorewood / Orono", time: "2 hr 20 min", miles: "140 mi" },
              { city: "Minnetonka", time: "2 hr 20 min", miles: "140 mi" },
              { city: "Eden Prairie", time: "2 hr 30 min", miles: "150 mi" },
              { city: "Edina", time: "2 hr 30 min", miles: "150 mi" },
              { city: "Woodbury", time: "2 hr 45 min", miles: "170 mi" },
            ].map(({ city, time, miles }) => (
              <div key={city} className="bg-background rounded-xl border border-border p-4 text-center hover:border-primary/40 transition-colors">
                <div className="text-xs text-muted-foreground font-medium mb-1">{miles}</div>
                <div className="font-serif font-bold text-foreground text-sm sm:text-base leading-tight mb-1">{city}</div>
                <div className="text-primary font-bold text-lg sm:text-xl">{time}</div>
              </div>
            ))}
          </div>

          <div className="bg-background rounded-xl border border-border p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground mb-1">Route: I-94 West → Exit 100 Alexandria → Hwy 29 North → Lake Miltona</p>
              <p className="text-sm text-muted-foreground">No construction zones, no toll roads, no Hwy 371 summer gridlock. Depart Friday at 3 PM and you're on the dock by 5:30.</p>
            </div>
            <a href="/book-direct" className="shrink-0 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors">
              Check Availability
            </a>
          </div>
        </div>
      </section>

      <CabinDivider className="text-foreground" />

      {/* ── From the Blog ── */}
      <section className="py-14 sm:py-20" id="from-the-blog">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
              <BookOpen className="w-4 h-4" />
              Guides &amp; local knowledge
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-3">
              Plan Your Stay
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Everything you need before you arrive — from packing lists to pontoon tips.
            </p>
          </div>
          <BlogPreview />
          <div className="text-center mt-8">
            <a
              href="/blog"
              className="inline-flex items-center gap-2 border border-border rounded-full px-6 py-2.5 text-sm font-semibold text-foreground hover:border-primary/50 hover:text-primary transition-colors"
            >
              All guides <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <CabinDivider className="text-foreground" />

      {/* ── Minnesota Cabin FAQ ── */}
      <section className="py-14 sm:py-20" id="faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-3">
              Minnesota Lake Cabin FAQ
            </h2>
            <p className="text-muted-foreground text-lg">
              Everything Twin Cities families ask before booking their Lake Miltona weekend.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "How many people does the cabin sleep?",
                a: "Legendary Log Cabin sleeps 12 guests across 4 bedrooms with 11 beds total, including the living-room pull-out."
              },
              {
                q: "What are the check-in and check-out times?",
                a: "Check-in is from 4:00 PM to 8:00 PM and check-out is at 11:00 AM."
              },
              {
                q: "Where is the cabin located?",
                a: "On the western shore of Lake Miltona in Douglas County, Minnesota, about 15 minutes north of Alexandria, MN."
              },
              {
                q: "What amenities are included?",
                a: "A private sandy beach and dock, kayaks and paddleboard, private sauna, game room with pool table and bar, stone fireplace, fully stocked kitchen, Smart TV, high-speed WiFi, EV charger, and grills. A 25-ft pontoon is a seasonal add-on."
              },
              {
                q: "How do I book?",
                a: "Book directly through our website at legendarylogcabin.com. Pick your dates on the booking calendar to see live pricing, then complete checkout securely via Stripe."
              },
              {
                q: "How far is Lake Miltona from Minneapolis?",
                a: "Lake Miltona is approximately 145 miles and 2.5 hours from downtown Minneapolis via I-94 West to Exit 100 (Alexandria) and Hwy 29 North. It's a comparable drive to Brainerd but with dramatically less traffic on the I-94 corridor."
              },
              {
                q: "What is the best luxury lake cabin rental near Minneapolis?",
                a: "Legendary Log Cabin on Lake Miltona is consistently cited as one of the top luxury cabin rentals within 2.5 hours of Minneapolis. It has a private sandy beach, optional 25-foot pontoon boat, cedar sauna, stone fireplace, and game room — all private, no shared resort facilities."
              },
              {
                q: "How does Lake Miltona compare to Brainerd for a Twin Cities weekend?",
                a: "Drive time is nearly identical. The difference: Lake Miltona is significantly less crowded, Legendary Log Cabin offers a private beach (not shared resort access), and you book the entire cabin rather than a resort unit. Total cost is comparable or lower when you factor in boat rental savings."
              },
              {
                q: "Can Legendary Log Cabin sleep my extended family of 10–12?",
                a: "Yes — the cabin has 4 bedrooms, and 11 beds in total. The great room, two decks, private beach, and full kitchen accommodate large groups comfortably. It's purpose-built for multi-family and extended-family stays."
              },
              {
                q: "Is there a pontoon boat available at Legendary Log Cabin?",
                a: "Yes. The 25-foot Premier Sunsation pontoon seats 14 and is a paid add-on to a cabin booking only, docked at the cabin's private dock on Lake Miltona — no trailering, no marina pickup. There is a 2-night minimum starting at $1,250, up to $2,400 for 7 nights; first tank of fuel included. Book it when you reserve your dates."
              },
              {
                q: "What is the cost to rent Legendary Log Cabin?",
                a: "Nightly rates are set dynamically based on season and demand, plus a one-time $299 cleaning fee. The rate includes up to 12 guests — each additional guest over 12 is $100 per person, per night. Pick your dates on the booking calendar to see live pricing."
              },
              {
                q: "Does Legendary Log Cabin have a private beach?",
                a: "Yes — a wide private sandy beach with a gradual, shallow entry is directly in front of the cabin. Unlike resort \"lakefront\" access, there are no other guests on this beach. The sand is clean and groomed, and it's ideal for families with young children."
              },
              {
                q: "When is the best time to visit Lake Miltona from the Twin Cities?",
                a: "July and August offer warm water (mid-70s°F), long days, and peak cabin season. Book 6–8 weeks ahead for summer weekends. September and October offer stunning fall color, excellent walleye fishing, and lower rates with fewer crowds. Winter ice fishing weekends are popular with sauna and fireplace included."
              },
              {
                q: "Is Lake Miltona crowded in summer compared to Minnetonka or Brainerd?",
                a: "No — Lake Miltona is a primarily residential lake with no commercial resort strip. Summer Saturdays on Lake Miltona feel like Brainerd did 20 years ago. The sandbar is accessible, the water is quiet in the mornings, and the west shore where the cabin sits is peaceful and low-traffic."
              },
              {
                q: "Why do Wayzata and Edina families choose Lake Miltona over other Minnesota cabin destinations?",
                a: "Wayzata and Edina residents cite three factors: the private sandy beach (difficult to find at most rentals), the cabin's quality matching their home standards (log architecture, stone fireplace, full kitchen), and the drive time — I-94 West from the southwest metro moves faster than Hwy 371 to Brainerd on summer Fridays."
              },
            ].map(({ q, a }, i) => (
              <details key={i} className="group bg-background border border-border rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between gap-4 px-5 py-4 cursor-pointer list-none font-semibold text-foreground hover:bg-muted/30 transition-colors">
                  <span>{q}</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-4">
                  {a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CabinDivider className="text-foreground" />

      {/* ── Get the App ── */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

            {/* Text side */}
            <div>
              <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 text-sm font-semibold mb-5">
                <Smartphone className="w-4 h-4" />
                Mobile App — Free
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold leading-tight mb-4">
                Book &amp; manage your stay from anywhere
              </h2>
              <p className="text-primary-foreground/80 text-lg leading-relaxed mb-8 max-w-md">
                Browse photos, check availability, book your dates, and chat with our AI concierge — all from your phone. Your perfect lake getaway is three taps away.
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  "Full booking flow",
                  "AI concierge",
                  "14 cabin photos",
                  "Live availability",
                ].map((f) => (
                  <span key={f} className="inline-flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1 text-sm font-medium">
                    <Check className="w-3.5 h-3.5" /> {f}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="/mobile/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-white text-primary rounded-xl px-5 py-3.5 font-semibold hover:bg-white/90 transition-colors shadow-lg"
                >
                  <SiApple role="img" aria-label="Apple App Store" className="w-5 h-5" />
                  <span className="text-left" aria-hidden="true">
                    <span className="block text-xs font-normal opacity-90">Download on the</span>
                    <span className="block text-sm font-bold leading-tight">App Store</span>
                  </span>
                </a>
                <a
                  href="/mobile/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-white text-primary rounded-xl px-5 py-3.5 font-semibold hover:bg-white/90 transition-colors shadow-lg"
                >
                  <SiGoogleplay role="img" aria-label="Google Play Store" className="w-5 h-5" />
                  <span className="text-left" aria-hidden="true">
                    <span className="block text-xs font-normal opacity-90">Get it on</span>
                    <span className="block text-sm font-bold leading-tight">Google Play</span>
                  </span>
                </a>
                <a
                  href="/mobile/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-white/40 rounded-xl px-5 py-3.5 text-sm font-semibold hover:bg-white/10 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Try in browser
                </a>
              </div>
            </div>

            {/* Phone mockup */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-white/10 blur-3xl rounded-full scale-75" />
                {/* Phone frame */}
                <div className="relative w-[220px] sm:w-[260px] bg-white/10 border border-white/20 rounded-[2.5rem] p-2.5 shadow-2xl backdrop-blur-sm">
                  <div className="bg-[#1B4332] rounded-[2rem] overflow-hidden aspect-[9/19]">
                    {/* Status bar */}
                    <div className="flex justify-between items-center px-4 pt-3 pb-1">
                      <span className="text-white/70 text-[10px] font-semibold">9:41</span>
                      <div className="w-16 h-3 bg-black rounded-full" />
                      <div className="flex gap-1 items-center">
                        <div className="w-3 h-1.5 bg-white/70 rounded-sm" />
                        <div className="w-1 h-1.5 bg-white/40 rounded-sm" />
                      </div>
                    </div>
                    {/* App content preview */}
                    <div className="bg-[#FDF8F3] mx-1.5 rounded-2xl overflow-hidden" style={{ height: "82%" }}>
                      {/* Hero image placeholder */}
                      <div className="bg-gradient-to-b from-[#1B4332] to-[#2d6a4f] h-28 relative flex items-end p-3">
                        <div className="absolute inset-0 opacity-20" style={{
                          backgroundImage: "image-set(url('/photos/photo-78-ed3ba9ee-cb47-4f67-8740-2a0554bb7f33-400.webp') 1x, url('/photos/photo-78-ed3ba9ee-cb47-4f67-8740-2a0554bb7f33-800.webp') 2x)",
                          backgroundSize: "cover",
                          backgroundPosition: "center"
                        }} />
                        <div className="relative">
                          <div className="text-white text-[9px] font-bold leading-tight">Legendary Log Cabin</div>
                          <div className="text-white/70 text-[7px]">Lake Miltona, Minnesota</div>
                        </div>
                        <div className="absolute bottom-2 right-2 bg-black/40 rounded-full px-1.5 py-0.5 text-white text-[7px]">1/14</div>
                      </div>
                      {/* Rating row */}
                      <div className="px-3 py-2 flex items-center gap-1">
                        <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
                        <span className="text-[8px] font-bold text-gray-800">Top rated</span>
                        <span className="text-[7px] text-gray-500">· Airbnb &amp; VRBO</span>
                      </div>
                      {/* Amenity pills */}
                      <div className="px-3 flex gap-1">
                        {["6 guests", "3 beds", "Lake"].map(b => (
                          <div key={b} className="bg-[#F2E8D5] rounded-full px-1.5 py-0.5 text-[6px] text-gray-700 font-medium">{b}</div>
                        ))}
                      </div>
                      {/* Reserve bar */}
                      <div className="absolute bottom-2 left-0 right-0 mx-1.5 bg-white border border-gray-100 rounded-xl px-3 py-2 flex items-center justify-between shadow-sm">
                        <div>
                          <div className="text-[8px] font-bold text-gray-900">from $900<span className="font-normal text-gray-500"> / night</span></div>
                          <div className="text-[7px] text-[#1B4332] underline">Select dates</div>
                        </div>
                        <div className="bg-[#1B4332] rounded-lg px-2 py-1">
                          <span className="text-white text-[8px] font-bold">Reserve</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Home indicator */}
                  <div className="flex justify-center pt-2">
                    <div className="w-20 h-1 bg-white/30 rounded-full" />
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <footer className="bg-muted/50 border-t border-border mb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="mb-8 rounded-2xl border border-border bg-background/60 p-5 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 items-center">
            <div>
              <h3 className="font-serif text-lg font-bold text-foreground">Save on your first stay</h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                Join our list for a one-time first-booking discount and seasonal Lake Miltona tips.
              </p>
            </div>
            <EmailSignupForm source="footer" className="w-full" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center">
                  <Waves className="w-3.5 h-3.5 text-primary-foreground" />
                </div>
                <span className="font-serif font-bold text-foreground">Legendary Log Cabin</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                A luxury lakefront escape on Lake Miltona, Minnesota. Private beach, dock, sauna, and 5,731 acres of crystal-clear water.
              </p>
              <SocialLinks className="mt-4" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-foreground mb-3">Quick Links</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#highlights" className="hover:text-foreground transition-colors">The Cabin</a></li>
                <li><a href="#amenities" className="hover:text-foreground transition-colors">Amenities</a></li>
                <li><a href="#reviews" className="hover:text-foreground transition-colors">Reviews</a></li>
                <li><a href="/gallery" className="hover:text-foreground transition-colors" data-testid="footer-link-gallery">Photo Gallery</a></li>
                <li><a href="/#booking" className="hover:text-foreground transition-colors" data-testid="footer-link-availability">Book Now</a></li>
                <li><a href="/book-direct" className="hover:text-foreground transition-colors" data-testid="footer-link-book-direct">Book Direct</a></li>
                <li><a href="/book" className="hover:text-foreground transition-colors text-stone-400" data-testid="footer-link-book-gvr">Book via Google (GVR)</a></li>
                <li><a href="/blog" className="hover:text-foreground transition-colors" data-testid="footer-link-blog">Guides & FAQ</a></li>
                <li><a href="/contact" className="hover:text-foreground transition-colors" data-testid="footer-link-contact">Contact Us</a></li>
                <li><a href="/cabin-facts.html" className="hover:text-foreground transition-colors" data-testid="footer-link-cabin-facts">Cabin Facts</a></li>
                <li><a href="/media-kit" className="hover:text-foreground transition-colors" data-testid="footer-link-media-kit">Media Kit & Press</a></li>
                <li><a href="/admin" className="hover:text-foreground transition-colors" data-testid="footer-link-host-login">Host Login</a></li>
                <li><a href="/hosthq" className="text-indigo-600 hover:text-indigo-700 transition-colors" data-testid="footer-link-hosthq">For Hosts → HostHQ</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-sm text-foreground mb-3">Contact</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 shrink-0" /> Lake Miltona, Miltona, Minnesota</li>
                <li className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 shrink-0" /> <a href={CONCIERGE_PHONE_TEL} className="hover:text-foreground transition-colors" onClick={() => trackEvent("phone_call_click", { source: "footer", line: "concierge" })} data-testid="footer-link-phone">{CONCIERGE_PHONE_DISPLAY}</a> · 24/7</li>
                <li className="flex items-center gap-2"><Star className="w-3.5 h-3.5 shrink-0 fill-primary text-primary" /> {REVIEW_AVG}★ · {REVIEW_COUNT} reviews on Airbnb &amp; VRBO</li>
                <li className="flex items-center gap-2"><Users className="w-3.5 h-3.5 shrink-0" /> 4 bedrooms · 11 beds</li>
                <li className="flex items-center gap-2"><CalendarIcon className="w-3.5 h-3.5 shrink-0" /> License #2181</li>
              </ul>
            </div>
          </div>
          <Separator className="my-5 sm:my-6" />
          <p className="text-xs text-muted-foreground text-center">
            © {new Date().getFullYear()} Legendary Log Cabin · All rights reserved · Direct booking saves you fees
          </p>
        </div>
      </footer>

      <GalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        initialIndex={galleryIndex}
        onCurrentChange={(idx) => { lastGalleryIndex.current = idx; }}
      />

      <MobileBookingDrawer initialDates={initialBookingDates} />
      <ResumedCheckout />
    </div>
  );
}
